// Importar configurações do Supabase
// Removido import do Supabase - usamos apenas webhooks N8N
import {
  API_URL,
  DELIVAI_INTERNAL_CNPJ,
  N8N_AUTH_HEADER,
  N8N_WEBHOOK_PROMPT_TEMPLATES_PATH,
  N8N_WEBHOOK_URL,
  PANEL_FETCH_CREDENTIALS,
  panelAuthHeaders,
} from '../config';
import { fetchWithTransientRetry, HttpStatusError } from '../services/api/transientRetry';
import type {
  PromptTemplateCreateInput,
  PromptTemplateRow,
  PromptTemplateUpdateInput,
} from '../types/promptTemplate';
import {
  normalizePromptTemplateList,
  normalizePromptTemplateRow,
} from './promptTemplateNormalize';
import {
  mergeLicenseCatalogFromPayTypes,
  defaultLicenseCatalog,
  type LicenseCatalogState,
} from './licenseCatalog';
import { formatApiErrorForUser, parseApiErrorPayload } from '../services/api/errors';
import {
  normalizePayTypesForCustomerUpdate,
  stripEmptyJsonFieldsFromCustomerUpdate,
} from './jsonPayloadGuard';
import { assignVisivelEm, parseVisivelEm, type MenuChannel } from './menuChannelVisibility';
import { logDebug, logError, logWarn } from './logger';
import { handlePanelUnauthorized } from './sessionExpired';
import { normalizeCustomerPayTypes } from './storeStatus';
import {
  agoraEmSaoPaulo,
  caixaAbertoEstaVencido,
  caixaPertenceAJanela,
  getJanelaParaNovoCaixa,
  parseHorarioFuncionamentoRaw,
  payloadFecharCaixa,
  payloadNovoCaixaAberto,
  orderMatchesOperationalDay,
} from './caixaRules';

/** SEC-2: cookie HttpOnly + Authorization legado só se ainda houver no env. */
function panelFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const hdrs: Record<string, string> = {};
  const raw = init.headers;
  if (raw instanceof Headers) {
    raw.forEach((v, k) => {
      hdrs[k] = v;
    });
  } else if (Array.isArray(raw)) {
    raw.forEach(([k, v]) => {
      hdrs[k] = v;
    });
  } else if (raw && typeof raw === "object") {
    Object.assign(hdrs, raw);
  }
  delete hdrs.Authorization;
  delete hdrs.authorization;
  return fetchWithTransientRetry(input, {
    ...init,
    credentials: PANEL_FETCH_CREDENTIALS,
    headers: panelAuthHeaders(hdrs),
  }).then((res) => {
    if (res.status === 401) {
      handlePanelUnauthorized(typeof input === "string" ? input : String(input));
    }
    return res;
  });
}

function isTransientGatewayFailure(status: number, rawText: string): boolean {
  if (status === 502 || status === 503 || status === 504) return true;
  const trimmed = (rawText || "").trim();
  if (!trimmed) return true;
  return (
    trimmed.startsWith("<!DOCTYPE") ||
    trimmed.startsWith("<html") ||
    /easypanel|service is not reachable/i.test(trimmed)
  );
}

async function sleepMs(ms: number): Promise<void> {
  await new Promise((r) => setTimeout(r, ms));
}

// Tipos para o sistema de cardápio
export interface MenuProduct {
  id?: string;
  /** Ordem de exibição (catálogo e telas de pedido); menor = primeiro */
  ordem?: number;
  nome?: string;
  name?: string;
  title?: string;
  preco?: number;
  price?: number;
  valor?: number;
  precos?: { [key: string]: number }; // Para múltiplos preços (ex: "pequeno", "grande")
  descricao?: string;
  description?: string;
  desc?: string;
  categoria?: string;
  category?: string;
  tipo?: string;
  disponivel?: boolean;
  /** Item reconhecido no teste e bloqueado até a contratação. */
  trial_locked?: boolean;
  available?: boolean;
  ingredientes?: string[];
  ingredients?: string[];
  alergenos?: string[];
  tempo_preparo?: number;
  prep_time?: number;
  tempo?: number;
  duracao?: number; // Duração em minutos para serviços
  duration?: number; // Alias para duração
  modalidade?: string; // Modalidade do serviço: "tempo" (minutos) ou "dia" (dias)
  imagem?: string;
  image_url?: string;
  visita?: boolean;
  requires_visit?: boolean;
  returnIncluded?: boolean;
  return_included?: boolean;
  sinal_required?: boolean;
  sinal_amount?: number;
  /** Ausente = todos os cardápios. Não gravar no JSON até o lojista alterar. */
  visivel_em?: MenuChannel[];
}

// Interface para tipo de preço com informações adicionais
export interface PriceTypeConfig {
  nome: string; // Nome do tipo de preço (ex: "Padrao", "Broto")
  preco_sugerido?: number; // Preço sugerido para este tipo
  sabores?: number; // Número de sabores (apenas para Pizza, 1-4)
}

export interface MenuCategory {
  id?: string;
  /** Ordem de exibição das categorias */
  ordem?: number;
  nome?: string;
  name?: string;
  title?: string;
  ativo?: boolean;
  produtos?: MenuProduct[];
  products?: MenuProduct[];
  tipos_preco?: string[]; // Tipos de preço definidos para a categoria (ex: ['padrao', 'grande']) - DEPRECATED, usar tipos_preco_config
  tipos_preco_config?: PriceTypeConfig[]; // Configuração completa dos tipos de preço
  tipo?: 'produto' | 'pizza' | 'adicional'; // Tipo da categoria
  adicionais_categorias?: string[]; // IDs ou nomes das categorias de adicionais (apenas para produto e pizza)
  limite_quantidade?: number; // Limite máximo de adicionais por produto (apenas para tipo 'adicional')
  limite_minimo?: number; // Mínimo de adicionais desta categoria por produto (apenas para tipo 'adicional')
  disponivel_em_categorias?: string[]; // Nomes das categorias onde este adicional está disponível (categoria inteira)
  disponivel_em_produtos?: string[]; // Ids ou nomes dos produtos onde este adicional está disponível (produtos específicos)
  modalidade?: string; // Modalidade da categoria: "tempo" (minutos) ou "dia" (dias)
  /** Ausente = todos os cardápios. Não gravar no JSON até o lojista alterar. */
  visivel_em?: MenuChannel[];
}

export interface MenuData {
  produtos?: MenuProduct[];
  products?: MenuProduct[];
  categorias?: MenuCategory[];
  categories?: MenuCategory[];
}

/** Compara nomes com regra alfanumérica (número no início, depois locale), igual ao catálogo legado */
export function compareNamesAlphanumeric(nameA: string, nameB: string): number {
  const a = nameA.trim();
  const b = nameB.trim();
  const numMatchA = a.match(/^(\d+)/);
  const numMatchB = b.match(/^(\d+)/);
  if (numMatchA && numMatchB) {
    const numA = parseInt(numMatchA[1], 10);
    const numB = parseInt(numMatchB[1], 10);
    if (numA !== numB) return numA - numB;
    const restA = a.substring(numMatchA[1].length).trim();
    const restB = b.substring(numMatchB[1].length).trim();
    return restA.localeCompare(restB, 'pt-BR', { sensitivity: 'base' });
  }
  if (numMatchA && !numMatchB) return -1;
  if (!numMatchA && numMatchB) return 1;
  return a.localeCompare(b, 'pt-BR', { sensitivity: 'base' });
}

/** Ordena por `ordem` quando definido; desempate com compareNamesAlphanumeric no rótulo */
export function sortCatalogByOrdemThenName<T extends { ordem?: number }>(
  items: T[],
  getName: (item: T) => string
): T[] {
  return [...items].sort((a, b) => {
    const oa = a.ordem;
    const ob = b.ordem;
    const ha = oa !== undefined && oa !== null;
    const hb = ob !== undefined && ob !== null;
    if (ha && hb && oa !== ob) return (oa as number) - (ob as number);
    if (ha && !hb) return -1;
    if (!ha && hb) return 1;
    return compareNamesAlphanumeric(getName(a), getName(b));
  });
}

/** Garante `ordem` em categorias e produtos para persistência e arrastar */
export function assignDefaultOrdem(menuData: MenuData | null): MenuData | null {
  if (!menuData) return null;
  let next: MenuData = { ...menuData };
  if (next.categorias?.length) {
    next = {
      ...next,
      categorias: next.categorias.map((cat, ci) => ({
        ...cat,
        ordem: cat.ordem != null ? cat.ordem : ci,
        produtos: (cat.produtos || []).map((p, pi) => ({
          ...p,
          ordem: p.ordem != null ? p.ordem : pi,
        })),
      })),
    };
  }
  if (next.produtos?.length) {
    next = {
      ...next,
      produtos: next.produtos.map((p, i) => ({
        ...p,
        ordem: p.ordem != null ? p.ordem : i,
      })),
    };
  }
  return next;
}

/** Atualiza o fragmento de cardápio em `rawMenuData` após salvar sem refetch */
export function mergeCardapioIntoRawMenu(prev: any, cardapioFragment: any): any {
  if (prev == null || typeof prev !== 'object' || Array.isArray(prev)) {
    return cardapioFragment;
  }
  if (Object.prototype.hasOwnProperty.call(prev, 'cardapio') && prev.cardapio !== undefined) {
    return { ...prev, cardapio: cardapioFragment };
  }
  const servicos = (prev as any).servicos;
  if (servicos !== undefined) {
    return { ...cardapioFragment, servicos };
  }
  return cardapioFragment;
}

// Formatar nome de categoria para exibição: substituir _ por espaço e capitalizar cada palavra
function formatCategoryDisplayName(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/** Detecta item de `categorias: [...]` no JSON (não confundir com array de produtos). */
function looksLikeMenuCategory(obj: any): boolean {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  return (
    Array.isArray(obj.produtos) ||
    Array.isArray(obj.products) ||
    Array.isArray(obj.itens) ||
    obj.tipo === 'adicional' ||
    obj.tipo === 'pizza' ||
    obj.tipo === 'produto'
  );
}

/** Disponível salvo no painel ou legado sem flags explícitas (só some se `ativo`/`disponivel` = false). */
function isMenuItemAvailable(item: any): boolean {
  if (!item || typeof item !== 'object') return false;
  if (item.disponivel === false || item.ativo === false) return false;
  return true;
}

function isMenuCategoryActive(cat: any): boolean {
  if (!cat || typeof cat !== 'object') return true;
  return cat.ativo !== false;
}

function looksLikeMenuCategoryListEntry(obj: any): boolean {
  return (
    looksLikeMenuCategory(obj) ||
    (obj &&
      typeof obj === 'object' &&
      (obj.nome != null || obj.name != null || obj.title != null) &&
      (Array.isArray(obj.itens) || Array.isArray(obj.produtos) || Array.isArray(obj.products)))
  );
}

/** Lê o cardápio salvo na empresa (`cardapio_json`, `catalog` ou `cardapio`), com parse se vier string. */
export function getCardapioRawFromCliente(cliente: Record<string, unknown> | null | undefined): any {
  if (!cliente || typeof cliente !== 'object') return null;
  const raw =
    (cliente as { cardapio_json?: unknown; catalog?: unknown; cardapio?: unknown }).cardapio_json ??
    (cliente as { catalog?: unknown }).catalog ??
    (cliente as { cardapio?: unknown }).cardapio;
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  return raw;
}

/** Extrai só a seção `cardapio` quando o JSON salvo usa `{ cardapio, servicos }`. */
export function getCardapioSectionFromStoredMenu(raw: any): any {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const section = (raw as { cardapio?: unknown }).cardapio;
  if (section != null && typeof section === 'object' && !Array.isArray(section)) {
    return section;
  }
  return raw;
}

function visivelEmField(raw: any): { visivel_em?: MenuChannel[] } {
  const parsed = parseVisivelEm(raw?.visivel_em);
  return parsed !== undefined ? { visivel_em: parsed } : {};
}

function stampTrialLock(item: Record<string, unknown>, product: MenuProduct): void {
  if (product.duracao != null && product.duracao > 0) item.duracao = product.duracao;
  if (product.trial_locked) {
    item.trial_locked = true;
    item.ativo = false;
    item.disponivel = false;
  }
}

function mapMenuProductsToRawItems(products: MenuProduct[]): any[] {
  return (products || []).map((product, prodIndex) => {
    const item: Record<string, unknown> = {
      ordem: product.ordem ?? prodIndex,
      nome: product.nome,
      ativo: product.disponivel === true,
      precos: product.precos || (product.preco !== undefined ? { padrao: product.preco } : {}),
    };
    if (product.descricao) item.descricao = product.descricao;
    if (product.imagem || product.image_url) item.imagem = product.imagem || product.image_url;
    if (product.id) item.id = product.id;
    stampTrialLock(item, product);
    assignVisivelEm(item, product);
    return item;
  });
}

function applyMenuCategoryMetadataToRaw(rebuiltCat: Record<string, unknown>, category: MenuCategory): void {
  if (category.tipos_preco_config && category.tipos_preco_config.length > 0) {
    rebuiltCat.tipos_preco_config = category.tipos_preco_config;
    rebuiltCat.tipos_preco = category.tipos_preco_config.map(tp => tp.nome);
  } else if (category.tipos_preco && category.tipos_preco.length > 0) {
    rebuiltCat.tipos_preco = category.tipos_preco;
  }
  if (category.tipo) rebuiltCat.tipo = category.tipo;
  if (category.adicionais_categorias?.length) {
    rebuiltCat.adicionais_categorias = category.adicionais_categorias;
  }
  if (category.tipo === 'adicional') {
    if (category.limite_quantidade) rebuiltCat.limite_quantidade = category.limite_quantidade;
    if (category.limite_minimo !== undefined && category.limite_minimo !== null) {
      rebuiltCat.limite_minimo = category.limite_minimo;
    }
    if (category.disponivel_em_categorias?.length) {
      rebuiltCat.disponivel_em_categorias = category.disponivel_em_categorias;
    }
    if (category.disponivel_em_produtos?.length) {
      rebuiltCat.disponivel_em_produtos = category.disponivel_em_produtos;
    }
  }
  assignVisivelEm(rebuiltCat, category);
}

function findOriginalCategoryInList(list: any[], category: MenuCategory, catIndex: number): any {
  return (
    list.find(
      (c: any) =>
        (category.id &&
          c.id &&
          (`categoria-${c.id}` === category.id || c.id === category.id)) ||
        (c.nome &&
          category.nome &&
          String(c.nome).trim() === String(category.nome).trim())
    ) ?? list[catIndex]
  );
}

function resolveCategoryStorageKey(
  category: MenuCategory,
  cardapioRaw: Record<string, unknown>,
  catIndex: number
): string {
  const fromId = (category.id || '').replace(/^categoria-/, '');
  if (fromId && cardapioRaw[fromId] != null) return fromId;
  const nome = category.nome?.trim();
  if (nome) {
    for (const [k, v] of Object.entries(cardapioRaw)) {
      if (k === 'categorias' || k === 'categories' || Array.isArray(v)) continue;
      if (v && typeof v === 'object' && String((v as { nome?: string }).nome || '').trim() === nome) {
        return k;
      }
    }
  }
  return fromId || (nome ? nome.toLowerCase().replace(/\s+/g, '_') : String(catIndex));
}

function rebuildFromCategoriasArrayFormat(menuData: MenuData, cardapioRaw: any): any | null {
  const list = cardapioRaw?.categorias ?? cardapioRaw?.categories;
  if (!Array.isArray(list) || list.length === 0 || !menuData?.categorias?.length) return null;
  const first = list[0];
  if (
    !looksLikeMenuCategory(first) &&
    !(first?.nome && (Array.isArray(first?.itens) || Array.isArray(first?.produtos)))
  ) {
    return null;
  }

  const defaultUseItens = list.some((c: any) => Array.isArray(c?.itens));

  const categorias = menuData.categorias.map((category, catIndex) => {
    const originalCat = findOriginalCategoryInList(list, category, catIndex) || {};
    const useItens = Array.isArray(originalCat.itens)
      ? true
      : Array.isArray(originalCat.produtos) || Array.isArray(originalCat.products)
        ? false
        : defaultUseItens;
    const items = mapMenuProductsToRawItems(category.produtos || []);
    const rebuiltCat: Record<string, unknown> = {
      ...originalCat,
      ordem: category.ordem ?? catIndex,
      nome: category.nome,
      ativo: category.ativo === true,
    };
    if (useItens) {
      rebuiltCat.itens = items;
      delete rebuiltCat.produtos;
      delete rebuiltCat.products;
    } else {
      rebuiltCat.produtos = items;
      delete rebuiltCat.itens;
    }
    applyMenuCategoryMetadataToRaw(rebuiltCat, category);
    return rebuiltCat;
  });

  return { ...cardapioRaw, categorias };
}

/**
 * Mapeia um objeto de categoria cru (formato API / `categorias: []`) para MenuCategory.
 * Usado no formato em lista e quando a chave `categorias` é processada no normalizeMenuData.
 */
function mapRawCategoryToMenuCategory(cat: any, index: number): MenuCategory {
  let tipos_preco_config: PriceTypeConfig[] | undefined = undefined;
  if (cat.tipos_preco_config && Array.isArray(cat.tipos_preco_config)) {
    tipos_preco_config = cat.tipos_preco_config;
  } else if (cat.tipos_preco && Array.isArray(cat.tipos_preco)) {
    tipos_preco_config = cat.tipos_preco.map((tp: string) => ({
      nome: tp,
      preco_sugerido: undefined,
      sabores: undefined,
    }));
  }

  return {
    id: cat.id || `categoria-${index}`,
    nome: cat.nome || cat.name || cat.title,
    ordem: cat.ordem !== undefined && cat.ordem !== null ? cat.ordem : undefined,
    ativo: isMenuCategoryActive(cat),
    produtos: (cat.produtos || cat.products || cat.itens || []).map((prod: any, prodIndex: number) => ({
      id: prod.id || `produto-${index}-${prodIndex}`,
      nome: prod.nome || prod.name || prod.title,
      descricao: prod.descricao || prod.description || prod.desc,
      disponivel: prod.trial_locked === true ? false : isMenuItemAvailable(prod),
      trial_locked: prod.trial_locked === true ? true : undefined,
      duracao: typeof prod.duracao === "number" ? prod.duracao : undefined,
      precos: prod.precos || (prod.preco !== undefined ? { padrao: prod.preco } : {}),
      imagem: prod.imagem || prod.image_url,
      ordem: prod.ordem !== undefined && prod.ordem !== null ? prod.ordem : undefined,
      ...visivelEmField(prod),
    })),
    tipos_preco: cat.tipos_preco || undefined,
    tipos_preco_config: tipos_preco_config,
    tipo: cat.tipo || undefined,
    adicionais_categorias: cat.adicionais_categorias || undefined,
    limite_quantidade: cat.limite_quantidade !== undefined && cat.limite_quantidade !== null ? cat.limite_quantidade : undefined,
    limite_minimo: cat.limite_minimo !== undefined && cat.limite_minimo !== null ? cat.limite_minimo : undefined,
    disponivel_em_categorias: Array.isArray(cat.disponivel_em_categorias) ? cat.disponivel_em_categorias : undefined,
    disponivel_em_produtos: Array.isArray(cat.disponivel_em_produtos) ? cat.disponivel_em_produtos : undefined,
    ...visivelEmField(cat),
  };
}

/**
 * Categorias tipo `adicional` com `disponivel_em_categorias` / `disponivel_em_produtos` devem
 * aparecer em `adicionais_categorias` das categorias-alvo (igual ao painel admin).
 */
function mergeAdicionaisDisponivelEmIntoTargets(categorias: MenuCategory[]): void {
  categorias.forEach((adicionalCat) => {
    if (adicionalCat.tipo !== 'adicional') return;
    const nomesCategorias = adicionalCat.disponivel_em_categorias;
    const idsOuNomesProdutos = adicionalCat.disponivel_em_produtos;
    if (!nomesCategorias?.length && !idsOuNomesProdutos?.length) return;
    const nomeAdicional =
      adicionalCat.nome || formatCategoryDisplayName((adicionalCat.id || '').replace(/^categoria-/, ''));
    categorias.forEach((outra) => {
      if (outra.tipo === 'adicional' || outra.id === adicionalCat.id) return;
      let adicionar = false;
      if (nomesCategorias?.length && outra.nome && nomesCategorias.includes(outra.nome)) {
        adicionar = true;
      }
      if (!adicionar && idsOuNomesProdutos?.length && outra.produtos?.length) {
        const temProduto = outra.produtos.some((p: any) =>
          idsOuNomesProdutos!.some((ref: string | number) => {
            const refStr = String(ref).trim();
            return (
              (p.id != null && String(p.id).trim() === refStr) ||
              (p.nome && String(p.nome).trim() === refStr)
            );
          })
        );
        if (temProduto) adicionar = true;
      }
      if (adicionar) {
        const atual = outra.adicionais_categorias || [];
        if (!atual.includes(nomeAdicional)) {
          outra.adicionais_categorias = [...atual, nomeAdicional];
        }
      }
    });
  });
}

// Função para normalizar dados de cardápio
export function normalizeMenuData(rawData: any): MenuData | null {
  if (!rawData) return null;
  
  // Processar TODOS os tipos de dados (pizzas, refrigerantes, lanches, etc.)
  const categorias: MenuCategory[] = [];
  
  // Processar cada chave do objeto que pode conter categorias
  Object.entries(rawData).forEach(([key, value]: [string, any]) => {
    
          // Se o valor tem estrutura similar a pizzas (com itens)
      if (value && typeof value === 'object' && value.itens && Array.isArray(value.itens)) {
      const produtos: MenuProduct[] = value.itens.map((item: any, index: number) => {
        return {
          id: item.id || `${key}-${index}`,
          nome: item.nome || item.name || item.title,
          descricao: item.descricao || item.description || item.desc,
          disponivel: item.trial_locked === true ? false : isMenuItemAvailable(item),
          trial_locked: item.trial_locked === true ? true : undefined,
          duracao: typeof item.duracao === "number" ? item.duracao : undefined,
          precos: item.precos || (item.preco !== undefined ? { 'padrao': item.preco } : {}),
          imagem: item.imagem || item.image_url,
          ordem: item.ordem !== undefined && item.ordem !== null ? item.ordem : undefined,
          ...visivelEmField(item)
        };
      });
      
      // Migrar tipos_preco antigo para tipos_preco_config
      let tipos_preco_config: PriceTypeConfig[] | undefined = undefined;
      if (value.tipos_preco_config && Array.isArray(value.tipos_preco_config)) {
        tipos_preco_config = value.tipos_preco_config;
      } else if (value.tipos_preco && Array.isArray(value.tipos_preco)) {
        // Migrar estrutura antiga para nova
        tipos_preco_config = value.tipos_preco.map((tp: string) => ({
          nome: tp,
          preco_sugerido: undefined,
          sabores: undefined
        }));
      }
      
      categorias.push({
        id: `categoria-${key}`,
        nome: (value.nome && String(value.nome).trim()) ? String(value.nome).trim() : formatCategoryDisplayName(key),
        ordem: value.ordem !== undefined && value.ordem !== null ? value.ordem : undefined,
        ativo: isMenuCategoryActive(value),
        produtos: produtos,
        tipos_preco: value.tipos_preco || undefined, // Manter para compatibilidade
        tipos_preco_config: tipos_preco_config,
        tipo: value.tipo || undefined, // 'produto' | 'pizza' | 'adicional'
        adicionais_categorias: value.adicionais_categorias || undefined,
        limite_quantidade: value.limite_quantidade || undefined,
        limite_minimo: value.limite_minimo !== undefined && value.limite_minimo !== null ? value.limite_minimo : undefined,
        disponivel_em_categorias: Array.isArray(value.disponivel_em_categorias) ? value.disponivel_em_categorias : undefined,
        disponivel_em_produtos: Array.isArray(value.disponivel_em_produtos) ? value.disponivel_em_produtos : undefined,
        ...visivelEmField(value)
      });
    }
          // Se o valor é um array: lista de produtos (legado) OU `categorias: [ {...}, ... ]` (formato API)
      else if (Array.isArray(value)) {
      if (
        (key === 'categorias' || key === 'categories') &&
        value.length > 0 &&
        looksLikeMenuCategoryListEntry(value[0])
      ) {
        value.forEach((cat: any, index: number) => {
          categorias.push(mapRawCategoryToMenuCategory(cat, index));
        });
      } else {
      const produtos: MenuProduct[] = value.map((item: any, index: number) => {
        return {
          id: item.id || `${key}-${index}`,
          nome: item.nome || item.name || item.title,
          descricao: item.descricao || item.description || item.desc,
          disponivel: item.ativo === true || item.disponivel === true,
          precos: item.precos || (item.preco !== undefined ? { 'padrao': item.preco } : {}),
          imagem: item.imagem || item.image_url,
          ...visivelEmField(item)
        };
      });
      
      categorias.push({
        id: `categoria-${key}`,
        nome: formatCategoryDisplayName(key),
        ativo: true, // Array de produtos assume categoria ativa
        produtos: produtos,
        tipos_preco: undefined, // Arrays não têm tipos_preco definidos
        tipo: undefined,
        adicionais_categorias: undefined
      });
      }
    }
          // Se o valor tem subcategorias (como bebidas com refrigerantes_lata, refrigerantes_600ml)
      else if (value && typeof value === 'object' && !Array.isArray(value)) {
      
      // Agrupar produtos por tipo para bebidas
      if (key.toLowerCase() === 'bebidas') {
        const produtosAgrupados: { [tipo: string]: MenuProduct[] } = {};
        
        Object.entries(value).forEach(([subKey, subValue]: [string, any]) => {
          if (subValue && typeof subValue === 'object' && subValue.itens && Array.isArray(subValue.itens)) {
            const produtos: MenuProduct[] = subValue.itens.map((item: any, index: number) => {
              return {
                id: item.id || `${key}-${subKey}-${index}`,
                nome: item.nome || item.name || item.title,
                descricao: item.descricao || item.description || item.desc,
                disponivel: item.ativo === true,
                precos: item.precos || (item.preco !== undefined ? { 'padrao': item.preco } : {}),
                imagem: item.imagem || item.image_url,
                ...visivelEmField(item)
              };
            });
            
            // Determinar o tipo baseado na subcategoria
            let tipo = 'Outros';
            if (subKey.includes('refrigerante')) {
              tipo = 'Refrigerantes';
            } else if (subKey.includes('cerveja')) {
              tipo = 'Cervejas';
            } else if (subKey.includes('suco') || subKey.includes('natural')) {
              tipo = 'Sucos';
            } else if (subKey.includes('agua')) {
              tipo = 'Águas';
            }
            
            if (!produtosAgrupados[tipo]) {
              produtosAgrupados[tipo] = [];
            }
            produtosAgrupados[tipo].push(...produtos);
          }
        });
        
        // Criar categorias agrupadas
        Object.entries(produtosAgrupados).forEach(([tipo, produtos]) => {
          if (produtos.length > 0) {
            // Migrar tipos_preco para tipos_preco_config se existir
            let tipos_preco_config: PriceTypeConfig[] | undefined = undefined;
            
            categorias.push({
              id: `categoria-${key}-${tipo.toLowerCase()}`,
              nome: tipo,
              ativo: true, // Categorias agrupadas de bebidas assumem ativas
              produtos: produtos,
              tipos_preco: undefined, // Categorias agrupadas não têm tipos_preco definidos
              tipos_preco_config: tipos_preco_config,
              tipo: undefined,
              adicionais_categorias: undefined
            });
          }
        });
      } else {
        // Para outras categorias, manter o comportamento original
        Object.entries(value).forEach(([subKey, subValue]: [string, any]) => {
          if (subValue && typeof subValue === 'object' && subValue.itens && Array.isArray(subValue.itens)) {
            const produtos: MenuProduct[] = subValue.itens.map((item: any, index: number) => {
              return {
                id: item.id || `${key}-${subKey}-${index}`,
                nome: item.nome || item.name || item.title,
                descricao: item.descricao || item.description || item.desc,
                disponivel: item.ativo === true,
                precos: item.precos || (item.preco !== undefined ? { 'padrao': item.preco } : {}),
                imagem: item.imagem || item.image_url,
                ...visivelEmField(item)
              };
            });
            
            // Migrar tipos_preco antigo para tipos_preco_config
            let tipos_preco_config: PriceTypeConfig[] | undefined = undefined;
            if (subValue.tipos_preco_config && Array.isArray(subValue.tipos_preco_config)) {
              tipos_preco_config = subValue.tipos_preco_config;
            } else if (subValue.tipos_preco && Array.isArray(subValue.tipos_preco)) {
              tipos_preco_config = subValue.tipos_preco.map((tp: string) => ({
                nome: tp,
                preco_sugerido: undefined,
                sabores: undefined
              }));
            }
            
            categorias.push({
              id: `categoria-${key}-${subKey}`,
              nome: `${formatCategoryDisplayName(key)} - ${formatCategoryDisplayName(subKey)}`,
              ativo: subValue.ativo === true, // Preservar status ativo da subcategoria
              produtos: produtos,
              tipos_preco: subValue.tipos_preco || undefined, // Manter para compatibilidade
              tipos_preco_config: tipos_preco_config,
              tipo: subValue.tipo || undefined,
              adicionais_categorias: subValue.adicionais_categorias || undefined,
              limite_quantidade: subValue.limite_quantidade || undefined,
              limite_minimo: subValue.limite_minimo !== undefined && subValue.limite_minimo !== null ? subValue.limite_minimo : undefined,
              disponivel_em_categorias: Array.isArray(subValue.disponivel_em_categorias) ? subValue.disponivel_em_categorias : undefined,
              disponivel_em_produtos: Array.isArray(subValue.disponivel_em_produtos) ? subValue.disponivel_em_produtos : undefined,
              ...visivelEmField(subValue)
            });
          }
        });
      }
    }
  });

  mergeAdicionaisDisponivelEmIntoTargets(categorias);
  
  // Se encontrou categorias processadas, retornar
  if (categorias.length > 0) {
    return { categorias };
  }

  // Se for um array, tratar como array de produtos
  if (Array.isArray(rawData)) {
    return {
      produtos: rawData.map((item: any, index: number) => ({
        id: item.id || `produto-${index}`,
        nome: item.nome || item.name || item.title,
        descricao: item.descricao || item.description || item.desc,
        disponivel: item.disponivel === true || item.ativo === true,
        precos: item.precos || (item.preco !== undefined ? { 'padrao': item.preco } : {}),
        imagem: item.imagem || item.image_url,
        ...visivelEmField(item)
      }))
    };
  }

  // Se for um objeto com categorias (fallback quando nenhuma categoria foi extraída no loop por chaves)
  if (rawData.categorias || rawData.categories) {
    const rawCats = rawData.categorias || rawData.categories;
    if (!Array.isArray(rawCats)) return null;
    const categoriasLista = rawCats.map((cat: any, index: number) => mapRawCategoryToMenuCategory(cat, index));
    mergeAdicionaisDisponivelEmIntoTargets(categoriasLista);
    return { categorias: categoriasLista };
  }

  // Se for um objeto com produtos
  if (rawData.produtos || rawData.products) {
    const produtos = rawData.produtos || rawData.products;
    return {
      produtos: produtos.map((item: any, index: number) => ({
        id: item.id || `produto-${index}`,
        nome: item.nome || item.name || item.title,
        descricao: item.descricao || item.description || item.desc,
        disponivel: item.disponivel === true || item.ativo === true,
        precos: item.precos || {},
        imagem: item.imagem || item.image_url,
        ...visivelEmField(item)
      }))
    };
  }

  return null;
}

// Funções de API para conversas (mantidas do arquivo original)
export interface Conversation {
  id: string;
  customer_id: string;
  customer_name: string;
  customer_phone: string;
  status: 'open' | 'closed';
  created_at: string;
  updated_at: string;
  last_message?: string;
  unread_count?: number;
}

export interface Message {
  id: string;
  conversation_id: string;
  content: string;
  type: 'text' | 'image' | 'audio' | 'document';
  direction: 'in' | 'out';
  timestamp: string;
  status: 'sent' | 'delivered' | 'read' | 'failed';
}

export interface Customer {
  id: string;
  name: string;
  phone: string;
  email?: string;
  address?: string;
  created_at: string;
  updated_at: string;
}

// Configuração da API
const API_BASE_URL = API_URL;

export const api = {
  // Endpoints para conversas
  conversations: {
    list: () => `${API_BASE_URL}/conversations`,
    get: (id: string) => `${API_BASE_URL}/conversations/${id}`,
    messages: (id: string) => `${API_BASE_URL}/conversations/${id}/messages`,
    sendMessage: (id: string) => `${API_BASE_URL}/conversations/${id}/messages`,
    markAsRead: (id: string) => `${API_BASE_URL}/conversations/${id}/read`,
  },
  
  // Endpoints para clientes
  customers: {
    get: (id: string) => `${API_BASE_URL}/customers/${id}`,
    update: (id: string) => `${API_BASE_URL}/customers/${id}`,
  }
};

export type GetCustomerByCnpjOptions = {
  forceRefresh?: boolean;
  /** `public`: minimização LGPD (cardápio/rastreio). `internal`: painel da loja. */
  scope?: "public" | "internal";
  /** Com scope=public, filtra pending_orders só deste telefone. */
  telefone?: string;
};

function resolveGetCustomerOptions(
  forceRefreshOrOptions?: boolean | GetCustomerByCnpjOptions
): Required<Pick<GetCustomerByCnpjOptions, "forceRefresh" | "scope">> &
  Pick<GetCustomerByCnpjOptions, "telefone"> {
  if (typeof forceRefreshOrOptions === "boolean") {
    return { forceRefresh: forceRefreshOrOptions, scope: "internal" };
  }
  return {
    forceRefresh: forceRefreshOrOptions?.forceRefresh ?? false,
    scope: forceRefreshOrOptions?.scope ?? "internal",
    telefone: forceRefreshOrOptions?.telefone,
  };
}

function menuCacheKey(cnpj: string, scope: "public" | "internal", telefone?: string) {
  const tel = telefone?.replace(/\D/g, "") || "";
  return scope === "public" && tel ? `menu_${cnpj}_public_${tel}` : `menu_${cnpj}_${scope}`;
}

// Funções para buscar dados do cliente via n8n webhook
export async function getCustomerByCnpj(
  cnpj: string,
  forceRefreshOrOptions?: boolean | GetCustomerByCnpjOptions
): Promise<any> {
  try {
    const opts = resolveGetCustomerOptions(forceRefreshOrOptions);
    const { getMenuFromCache, setMenuInCache } = await import("./cache");
    const cacheKey = menuCacheKey(cnpj.replace(/\D/g, ""), opts.scope, opts.telefone);

    if (!opts.forceRefresh) {
      const cachedData = getMenuFromCache(cacheKey);
      if (cachedData) {
        logDebug("📦 Dados do cardápio carregados do cache para CNPJ:", cnpj, opts.scope);
        return {
          success: true,
          cliente: cachedData,
          fromCache: true,
        };
      }
    }

    logDebug("🌐 Buscando dados do cardápio no servidor para CNPJ:", cnpj, "scope:", opts.scope);

    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");

    const params = new URLSearchParams();
    params.set("cnpj", cnpj.replace(/\D/g, ""));
    params.set("scope", opts.scope);
    const tel = opts.telefone?.replace(/\D/g, "");
    if (tel && tel.length >= 10) params.set("telefone", tel);

    const url = `${N8N_WEBHOOK_URL}get-customer?${params.toString()}`;

    const customerHeaders: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (opts.scope !== "public" && N8N_AUTH_HEADER) {
      customerHeaders.Authorization = N8N_AUTH_HEADER;
    }
    const response = await panelFetch(url, {
      method: "GET",
      headers: customerHeaders,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get('content-type') || '';
    const text = await response.text();
    if (contentType.includes('text/html') || text.trimStart().startsWith('<')) {
      const hint = typeof process !== 'undefined' && process.env?.NODE_ENV === 'development'
        ? ' Em desenvolvimento, adicione REACT_APP_USE_PROXY=true no .env e reinicie o npm start.'
        : '';
      throw new Error(`Webhook get-customer respondeu com HTML em vez de JSON.${hint}`);
    }
    let data: any;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error('Resposta do get-customer não é JSON válido.');
    }
    
    logDebug("📋 Resposta bruta do webhook:", data);
    logDebug("📋 [getCustomerByCnpj] Verificando pay_types na resposta:");
    if (Array.isArray(data) && data.length > 0) {
      logDebug("📋 [getCustomerByCnpj] pay_types no array[0]:", data[0].pay_types);
    } else if (data.success && data.data) {
      logDebug("📋 [getCustomerByCnpj] pay_types no data.data:", data.data.pay_types);
    } else {
      logDebug("📋 [getCustomerByCnpj] pay_types no data:", data.pay_types);
    }
    
    // O webhook retorna um array com os dados do cliente
    if (Array.isArray(data) && data.length > 0) {
      const cliente = data[0]; // Pega o primeiro cliente encontrado
      logDebug("👤 Cliente encontrado:", cliente);
      logDebug("💳 [getCustomerByCnpj] pay_types no cliente:", cliente.pay_types, "Tipo:", typeof cliente.pay_types);
      
      normalizeCustomerPayTypes(cliente);
      logDebug("💳 [getCustomerByCnpj] pay_types normalizado:", cliente.pay_types);
      
      // Salvar no cache
      setMenuInCache(cacheKey, cliente);
      logDebug("💾 Dados salvos no cache");
      
      return {
        success: true,
        cliente: cliente,
        fromCache: false
      };
    }
    
    // Se não encontrou cliente
    if (Array.isArray(data) && data.length === 0) {
      return {
        success: false,
        error: "Cliente não encontrado",
        code: "NOT_FOUND"
      };
    }
    
    // Se retornou um objeto com success/data
    if (data.success && data.data) {
      const cliente = data.data;
      logDebug("💳 [getCustomerByCnpj] pay_types no data.data:", cliente.pay_types, "Tipo:", typeof cliente.pay_types);
      
      normalizeCustomerPayTypes(cliente);
      logDebug("💳 [getCustomerByCnpj] pay_types normalizado (data.data):", cliente.pay_types);
      
      // Salvar no cache
      setMenuInCache(cacheKey, cliente);
      logDebug("💾 Dados salvos no cache");
      
      return {
        success: true,
        cliente: cliente,
        fromCache: false
      };
    }
    
    // Fallback para outros formatos
    const cliente = data;
    logDebug("💳 [getCustomerByCnpj] pay_types no fallback:", cliente.pay_types, "Tipo:", typeof cliente.pay_types);
    
    normalizeCustomerPayTypes(cliente);
    
    return {
      success: true,
      cliente: cliente,
      fromCache: false
    };
  } catch (error) {
    logError('Erro ao buscar cliente por CNPJ:', error);
    throw error;
  }
}

// Função para processar dados do cardápio
export function processMenuData(rawData: any): MenuData | null {
  const m = normalizeMenuData(rawData);
  return m ? assignDefaultOrdem(m) : null;
}

// Função para reconstruir o JSON original a partir do MenuData processado
export function rebuildOriginalJson(menuData: MenuData, originalRawDataInput: any): any {
  logDebug("🔧 Iniciando rebuildOriginalJson...");
  logDebug("📋 MenuData recebido:", menuData);
  logDebug("📋 OriginalRawData recebido:", originalRawDataInput);

  const cardapioRaw = getCardapioSectionFromStoredMenu(originalRawDataInput);

  const fromCategoriasArray = rebuildFromCategoriasArrayFormat(menuData, cardapioRaw);
  if (fromCategoriasArray) {
    logDebug("✅ Rebuild formato categorias[] concluído");
    return fromCategoriasArray;
  }
  
  // Se o formato original tem múltiplas categorias (pizzas, refrigerantes, lanches, etc.)
  if (cardapioRaw && typeof cardapioRaw === 'object' && !Array.isArray(cardapioRaw)) {
    const rebuiltJson: any = {};
    
    // Reconstruir cada categoria baseada na estrutura original
    if (menuData.categorias) {
      menuData.categorias.forEach((category, catIndex) => {
        const categoryName = category.nome || '';
        logDebug(`🏷️ Processando categoria: ${categoryName}`);
        
        // Para categorias simples como "Pizzas", "Lanches", etc.
        if (!categoryName.includes(' - ') && categoryName !== 'Refrigerantes' && categoryName !== 'Cervejas' && categoryName !== 'Sucos' && categoryName !== 'Águas') {
          const categoryKey = resolveCategoryStorageKey(category, cardapioRaw as Record<string, unknown>, catIndex);
          logDebug(`🔍 Processando categoria simples: ${categoryKey}`);
          
          // Verificar se a categoria original tinha estrutura com itens
          const originalCategory = cardapioRaw[categoryKey];
          if (originalCategory && originalCategory.itens) {
                         // Estrutura com itens (como pizzas)
             rebuiltJson[categoryKey] = {
               ativo: category.ativo === true, // Preservar status ativo da categoria
               ordem: category.ordem ?? catIndex,
               ...(category.nome && category.nome.trim() ? { nome: category.nome.trim() } : {}),
               itens: (category.produtos || []).map((product, prodIndex) => {
                 const item: any = {
                   ordem: product.ordem ?? prodIndex,
                   nome: product.nome,
                   ativo: product.disponivel === true,
                   precos: product.precos || (product.preco !== undefined ? { 'padrao': product.preco } : {})
                 };
                 if (product.descricao) item.descricao = product.descricao;
                 if (product.imagem || product.image_url) item.imagem = product.imagem || product.image_url;
                 if (product.id) item.id = product.id;
                 stampTrialLock(item, product);
    assignVisivelEm(item, product);
                 return item;
               })
             };
             assignVisivelEm(rebuiltJson[categoryKey], category);
             
             // Adicionar tipos de preço se definidos na categoria (nova estrutura)
             if (category.tipos_preco_config && category.tipos_preco_config.length > 0) {
               rebuiltJson[categoryKey].tipos_preco_config = category.tipos_preco_config;
               // Manter compatibilidade com estrutura antiga
               rebuiltJson[categoryKey].tipos_preco = category.tipos_preco_config.map(tp => tp.nome);
             } else if (category.tipos_preco && category.tipos_preco.length > 0) {
               rebuiltJson[categoryKey].tipos_preco = category.tipos_preco;
             }
             
             // Adicionar tipo da categoria
             if (category.tipo) {
               rebuiltJson[categoryKey].tipo = category.tipo;
             }
             
             // Adicionar categorias de adicionais
             if (category.adicionais_categorias && category.adicionais_categorias.length > 0) {
               rebuiltJson[categoryKey].adicionais_categorias = category.adicionais_categorias;
             }
             
             // Adicionar limite máximo e mínimo (apenas para categorias de adicionais)
             if (category.tipo === 'adicional') {
               if (category.limite_quantidade) {
                 rebuiltJson[categoryKey].limite_quantidade = category.limite_quantidade;
                 logDebug(`✅ Limite de quantidade salvo para ${categoryKey}:`, category.limite_quantidade);
               }
               if (category.limite_minimo !== undefined && category.limite_minimo !== null) {
                 rebuiltJson[categoryKey].limite_minimo = category.limite_minimo;
               }
               if (Array.isArray(category.disponivel_em_categorias) && category.disponivel_em_categorias.length > 0) {
                 rebuiltJson[categoryKey].disponivel_em_categorias = category.disponivel_em_categorias;
               }
               if (Array.isArray(category.disponivel_em_produtos) && category.disponivel_em_produtos.length > 0) {
                 rebuiltJson[categoryKey].disponivel_em_produtos = category.disponivel_em_produtos;
               }
             }
            logDebug(`✅ Categoria com itens processada: ${categoryKey} (${(category.produtos || []).length} produtos)`, {
              tipo: category.tipo,
              limite_quantidade: category.limite_quantidade,
              salvo_no_json: rebuiltJson[categoryKey].limite_quantidade
            });
          } else {
                         // Estrutura simples (array de produtos) - SEMPRE incluir campo ativo da categoria
             // SEMPRE criar estrutura com campo ativo para todas as categorias
             rebuiltJson[categoryKey] = {
               ativo: category.ativo === true, // SEMPRE preservar status ativo da categoria
               ordem: category.ordem ?? catIndex,
               ...(category.nome && category.nome.trim() ? { nome: category.nome.trim() } : {}),
               itens: (category.produtos || []).map((product, prodIndex) => {
                 const item: any = {
                   ordem: product.ordem ?? prodIndex,
                   nome: product.nome,
                   ativo: product.disponivel === true,
                   precos: product.precos || {}
                 };
                 if (product.descricao) item.descricao = product.descricao;
                 if (product.imagem || product.image_url) item.imagem = product.imagem || product.image_url;
                 if (product.id) item.id = product.id;
                 stampTrialLock(item, product);
    assignVisivelEm(item, product);
                 return item;
               })
             };
             assignVisivelEm(rebuiltJson[categoryKey], category);
             
             // Adicionar tipos de preço se definidos na categoria (nova estrutura)
             if (category.tipos_preco_config && category.tipos_preco_config.length > 0) {
               rebuiltJson[categoryKey].tipos_preco_config = category.tipos_preco_config;
               // Manter compatibilidade com estrutura antiga
               rebuiltJson[categoryKey].tipos_preco = category.tipos_preco_config.map(tp => tp.nome);
             } else if (category.tipos_preco && category.tipos_preco.length > 0) {
               rebuiltJson[categoryKey].tipos_preco = category.tipos_preco;
             }
             
             // Adicionar tipo da categoria
             if (category.tipo) {
               rebuiltJson[categoryKey].tipo = category.tipo;
             }
             
             // Adicionar categorias de adicionais
             if (category.adicionais_categorias && category.adicionais_categorias.length > 0) {
               rebuiltJson[categoryKey].adicionais_categorias = category.adicionais_categorias;
             }
             
             // Adicionar limite máximo e mínimo (apenas para categorias de adicionais)
             if (category.tipo === 'adicional') {
               if (category.limite_quantidade) {
                 rebuiltJson[categoryKey].limite_quantidade = category.limite_quantidade;
                 logDebug(`✅ Limite de quantidade salvo para ${categoryKey}:`, category.limite_quantidade);
               }
               if (category.limite_minimo !== undefined && category.limite_minimo !== null) {
                 rebuiltJson[categoryKey].limite_minimo = category.limite_minimo;
               }
               if (Array.isArray(category.disponivel_em_categorias) && category.disponivel_em_categorias.length > 0) {
                 rebuiltJson[categoryKey].disponivel_em_categorias = category.disponivel_em_categorias;
               }
               if (Array.isArray(category.disponivel_em_produtos) && category.disponivel_em_produtos.length > 0) {
                 rebuiltJson[categoryKey].disponivel_em_produtos = category.disponivel_em_produtos;
               }
             }
             
             logDebug(`✅ Categoria simples SEMPRE com ativo processada: ${categoryKey} (${(category.produtos || []).length} produtos) - ativo: ${category.ativo}`, {
               tipo: category.tipo,
               limite_quantidade: category.limite_quantidade,
               salvo_no_json: rebuiltJson[categoryKey].limite_quantidade
             });
          }
        }
        // Para categorias aninhadas (ex: "Bebidas - Refrigerantes Lata")
        else if (categoryName.includes(' - ')) {
          const [mainCategory, subCategory] = categoryName.split(' - ');
          const mainKey = mainCategory.toLowerCase();
          const subKey = subCategory.toLowerCase().replace(/\s+/g, '_');
          
          // Inicializar categoria principal se não existir
          if (!rebuiltJson[mainKey]) {
            rebuiltJson[mainKey] = {};
          }
          
                     // Adicionar subcategoria
           rebuiltJson[mainKey][subKey] = {
             ativo: category.ativo === true, // Preservar status ativo da categoria
             ordem: category.ordem ?? catIndex,
             itens: (category.produtos || []).map((product, prodIndex) => {
               const item: any = {
                 ordem: product.ordem ?? prodIndex,
                 nome: product.nome,
                 ativo: product.disponivel === true,
                 precos: product.precos || {}
               };
               if (product.descricao) item.descricao = product.descricao;
               if (product.imagem || product.image_url) item.imagem = product.imagem || product.image_url;
               if (product.id) item.id = product.id;
               stampTrialLock(item, product);
    assignVisivelEm(item, product);
               return item;
             })
           };
           assignVisivelEm(rebuiltJson[mainKey][subKey], category);
           
           // Adicionar tipos de preço se definidos na categoria (nova estrutura)
           if (category.tipos_preco_config && category.tipos_preco_config.length > 0) {
             rebuiltJson[mainKey][subKey].tipos_preco_config = category.tipos_preco_config;
             // Manter compatibilidade com estrutura antiga
             rebuiltJson[mainKey][subKey].tipos_preco = category.tipos_preco_config.map(tp => tp.nome);
           } else if (category.tipos_preco && category.tipos_preco.length > 0) {
             rebuiltJson[mainKey][subKey].tipos_preco = category.tipos_preco;
           }
           
           // Adicionar tipo da categoria
           if (category.tipo) {
             rebuiltJson[mainKey][subKey].tipo = category.tipo;
           }
           
           // Adicionar categorias de adicionais
           if (category.adicionais_categorias && category.adicionais_categorias.length > 0) {
             rebuiltJson[mainKey][subKey].adicionais_categorias = category.adicionais_categorias;
           }
           
           // Adicionar limite máximo e mínimo (apenas para categorias de adicionais)
           if (category.tipo === 'adicional') {
             if (category.limite_quantidade) {
               rebuiltJson[mainKey][subKey].limite_quantidade = category.limite_quantidade;
               logDebug(`✅ Limite de quantidade salvo para ${mainKey}.${subKey}:`, category.limite_quantidade);
             }
             if (category.limite_minimo !== undefined && category.limite_minimo !== null) {
               rebuiltJson[mainKey][subKey].limite_minimo = category.limite_minimo;
             }
             if (Array.isArray(category.disponivel_em_categorias) && category.disponivel_em_categorias.length > 0) {
               rebuiltJson[mainKey][subKey].disponivel_em_categorias = category.disponivel_em_categorias;
             }
             if (Array.isArray(category.disponivel_em_produtos) && category.disponivel_em_produtos.length > 0) {
               rebuiltJson[mainKey][subKey].disponivel_em_produtos = category.disponivel_em_produtos;
             }
           }
          logDebug(`✅ Categoria aninhada processada: ${mainKey}.${subKey}`, {
            tipo: category.tipo,
            limite_quantidade: category.limite_quantidade,
            salvo_no_json: rebuiltJson[mainKey][subKey].limite_quantidade
          });
        }
        // Para categorias agrupadas de bebidas
        else if (categoryName === 'Refrigerantes' || categoryName === 'Cervejas' || categoryName === 'Sucos' || categoryName === 'Águas') {
          const mainKey = 'bebidas';
          
          // Inicializar categoria principal se não existir
          if (!rebuiltJson[mainKey]) {
            rebuiltJson[mainKey] = {};
          }
          
          // Mapear produtos de volta para suas subcategorias originais
          (category.produtos || []).forEach(product => {
            let subKey = 'outros';
            
            if (categoryName === 'Refrigerantes') {
              if (product.nome?.toLowerCase().includes('600ml')) {
                subKey = 'refrigerantes_600ml';
              } else {
                subKey = 'refrigerantes_lata';
              }
            } else if (categoryName === 'Cervejas') {
              subKey = 'cervejas';
            } else if (categoryName === 'Sucos') {
              subKey = 'sucos';
            } else if (categoryName === 'Águas') {
              subKey = 'aguas';
            }
            
            // Inicializar subcategoria se não existir
            if (!rebuiltJson[mainKey][subKey]) {
              rebuiltJson[mainKey][subKey] = {
                ativo: category.ativo === true, // Preservar status ativo da categoria
                ordem: category.ordem ?? catIndex,
                itens: []
              };
              assignVisivelEm(rebuiltJson[mainKey][subKey], category);
              
              // Adicionar tipos de preço se definidos na categoria (nova estrutura)
              if (category.tipos_preco_config && category.tipos_preco_config.length > 0) {
                rebuiltJson[mainKey][subKey].tipos_preco_config = category.tipos_preco_config;
                // Manter compatibilidade com estrutura antiga
                rebuiltJson[mainKey][subKey].tipos_preco = category.tipos_preco_config.map(tp => tp.nome);
              } else if (category.tipos_preco && category.tipos_preco.length > 0) {
                rebuiltJson[mainKey][subKey].tipos_preco = category.tipos_preco;
              }
              
              // Adicionar tipo da categoria
              if (category.tipo) {
                rebuiltJson[mainKey][subKey].tipo = category.tipo;
              }
              
              // Adicionar categorias de adicionais
              if (category.adicionais_categorias && category.adicionais_categorias.length > 0) {
                rebuiltJson[mainKey][subKey].adicionais_categorias = category.adicionais_categorias;
              }
            }
            
                         // Adicionar produto à subcategoria
             const item: any = {
               ordem: product.ordem,
               nome: product.nome,
               ativo: product.disponivel === true,
               precos: product.precos || {}
             };
             if (product.descricao) item.descricao = product.descricao;
             if (product.imagem || product.image_url) item.imagem = product.imagem || product.image_url;
             if (product.id) item.id = product.id;
             stampTrialLock(item, product);
    assignVisivelEm(item, product);
             
             rebuiltJson[mainKey][subKey].itens.push(item);
          });
          logDebug(`✅ Categoria de bebidas processada: ${mainKey}`);
        }
      });
    }
    
    logDebug("🎯 JSON reconstruído final:", rebuiltJson);
    return rebuiltJson;
  }
  
  // Se o formato original era array de produtos
  if (Array.isArray(cardapioRaw)) {
    if (menuData.produtos) {
      return menuData.produtos.map((product, prodIndex) => {
        const item: any = {
          ordem: product.ordem ?? prodIndex,
          nome: product.nome,
          ativo: product.disponivel === true,
          precos: product.precos || {}
        };
        if (product.descricao) item.descricao = product.descricao;
        if (product.imagem || product.image_url) item.imagem = product.imagem || product.image_url;
        if (product.id) item.id = product.id;
        stampTrialLock(item, product);
    assignVisivelEm(item, product);
        return item;
      });
    }
  }
  
  // Fallback: retornar o menuData como está
  logDebug("⚠️ Usando fallback - retornando menuData como está");
  return menuData;
}

// Função para validar a estrutura do JSON antes de enviar
export function validateMenuJsonStructure(cardapioJson: any): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  logDebug("🔍 Validando estrutura do JSON:", cardapioJson);
  
  if (!cardapioJson) {
    errors.push("JSON do cardápio está vazio ou nulo");
    return { isValid: false, errors };
  }
  
  // Se for string, tentar fazer parse
  let parsedJson = cardapioJson;
  if (typeof cardapioJson === 'string') {
    try {
      parsedJson = JSON.parse(cardapioJson);
    } catch (e) {
      errors.push("JSON inválido - não é possível fazer parse da string");
      return { isValid: false, errors };
    }
  }
  
  // Verificar se é um objeto
  if (typeof parsedJson !== 'object' || parsedJson === null) {
    errors.push("JSON deve ser um objeto válido");
    return { isValid: false, errors };
  }
  
  // Verificar se é nova estrutura (com cardapio e/ou servicos) ou estrutura antiga
  const hasNewStructure = parsedJson.cardapio || parsedJson.servicos;
  
  if (hasNewStructure) {
    logDebug("📦 Detectada nova estrutura com cardapio/servicos");
    
    // Validar seção cardapio se existir
    if (parsedJson.cardapio) {
      const cardapioValid = validateStructureSection(parsedJson.cardapio, "cardapio", errors);
      if (!cardapioValid && !parsedJson.servicos) {
        // Se cardapio é inválido E não tem servicos, é erro
        return { isValid: false, errors };
      }
    }
    
    // Validar seção servicos se existir
    if (parsedJson.servicos) {
      const servicosValid = validateServicesSection(parsedJson.servicos, errors);
      if (!servicosValid && !parsedJson.cardapio) {
        // Se servicos é inválido E não tem cardapio, é erro
        return { isValid: false, errors };
      }
    }
    
    // Deve ter pelo menos uma seção válida
    if (!parsedJson.cardapio && !parsedJson.servicos) {
      errors.push("JSON deve ter pelo menos uma seção: cardapio ou servicos");
      return { isValid: false, errors };
    }
  } else {
    logDebug("📦 Detectada estrutura antiga, validando como cardapio");
    // Estrutura antiga - validar como cardapio
    const categories = Object.keys(parsedJson);
    if (categories.length === 0) {
      errors.push("JSON deve ter pelo menos uma categoria (pizzas, lanches, bebidas, etc.)");
      return { isValid: false, errors };
    }
  }
  
  // Se estrutura antiga, validar categorias diretamente
  if (!hasNewStructure) {
    const categories = Object.keys(parsedJson);
    categories.forEach(categoryKey => {
      const category = parsedJson[categoryKey];
      validateCategoryStructure(category, categoryKey, errors);
    });
  }
  
  const isValid = errors.length === 0;
  
  if (isValid) {
    logDebug("✅ Estrutura do JSON validada com sucesso");
  } else {
    logDebug("❌ Erros encontrados na validação:", errors);
  }
  
  return { isValid, errors };
}

// Função auxiliar para validar uma seção (cardapio)
function validateStructureSection(section: any, sectionName: string, errors: string[]): boolean {
  if (!section || typeof section !== 'object') {
    errors.push(`Seção "${sectionName}" deve ser um objeto válido`);
    return false;
  }
  
  const categories = Object.keys(section);
  if (categories.length === 0) {
    logDebug(`⚠️ Seção "${sectionName}" está vazia, mas isso é permitido`);
    return true;
  }
  
  let hasValidCategory = false;
  categories.forEach(categoryKey => {
    const category = section[categoryKey];
    if (validateCategoryStructure(category, `${sectionName}.${categoryKey}`, errors)) {
      hasValidCategory = true;
    }
  });
  
  return hasValidCategory || categories.length === 0;
}

// Função auxiliar para validar seção de serviços
function validateServicesSection(servicos: any, errors: string[]): boolean {
  if (!servicos || typeof servicos !== 'object') {
    errors.push('Seção "servicos" deve ser um objeto válido');
    return false;
  }
  
  if (!servicos.categorias || !Array.isArray(servicos.categorias)) {
    errors.push('Seção "servicos" deve ter um array "categorias"');
    return false;
  }
  
  if (servicos.categorias.length === 0) {
    logDebug('⚠️ Seção "servicos" tem categorias vazias, mas isso é permitido');
    return true;
  }
  
  let hasValidCategory = false;
  servicos.categorias.forEach((categoria: any, index: number) => {
    if (validateServiceCategory(categoria, index, errors)) {
      hasValidCategory = true;
    }
  });
  
  return hasValidCategory || servicos.categorias.length === 0;
}

// Função auxiliar para validar categoria de serviço
function validateServiceCategory(categoria: any, index: number, errors: string[]): boolean {
  if (!categoria || typeof categoria !== 'object') {
    errors.push(`Categoria de serviço ${index} deve ser um objeto válido`);
    return false;
  }
  
  if (!categoria.nome) {
    errors.push(`Categoria de serviço ${index} deve ter um nome`);
    return false;
  }
  
  if (categoria.ativo === undefined) {
    errors.push(`Categoria de serviço ${index} deve ter status ativo/inativo`);
    return false;
  }
  
  return true;
}

// Função auxiliar para validar estrutura de categoria
function validateCategoryStructure(category: any, categoryKey: string, errors: string[]): boolean {
    
    // Verificar se a categoria é um objeto
    if (typeof category !== 'object' || category === null) {
      errors.push(`Categoria "${categoryKey}" deve ser um objeto válido`);
      return false;
    }
    
    // Verificar se é array (estrutura simples) ou objeto com subcategorias
    if (Array.isArray(category)) {
      // Estrutura simples: array de produtos
      category.forEach((product, index) => {
        if (typeof product !== 'object' || product === null) {
          errors.push(`Produto ${index} na categoria "${categoryKey}" deve ser um objeto válido`);
          return;
        }
        
        // Validar campos obrigatórios
        if (!product.nome) {
          errors.push(`Produto ${index} na categoria "${categoryKey}" deve ter um nome`);
        }
        
        if (product.ativo === undefined) {
          errors.push(`Produto ${index} na categoria "${categoryKey}" deve ter status ativo/inativo`);
        }
        
        if (!product.precos || typeof product.precos !== 'object') {
          errors.push(`Produto ${index} na categoria "${categoryKey}" deve ter preços válidos`);
        }
      });
    } else {
      // Estrutura com subcategorias (ex: pizzas.normais, bebidas.refrigerantes_lata)
      const subCategories = Object.keys(category);
      subCategories.forEach(subCategoryKey => {
        const subCategory = category[subCategoryKey];
        
        if (Array.isArray(subCategory)) {
          // Subcategoria é um array de produtos
          subCategory.forEach((product, index) => {
            if (typeof product !== 'object' || product === null) {
              errors.push(`Produto ${index} na subcategoria "${categoryKey}.${subCategoryKey}" deve ser um objeto válido`);
              return;
            }
            
            // Validar campos obrigatórios
            if (!product.nome) {
              errors.push(`Produto ${index} na subcategoria "${categoryKey}.${subCategoryKey}" deve ter um nome`);
            }
            
            if (product.ativo === undefined) {
              errors.push(`Produto ${index} na subcategoria "${categoryKey}.${subCategoryKey}" deve ter status ativo/inativo`);
            }
            
            if (!product.precos || typeof product.precos !== 'object') {
              errors.push(`Produto ${index} na subcategoria "${categoryKey}.${subCategoryKey}" deve ter preços válidos`);
            }
          });
        } else if (typeof subCategory === 'object' && subCategory !== null) {
          // Subcategoria pode ter configurações adicionais
          logDebug(`ℹ️ Subcategoria "${categoryKey}.${subCategoryKey}" tem estrutura de objeto - validação flexível`);
        }
      });
    }
    
    return true;
}

/**
 * Upload de imagem via backend Kotlin + MinIO (`/webhook/upload-produto-imagem`).
 */
const UPLOAD_IMAGE_TIMEOUT_MS = 30000;

async function uploadImageViaBackend(
  cnpj: string,
  fileName: string,
  base64: string,
  contentType?: string
): Promise<{ url: string; path: string; fileName: string }> {
  const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
  if (!N8N_WEBHOOK_URL) {
    throw new Error("Backend não configurado (REACT_APP_N8N_WEBHOOK_URL).");
  }

  const cnpjClean = cnpj.replace(/\D/g, "");
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), UPLOAD_IMAGE_TIMEOUT_MS);

  let response: Response;
  try {
    response = await panelFetch(`${N8N_WEBHOOK_URL}upload-produto-imagem`, {
      method: "POST",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        cnpj: cnpjClean,
        name: fileName,
        base64,
        mime: contentType || "image/jpeg",
      }),
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timeoutId);
    if ((e as Error).name === "AbortError") {
      throw new Error("Timeout ao enviar imagem. Verifique a conexão com o backend.");
    }
    throw new Error(`Erro de conexão: ${(e as Error).message}`);
  }
  clearTimeout(timeoutId);

  const text = await response.text();
  if (!response.ok) {
    let errorMsg = `Upload falhou: ${response.status}`;
    try {
      const errorData = JSON.parse(text);
      errorMsg += ` - ${errorData.message || errorData.error || text}`;
    } catch {
      errorMsg += ` - ${text.slice(0, 200)}`;
    }
    throw new Error(errorMsg);
  }

  let data: Record<string, unknown> = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error("Resposta inválida do upload de imagem.");
  }

  const url = String(data.public_url || data.url || "");
  if (!url) {
    throw new Error("Resposta do upload sem URL pública.");
  }
  const key = String(data.key || `${cnpjClean}/${fileName}`);
  return { url, path: key, fileName };
}

async function deleteImageViaBackend(
  cnpj: string,
  fileName: string
): Promise<{ success: boolean; deleted: boolean; fileName: string }> {
  const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
  if (!N8N_WEBHOOK_URL) {
    throw new Error("Backend não configurado (REACT_APP_N8N_WEBHOOK_URL).");
  }

  const cnpjClean = cnpj.replace(/\D/g, "");
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  let response: Response;
  try {
    response = await panelFetch(`${N8N_WEBHOOK_URL}delete-produto-imagem`, {
      method: "POST",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ cnpj: cnpjClean, name: fileName }),
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timeoutId);
    if ((e as Error).name === "AbortError") {
      throw new Error("Timeout ao excluir imagem.");
    }
    throw new Error(`Erro de conexão: ${(e as Error).message}`);
  }
  clearTimeout(timeoutId);

  if (!response.ok && response.status !== 404) {
    const text = await response.text();
    throw new Error(`Exclusão falhou: ${response.status} - ${text.slice(0, 200)}`);
  }

  return { success: true, deleted: response.ok, fileName };
}

/**
 * Upload de imagem de produto — MinIO via backend.
 */
export async function uploadProdutoImagem(
  cnpj: string,
  fileName: string,
  base64: string,
  contentType?: string
): Promise<{ url: string; path: string; fileName: string }> {
  return uploadImageViaBackend(cnpj, fileName, base64, contentType);
}

/**
 * Exclui imagem de produto — MinIO via backend.
 */
export async function deleteProdutoImagem(
  cnpj: string,
  fileName: string
): Promise<{ success: boolean; deleted: boolean; fileName: string }> {
  return deleteImageViaBackend(cnpj, fileName);
}

/** Pastas no Storage por nicho (cardápio/catálogo). */
export const NICHOS_STORAGE_FOLDERS: Record<string, string> = {
  delivery: 'Cardapios_delivery',
  clinica: 'Catalogos_Clinicas',
  oficina: 'Catalogos_oficinas',
  tattoo: 'Catalogos_Tattoo',
  digital: 'Catalogos_Digital',
};

function getCardapioFolderByNicho(nicho?: string | null): string {
  const key = (nicho || 'delivery').toLowerCase();
  return NICHOS_STORAGE_FOLDERS[key] ?? NICHOS_STORAGE_FOLDERS.delivery;
}

/**
 * Upload de imagem do cardápio/catálogo ou logomarca — MinIO via backend.
 * (O nicho legado só afetava pastas no Supabase; no MinIO usa `{cnpj}/{fileName}`.)
 */
export async function uploadCardapioImagem(
  cnpj: string,
  fileName: string,
  base64: string,
  contentType?: string,
  _nicho?: string | null
): Promise<{ url: string; path: string; fileName: string }> {
  return uploadImageViaBackend(cnpj, fileName, base64, contentType);
}

/**
 * Exclui imagem do cardápio/catálogo ou logomarca — MinIO via backend.
 */
export async function deleteCardapioImagem(
  cnpj: string,
  fileName: string,
  _nicho?: string | null
): Promise<{ success: boolean; deleted: boolean; fileName: string }> {
  return deleteImageViaBackend(cnpj, fileName);
}

// Função para atualizar cardápio do cliente
export async function updateCustomerCardapio(cnpj: string, cardapioJson: any): Promise<any> {
  try {
    logDebug("🚀 updateCustomerCardapio INICIADO");
    logDebug("👤 CNPJ:", cnpj);
    logDebug("📊 Dados para salvar:", JSON.stringify(cardapioJson, null, 2));
    
    // Importar funções de cache
    const { invalidateMenuCache } = await import('./cache');
    
    // Configuração do n8n webhook
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import('../config');
    
    const url = `${N8N_WEBHOOK_URL}save-cardapio`;
    logDebug("🌐 URL do webhook:", url);
    
    // Log para debug - temporariamente desabilitando validação
    logDebug("🔍 JSON para salvar (validação desabilitada):", cardapioJson);
    
    // Garantir que o JSON está no formato correto
    let formattedCardapioJson;
    if (typeof cardapioJson === 'string') {
      try {
        const parsedJson = JSON.parse(cardapioJson);
        formattedCardapioJson = JSON.stringify(parsedJson, null, 2);
      } catch (e) {
        logDebug("⚠️ Cardápio já está em formato string, mantendo como está");
        formattedCardapioJson = cardapioJson;
      }
    } else {
      // Se é um objeto, formatar com indentação
      formattedCardapioJson = JSON.stringify(cardapioJson, null, 2);
    }
    
    logDebug("📤 Enviando JSON formatado:", formattedCardapioJson);
    
    const requestBody = { 
      cnpj,
      cardapio_json: formattedCardapioJson 
    };
    logDebug("📦 Body da requisição:", JSON.stringify(requestBody, null, 2));
    
    logDebug("📡 Enviando requisição para webhook...");
    const response = await panelFetch(url, {
      method: 'POST',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    logDebug("📡 Status da resposta:", response.status);
    logDebug("📡 Headers da resposta:", Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      logError("❌ Erro HTTP:", response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    // Verificar se a resposta tem conteúdo antes de tentar fazer parse
    const responseText = await response.text();
    logDebug("📥 Resposta bruta do webhook:", responseText);
    
    let data;
    if (responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        logDebug("✅ Resposta do webhook save-cardapio:", data);
             } catch (parseError) {
         logError("❌ Erro ao fazer parse da resposta:", parseError);
         const errorMessage = parseError instanceof Error ? parseError.message : String(parseError);
         throw new Error(`Erro ao processar resposta do servidor: ${errorMessage}`);
       }
    } else {
      logDebug("⚠️ Resposta vazia do webhook, considerando sucesso");
      data = { success: true, message: "Alterações salvas com sucesso" };
    }
    
    // Invalidar cache após salvar com sucesso
    invalidateMenuCache(cnpj);
    logDebug("🗑️ Cache invalidado após salvar alterações");
    
    return data;
  } catch (error) {
    logError('Erro ao atualizar cardápio do cliente:', error);
    throw error;
  }
}

// Função para buscar dados do cliente e senha hash via webhook n8n (para login)
/** Normaliza resposta do backend Kotlin para o formato legado n8n (`empresa[]` + nome). */
function normalizeCheckCnpjResponse(data: any, cnpj: string): any {
  if (!data || data.exists !== true) return data;
  const clean = String(cnpj || "").replace(/\D/g, "");
  const senhaDefinida = Boolean(data.senha_definida ?? data.senha_criada ?? data.password_set);

  const sliceFrom = (source: Record<string, any>): Record<string, any> => ({
    id: source.id,
    cnpj: source.cnpj ?? clean,
    nome_empresa: source.nome_empresa ?? source.company_name ?? source.nome_fantasia,
    nome: source.nome,
    email: source.email,
    email_cliente: source.email_cliente ?? source.email,
    senha_definida: source.senha_definida ?? senhaDefinida,
    senha_criada: source.senha_criada ?? senhaDefinida,
    kitchen_email: source.kitchen_email ?? data.kitchen_email,
    kitchen_stores: source.kitchen_stores ?? data.kitchen_stores,
    mod_atendimento: source.mod_atendimento ?? source.module_orders_enabled ?? data.mod_atendimento ?? true,
    mod_motoboy: source.mod_motoboy ?? source.module_couriers_enabled ?? false,
    mod_gerente: source.mod_gerente ?? source.module_manager_enabled ?? false,
    mod_agendamento: source.mod_agendamento ?? source.module_appointments_enabled ?? false,
    ativo: source.ativo ?? source.active ?? data.active,
    aprovado: source.aprovado ?? source.approved ?? data.approved,
    pay_types: source.pay_types ?? source.payment_methods,
  });

  let empresaRow: Record<string, any>;
  if (Array.isArray(data.empresa) && data.empresa.length > 0) {
    empresaRow = { ...sliceFrom(data), ...sliceFrom(data.empresa[0] as Record<string, any>) };
  } else if (data.empresa && typeof data.empresa === "object") {
    empresaRow = { ...sliceFrom(data), ...sliceFrom(data.empresa as Record<string, any>) };
  } else {
    empresaRow = sliceFrom(data);
  }

  const nome =
    pickNomeEmpresaFromRow({ ...data, ...empresaRow }) ||
    (typeof empresaRow.nome === "string" && empresaRow.nome.trim() ? empresaRow.nome.trim() : "");
  if (nome) {
    empresaRow.nome = nome;
    empresaRow.nome_empresa = empresaRow.nome_empresa || nome;
  }

  return { ...data, empresa: [empresaRow] };
}

async function enrichCheckCnpjProfile(data: any, cnpj: string): Promise<any> {
  const clean = String(cnpj || "").replace(/\D/g, "");
  let merged = normalizeCheckCnpjResponse(data, clean);
  if (pickNomeEmpresaFromRow(merged) || pickNomeEmpresaFromRow(merged.empresa?.[0] ?? {})) {
    return merged;
  }

  const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");

  try {
    const pvRes = await panelFetch(`${N8N_WEBHOOK_URL}page-verify`, {
      method: "POST",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ cnpj: clean }),
    });
    if (pvRes.ok) {
      const pv = await pvRes.json();
      const nome = pickNomeEmpresaFromRow(pv);
      if (nome && merged.empresa?.[0]) {
        merged.empresa[0].nome = nome;
        merged.empresa[0].nome_empresa = nome;
        merged.empresa[0].id = merged.empresa[0].id ?? pv.id;
      }
    }
  } catch {
    /* page-verify opcional */
  }

  if (pickNomeEmpresaFromRow(merged.empresa?.[0] ?? {})) return merged;

  try {
    const customer = await getCustomerByCnpj(clean, true);
    const cliente = Array.isArray(customer)
      ? customer[0]
      : customer?.cliente ??
        (Array.isArray(customer?.data) ? customer.data[0] : customer?.data) ??
        customer;
    if (cliente && typeof cliente === "object") {
      const nome = pickNomeEmpresaFromRow(cliente as Record<string, unknown>);
      if (nome && merged.empresa?.[0]) {
        merged.empresa[0].nome = nome;
        merged.empresa[0].nome_empresa = nome;
        merged.empresa[0].id = merged.empresa[0].id ?? (cliente as Record<string, unknown>).id;
        if ((cliente as Record<string, unknown>).pay_types) {
          merged.empresa[0].pay_types = (cliente as Record<string, unknown>).pay_types;
        }
      }
    }
  } catch {
    /* get-customer pode falhar com jsonb legado no destino */
  }

  return merged;
}

export async function getClientDataForLogin(cnpj: string): Promise<any> {
  try {
    logDebug("🔐 getClientDataForLogin - Iniciando busca para CNPJ:", cnpj);

    const { N8N_WEBHOOK_URL } = await import('../config');
    const webhookUrl = `${N8N_WEBHOOK_URL}auth/check-cnpj`;
    const { N8N_AUTH_HEADER } = await import('../config');
    const clean = String(cnpj || "").replace(/\D/g, "");
    const requestBody = { cnpj: clean };

    let lastError: unknown;
    for (let attempt = 0; attempt < 2; attempt++) {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 20_000);
      try {
        const response = await panelFetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Authorization': N8N_AUTH_HEADER,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        });
        const rawText = await response.text();
        logDebug("📥 getClientDataForLogin - Status:", response.status, "ok:", response.ok, "attempt:", attempt);

        if (!response.ok) {
          if (attempt === 0 && isTransientGatewayFailure(response.status, rawText)) {
            await sleepMs(400);
            continue;
          }
          logError("❌ Erro HTTP:", response.status, rawText);
          throw new Error(`HTTP ${response.status}`);
        }

        if (!rawText.trim()) {
          if (attempt === 0) {
            await sleepMs(400);
            continue;
          }
          throw new Error("empty_check_cnpj");
        }

        let data: any;
        try {
          data = JSON.parse(rawText);
        } catch {
          if (attempt === 0) {
            await sleepMs(400);
            continue;
          }
          logError("❌ Resposta não é JSON:", rawText.slice(0, 400));
          throw new Error("invalid_check_cnpj_json");
        }
        return enrichCheckCnpjProfile(data, clean);
      } catch (error) {
        lastError = error;
        const network = error instanceof TypeError;
        if (attempt === 0 && network) {
          await sleepMs(400);
          continue;
        }
        throw error;
      } finally {
        window.clearTimeout(timeoutId);
      }
    }
    throw lastError instanceof Error ? lastError : new Error("check_cnpj_failed");
  } catch (error) {
    logError('Erro ao validar CNPJ:', error);
    throw error;
  }
}

/**
 * Nome da loja vindo do n8n/Supabase (às vezes só em `empresa[0]` após merge).
 */
function pickNomeEmpresaFromRow(row: Record<string, unknown>): string {
  const s = (v: unknown) =>
    v != null && String(v).trim() ? String(v).trim() : "";
  const direct = s(row.nome_empresa) || s(row.company_name) || s(row.nome_fantasia) || s(row.razao_social);
  if (direct) return direct;
  const em = row.empresa;
  if (Array.isArray(em) && em.length > 0 && em[0] && typeof em[0] === "object") {
    const e0 = em[0] as Record<string, unknown>;
    const nested = s(e0.nome_empresa) || s(e0.nome_fantasia) || s(e0.nome);
    if (nested) return nested;
  }
  if (em && typeof em === "object" && !Array.isArray(em)) {
    const e = em as Record<string, unknown>;
    const one = s(e.nome_empresa) || s(e.nome_fantasia);
    if (one) return one;
  }
  return "";
}

/**
 * Nome da loja para exibir (check-cnpj, get-customer, payload de colaborador, etc.).
 */
export function extractNomeEmpresaFromClientResponse(raw: unknown): string {
  if (raw == null || typeof raw !== "object") return "";
  const r = raw as Record<string, unknown>;
  const direct = pickNomeEmpresaFromRow(r);
  if (direct) return direct;
  /** `nome` no merge de colaborador é o funcionário; não usar como nome da loja. */
  const nome = r.nome;
  if (
    (r.cpf == null || String(r.cpf).replace(/\D/g, "") === "") &&
    typeof nome === "string" &&
    nome.trim()
  ) {
    return nome.trim();
  }
  const cliente = r.cliente;
  if (cliente && typeof cliente === "object") {
    const n = pickNomeEmpresaFromRow(cliente as Record<string, unknown>);
    if (n) return n;
  }
  const d = r.data;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    return extractNomeEmpresaFromClientResponse(d);
  }
  if (Array.isArray(d) && d.length > 0 && typeof d[0] === "object") {
    return extractNomeEmpresaFromClientResponse(d[0]);
  }
  return "";
}

/**
 * Converte respostas do webhook `colaboradores` (get) para o formato esperado pelo AuthContext
 * (`exists` + `empresa` com `senha_definida`), inclusive quando o n8n devolve só o Aggregate ou uma linha.
 */
function normalizeCollaboratorCheckResponse(
  raw: unknown,
  delivaiCnpj: string,
  cpfFilter?: string
): { exists: boolean; empresa: Record<string, unknown> | null } {
  if (raw == null || typeof raw !== "object") {
    return { exists: false, empresa: null };
  }
  const r = raw as Record<string, unknown>;

  if (r.exists === false) {
    return { exists: false, empresa: null };
  }
  if (r.exists === true && r.empresa != null && typeof r.empresa === "object") {
    const em = r.empresa;
    if (Array.isArray(em) && em.length > 0 && typeof em[0] === "object") {
      const first = em[0] as Record<string, unknown>;
      const nomeEmp =
        pickNomeEmpresaFromRow(first) ||
        pickNomeEmpresaFromRow({ empresa: em } as Record<string, unknown>);
      return {
        exists: true,
        empresa: {
          ...first,
          ...(nomeEmp ? { nome_empresa: nomeEmp } : {}),
        },
      };
    }
    if (!Array.isArray(em)) {
      const row = em as Record<string, unknown>;
      const nomeEmp = pickNomeEmpresaFromRow(row);
      return {
        exists: true,
        empresa: nomeEmp ? { ...row, nome_empresa: nomeEmp } : row,
      };
    }
  }

  const cpfDigits = cpfFilter?.replace(/\D/g, "") ?? "";

  let row: Record<string, unknown> | null = null;
  const colaboradores = r.colaboradores;
  if (Array.isArray(colaboradores) && colaboradores.length > 0) {
    const rows = colaboradores.filter((item) => item && typeof item === "object") as Record<
      string,
      unknown
    >[];
    if (cpfDigits) {
      row =
        rows.find(
          (item) => String(item.cpf ?? "").replace(/\D/g, "") === cpfDigits
        ) ?? null;
    } else {
      row = rows[0] ?? null;
    }
  } else if (Array.isArray(r.data) && r.data.length > 0 && typeof r.data[0] === "object") {
    row = r.data[0] as Record<string, unknown>;
  } else if (Array.isArray(r) && r.length > 0 && typeof r[0] === "object") {
    row = r[0] as Record<string, unknown>;
  } else if (r.id != null && (r.cpf != null || r.nome_completo != null || r.nome != null)) {
    row = r;
  }

  if (!row) {
    return { exists: false, empresa: null };
  }

  const senhaDef =
    row.senha_definida === true ||
    row.senha_definida === "true" ||
    row.senha_criada === true ||
    row.senha_criada === "true" ||
    row.password_set === true ||
    row.password_set === "true";

  const nome =
    (typeof row.nome_completo === "string" && row.nome_completo) ||
    (typeof row.nome === "string" && row.nome) ||
    "Colaborador";

  const nomeEmpresaFlat = pickNomeEmpresaFromRow(row);

  const empresa: Record<string, unknown> = {
    ...row,
    nome,
    cnpj:
      (typeof row.cnpj === "string" && row.cnpj.replace(/\D/g, "").length === 14
        ? row.cnpj.replace(/\D/g, "")
        : typeof row.cnpj_loja === "string" && row.cnpj_loja.replace(/\D/g, "").length === 14
          ? row.cnpj_loja.replace(/\D/g, "")
          : delivaiCnpj),
    email_cliente: typeof row.email === "string" ? row.email : "",
    senha_definida: senhaDef,
  };

  if (nomeEmpresaFlat) {
    empresa.nome_empresa = nomeEmpresaFlat;
  }

  return { exists: true, empresa };
}

/** Opções do webhook `colaboradores` com `action: "get"`. */
export type GetCollaboratorDataForLoginOptions = {
  /** CNPJ da loja (14 dígitos) — o body deve incluir `cnpj` para filtrar o vínculo correto. */
  cnpjLoja?: string;
  /**
   * Quando true, filtra o vínculo pelo CNPJ interno DelivAI (`?colaborador=1`).
   */
  internalDelivaiCnpj?: boolean;
};

/**
 * Busca vínculo do colaborador no login via `POST /webhook/colaboradores/resolve-login`
 * (rota pública). Não usa `POST /webhook/colaboradores` action=get — no test com
 * SESSION_AUTH_REQUIRED isso devolve 401 e o login Dev quebra antes da sessão existir.
 */
export async function getCollaboratorDataForLogin(
  cpf: string,
  options?: GetCollaboratorDataForLoginOptions
): Promise<any> {
  try {
    const cleanCpf = String(cpf || "").replace(/\D/g, "");
    const cleanLoja = options?.cnpjLoja?.replace(/\D/g, "") ?? "";
    const useInternal = options?.internalDelivaiCnpj === true;
    const { DELIVAI_INTERNAL_CNPJ } = await import("../config");
    const targetCnpj =
      cleanLoja.length === 14
        ? cleanLoja
        : useInternal
          ? DELIVAI_INTERNAL_CNPJ
          : "";

    logDebug(
      "🔐 getCollaboratorDataForLogin - CPF:",
      cleanCpf,
      targetCnpj.length === 14
        ? useInternal
          ? "CNPJ interno DelivAI"
          : "CNPJ loja"
        : "busca por CPF (legado)"
    );

    const { resolveCollaboratorLogin } = await import("../services/routes");
    const resolved = await resolveCollaboratorLogin(cleanCpf);
    const stores = resolved.stores || [];
    const match =
      targetCnpj.length === 14
        ? stores.find(
            (store) => String(store.cnpj || "").replace(/\D/g, "") === targetCnpj
          )
        : stores.find(
            (store) => store.senha_definida === true || store.password_set === true
          ) || stores[0];

    if (!match) {
      return { exists: false, empresa: null };
    }

    const senhaDef = match.senha_definida === true || match.password_set === true;
    const nome = (match.nome || "Colaborador").trim() || "Colaborador";
    const nomesLojas = stores
      .map((store) => (store.company_name || store.nome_empresa || "").trim())
      .filter(Boolean);
    const nomeEmpresa =
      targetCnpj.length === 14
        ? match.nome_empresa || match.company_name || ""
        : nomesLojas.join(" · ");
    const empresa = {
      nome,
      nome_completo: nome,
      employee_id: match.employee_id,
      id: match.employee_id,
      senha_definida: senhaDef,
      password_set: senhaDef,
      cargo: match.cargo,
      role: match.role,
      cnpj: String(match.cnpj || targetCnpj).replace(/\D/g, ""),
      company_name: nomeEmpresa,
      nome_empresa: nomeEmpresa,
      cpf: cleanCpf,
      agenda_enabled: match.agenda_enabled === true,
      ativo: true,
    };
    logDebug("🔐 Resposta check colaborador (resolve-login):", {
      exists: true,
      cnpj: empresa.cnpj,
    });
    return { exists: true, empresa };
  } catch (error) {
    logError("Erro ao validar CPF (colaborador):", error);
    throw error;
  }
}

/**
 * Perfil do colaborador já autenticado (`POST /webhook/colaboradores` action=get).
 * Sem header legado: no test com SESSION_AUTH_REQUIRED só o cookie vale.
 * 401 aqui não dispara logout (refresh da sessão).
 */
export async function getCollaboratorProfileWithSession(
  cpf: string,
  cnpj: string
): Promise<{ exists: boolean; empresa: Record<string, unknown> | null }> {
  const cleanCpf = String(cpf || "").replace(/\D/g, "");
  const cleanCnpj = String(cnpj || "").replace(/\D/g, "");
  if (cleanCpf.length !== 11 || cleanCnpj.length !== 14) {
    return { exists: false, empresa: null };
  }
  const { N8N_WEBHOOK_URL, N8N_WEBHOOK_COLABORADORES_PATH } = await import("../config");
  const webhookUrl = `${N8N_WEBHOOK_URL}${N8N_WEBHOOK_COLABORADORES_PATH}`;
  const response = await fetch(webhookUrl, {
    method: "POST",
    credentials: PANEL_FETCH_CREDENTIALS,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      action: "get",
      type: "one",
      cpf: cleanCpf,
      cnpj: cleanCnpj,
    }),
  });
  const rawText = await response.text();
  if (!response.ok) {
    return { exists: false, empresa: null };
  }
  let parsed: unknown = {};
  try {
    parsed = rawText ? JSON.parse(rawText) : {};
  } catch {
    return { exists: false, empresa: null };
  }
  return normalizeCollaboratorCheckResponse(parsed, cleanCnpj, cleanCpf);
}

/**
 * Lista colaboradores da empresa via webhook (`action: get`, `type: all`).
 * Contrato n8n recomendado: resposta JSON com array em `colaboradores` (ou `rows` / `data`).
 * Se o fluxo responder uma linha por execução, ajuste o n8n para agregar num único JSON.
 */
export async function fetchCollaboratorsList(cnpj: string): Promise<unknown> {
  const cleanCnpj = String(cnpj || "").replace(/\D/g, "");
  if (cleanCnpj.length !== 14) {
    throw new Error("CNPJ inválido para listar colaboradores.");
  }
  const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER, N8N_WEBHOOK_COLABORADORES_PATH } =
    await import("../config");
  const webhookUrl = `${N8N_WEBHOOK_URL}${N8N_WEBHOOK_COLABORADORES_PATH}`;
  const requestBody = {
    action: "get",
    cnpj: cleanCnpj,
    type: "all",
  };
  logDebug("📤 fetchCollaboratorsList:", webhookUrl, JSON.stringify(requestBody));

  const response = await panelFetch(webhookUrl, {
    method: "POST",
    headers: {
      Authorization: N8N_AUTH_HEADER,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });

  const rawText = await response.text();
  if (!response.ok) {
    logError("❌ Erro HTTP listagem colaboradores:", response.status, rawText);
    throw new Error(
      `HTTP ${response.status}: ${rawText.slice(0, 280) || response.statusText}`
    );
  }
  try {
    return rawText ? JSON.parse(rawText) : {};
  } catch {
    throw new Error("Resposta do webhook não é JSON válido.");
  }
}

/**
 * Atualiza colaborador na tabela (Supabase) via webhook `action: update`.
 * O n8n deve aplicar `colaborador_data` com filtro cpf + cliente_id (consulta CNPJ).
 */
export async function updateCollaboratorViaWebhook(
  cnpj: string,
  cpf: string,
  colaborador_data: Record<string, unknown>,
  employeeId?: string
): Promise<any> {
  const cleanCnpj = String(cnpj || "").replace(/\D/g, "");
  const cleanCpf = String(cpf || "").replace(/\D/g, "");
  const idClean = String(employeeId || "").trim();
  const hasValidId = /^[0-9a-f-]{36}$/i.test(idClean);
  if (cleanCnpj.length !== 14) {
    throw new Error("CNPJ inválido para atualizar colaborador.");
  }
  if (cleanCpf.length !== 11 && !hasValidId) {
    throw new Error("Informe CPF válido ou id do colaborador para atualizar.");
  }
  const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER, N8N_WEBHOOK_COLABORADORES_PATH } =
    await import("../config");
  const webhookUrl = `${N8N_WEBHOOK_URL}${N8N_WEBHOOK_COLABORADORES_PATH}`;
  const requestBody: Record<string, unknown> = {
    action: "update",
    cnpj: cleanCnpj,
    colaborador_data,
  };
  if (cleanCpf.length === 11) {
    requestBody.cpf = cleanCpf;
  }
  if (hasValidId) {
    requestBody.id = idClean;
  }
  logDebug("📤 updateCollaboratorViaWebhook:", webhookUrl, JSON.stringify(requestBody));

  const response = await panelFetch(webhookUrl, {
    method: "POST",
    headers: {
      Authorization: N8N_AUTH_HEADER,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });

  const rawText = await response.text();
  if (!response.ok) {
    logError("❌ Erro HTTP update colaborador:", response.status, rawText);
    throw new Error(
      `HTTP ${response.status}: ${rawText.slice(0, 280) || response.statusText}`
    );
  }
  try {
    return rawText ? JSON.parse(rawText) : {};
  } catch {
    return { success: true, raw: rawText };
  }
}

/** Apaga o cadastro de um colaborador já inativo. O login deixa de existir. */
export async function deleteCollaboratorViaWebhook(
  cnpj: string,
  cpf: string,
  employeeId?: string
): Promise<{ ok?: boolean; deleted?: boolean }> {
  const cleanCnpj = String(cnpj || "").replace(/\D/g, "");
  const cleanCpf = String(cpf || "").replace(/\D/g, "");
  const idClean = String(employeeId || "").trim();
  const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER, N8N_WEBHOOK_COLABORADORES_PATH } =
    await import("../config");
  const webhookUrl = `${N8N_WEBHOOK_URL}${N8N_WEBHOOK_COLABORADORES_PATH}`;
  const requestBody: Record<string, unknown> = {
    action: "delete",
    cnpj: cleanCnpj,
  };
  if (cleanCpf.length === 11) requestBody.cpf = cleanCpf;
  if (/^[0-9a-f-]{36}$/i.test(idClean)) requestBody.id = idClean;
  const response = await panelFetch(webhookUrl, {
    method: "POST",
    headers: {
      Authorization: N8N_AUTH_HEADER,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });
  const rawText = await response.text();
  if (!response.ok) {
    throw new Error("delete_failed");
  }
  const parsed = rawText ? JSON.parse(rawText) : {};
  if (parsed?.deleted === false) {
    throw new Error("delete_failed");
  }
  return parsed;
}

export type RegisterCollaboratorNewOptions = {
  /** Campos da tabela `colaboradores` — enviados em `body.colaborador_data` para o n8n. */
  colaborador_data?: Record<string, unknown>;
};

/**
 * Pré-cadastro: cria o registro do colaborador a partir do JSON da empresa (`action: "new"` no webhook `colaboradores`).
 * O n8n deve validar CPF contra o allowlist e inserir em `colaboradores` antes do `auth/gerate-code`.
 */
export async function registerCollaboratorNew(
  cnpj: string,
  cpf: string,
  options?: RegisterCollaboratorNewOptions
): Promise<any> {
  try {
    const cleanCpf = String(cpf || "").replace(/\D/g, "");
    const cleanCnpj = String(cnpj || "").replace(/\D/g, "");
    logDebug("🔐 registerCollaboratorNew - CNPJ:", cleanCnpj, "CPF:", cleanCpf);

    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER, N8N_WEBHOOK_COLABORADORES_PATH } =
      await import("../config");
    const webhookUrl = `${N8N_WEBHOOK_URL}${N8N_WEBHOOK_COLABORADORES_PATH}`;

    const requestBody: Record<string, unknown> = {
      action: "new",
      cnpj: cleanCnpj,
      cpf: cleanCpf,
    };
    if (options?.colaborador_data && typeof options.colaborador_data === "object") {
      requestBody.colaborador_data = options.colaborador_data;
    }
    logDebug("📤 registerCollaboratorNew - URL:", webhookUrl, JSON.stringify(requestBody));

    const response = await panelFetch(webhookUrl, {
      method: "POST",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    const rawText = await response.text();

    if (!response.ok) {
      logError("❌ Erro HTTP colaborador new:", response.status, rawText);
      throw new Error(
        `HTTP ${response.status}: ${rawText.slice(0, 280) || response.statusText}`
      );
    }

    let parsed: unknown;
    try {
      parsed = rawText ? JSON.parse(rawText) : {};
    } catch {
      logError("❌ Resposta colaborador new não é JSON:", rawText.slice(0, 400));
      throw new Error(
        `Resposta inválida (${response.status}). Esperado JSON do n8n. Início: ${rawText.slice(0, 120)}`
      );
    }

    const normalized = normalizeCollaboratorCheckResponse(parsed, cleanCnpj);
    const p = typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {};
    const inviteTokenRaw =
      (typeof p.invite_token === "string" && p.invite_token.trim()) ||
      (typeof p.inviteToken === "string" && p.inviteToken.trim()) ||
      "";
    const result: Record<string, unknown> = {
      ...p,
      ...normalized,
      ok: p.ok !== false,
      id: p.id ?? p.employee_id,
      invite_token: inviteTokenRaw || undefined,
      invite_expires_at: p.invite_expires_at ?? p.expires_at,
      invite_created_at: p.invite_created_at,
    };
    if (typeof p.mensagem === "string") {
      result.mensagem = p.mensagem;
    }
    logDebug("🔐 Resposta new colaborador (normalizada):", result);
    return result;
  } catch (error) {
    logError("Erro ao registrar colaborador (new):", error);
    throw error;
  }
}

// Função para buscar dados dos módulos da licença via webhook n8n
export async function getLicenseModulesData(cnpj: string): Promise<any> {
  try {
    logDebug("🔐 getLicenseModulesData - Iniciando busca de módulos para CNPJ:", cnpj);
    
    // URL do webhook para verificação de módulos
    const { N8N_WEBHOOK_URL } = await import('../config');
    const webhookUrl = `${N8N_WEBHOOK_URL}page-verify`;
    
    logDebug("🌐 getLicenseModulesData - Fazendo requisição para:", webhookUrl);
    
    // Enviar apenas o CNPJ
    const requestBody = { cnpj };
    logDebug("📤 getLicenseModulesData - JSON enviado:", JSON.stringify(requestBody));
    
    // Usar a mesma autenticação Basic Auth
    const { N8N_AUTH_HEADER } = await import('../config');
    
    const response = await panelFetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    logDebug("📥 getLicenseModulesData - Status da resposta:", response.status);
    logDebug("📥 getLicenseModulesData - Response ok:", response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      logError("❌ getLicenseModulesData - Erro HTTP:", response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    logDebug("🔐 getLicenseModulesData - Resposta do webhook:", data);
    logDebug("🔐 getLicenseModulesData - Estrutura completa da resposta:", JSON.stringify(data, null, 2));
    
    return data;
  } catch (error) {
    logError('Erro ao buscar dados dos módulos:', error);
    throw error;
  }
}

// Função específica para recuperação de senha (não interfere com AuthContext)
export async function getClientDataForRecovery(cnpj: string): Promise<any> {
  try {
    logDebug("🔐 [RECUPERAÇÃO] Enviando CNPJ para validação:", cnpj);
    
    // URL específica do webhook fornecido
    const { N8N_WEBHOOK_URL } = await import('../config');
    const webhookUrl = `${N8N_WEBHOOK_URL}auth/check-cnpj`;
    
    logDebug("🌐 [RECUPERAÇÃO] Fazendo requisição para:", webhookUrl);
    
    const clean = String(cnpj || "").replace(/\D/g, "");
    const requestBody = { cnpj: clean };
    logDebug("📤 [RECUPERAÇÃO] JSON enviado:", JSON.stringify(requestBody));
    
    // Usar a mesma autenticação Basic Auth que funciona no webhook save-cardapio
    const { N8N_AUTH_HEADER } = await import('../config');
    
    const response = await panelFetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    logDebug("📥 [RECUPERAÇÃO] Status da resposta:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      logError("❌ [RECUPERAÇÃO] Erro HTTP:", response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    logDebug("🔐 [RECUPERAÇÃO] Resposta do webhook:", data);
    
    return enrichCheckCnpjProfile(data, clean);
  } catch (error) {
    logError('[RECUPERAÇÃO] Erro ao validar CNPJ:', error);
    throw error;
  }
}

export type GenerateCodeAction = "wpp" | "mail";

export async function generateVerificationCode(
  cnpj: string,
  options?: {
    action?: GenerateCodeAction;
    cpf?: string;
    /** Obrigatório para o Switch no n8n (`auth/gerate-code`): colaborador vs empresa */
    type?: "colaborador" | "empresa";
  }
): Promise<any> {
  try {
    logDebug("📧 Gerando código de verificação para CNPJ:", cnpj, options);
    
    const { N8N_WEBHOOK_URL } = await import('../config');
    const webhookUrl = `${N8N_WEBHOOK_URL}auth/gerate-code`;
    
    logDebug("🌐 Fazendo requisição para:", webhookUrl);
    
    const cleanCnpj = cnpj.replace(/\D/g, "");
    /** Fluxo empresa (só CNPJ): o n8n espera `action` (mail | wpp) no Switch — padrão e-mail. */
    const requestBody: Record<string, string> =
      options?.cpf != null && options?.action != null
        ? {
            cnpj: cleanCnpj,
            cpf: options.cpf.replace(/\D/g, ""),
            action: options.action,
            type: options.type ?? "colaborador",
          }
        : {
            cnpj: cleanCnpj,
            type: options?.type ?? "empresa",
            action: options?.action ?? "mail",
          };
    logDebug("📤 JSON enviado:", JSON.stringify(requestBody));
    
    const { N8N_AUTH_HEADER } = await import('../config');
    
    const response = await panelFetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    logDebug("📥 Status da resposta:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      logError("❌ Erro HTTP:", response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    logDebug("📧 Resposta do webhook de geração de código:", data);
    
    return data;
  } catch (error) {
    logError('Erro ao gerar código de verificação:', error);
    throw error;
  }
}

export async function verifyCode(
  cnpj: string,
  code: string,
  options?: { cpf?: string }
): Promise<any> {
  try {
    logDebug("🔍 Verificando código para CNPJ:", cnpj);
    logDebug("🔍 Código:", code);
    
    const { N8N_WEBHOOK_URL } = await import('../config');
    const webhookUrl = `${N8N_WEBHOOK_URL}auth/verification-code`;
    
    logDebug("🌐 Fazendo requisição para:", webhookUrl);
    
    const cleanCnpj = cnpj.replace(/\D/g, "");
    const requestBody: Record<string, string> = {
      cnpj: cleanCnpj,
      code,
      type: options?.cpf ? "colaborador" : "empresa",
    };
    if (options?.cpf) {
      requestBody.cpf = options.cpf.replace(/\D/g, "");
    }
    logDebug("📤 JSON enviado:", JSON.stringify(requestBody));
    
    const { N8N_AUTH_HEADER } = await import('../config');
    
    const response = await panelFetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    logDebug("📥 Status da resposta:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      logError("❌ Erro HTTP:", response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    logDebug("🔍 Resposta do webhook de verificação:", data);
    
    return data;
  } catch (error) {
    logError('Erro ao verificar código:', error);
    throw error;
  }
}

export async function validateCollaboratorInvite(token: string): Promise<Record<string, unknown>> {
  const { N8N_WEBHOOK_URL, N8N_WEBHOOK_COLABORADORES_PATH } = await import("../config");
  const webhookUrl = `${N8N_WEBHOOK_URL}${N8N_WEBHOOK_COLABORADORES_PATH}/validate-invite`;
  const response = await panelFetch(webhookUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: token.trim() }),
  });
  const rawText = await response.text();
  let parsed: unknown;
  try {
    parsed = rawText ? JSON.parse(rawText) : {};
  } catch {
    throw new Error(rawText.slice(0, 280) || "Resposta inválida");
  }
  if (!response.ok) {
    const p = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    const msg =
      (typeof p.message === "string" && p.message) ||
      (typeof p.error === "string" && p.error) ||
      rawText.slice(0, 280);
    throw new Error(msg || `HTTP ${response.status}`);
  }
  return (parsed && typeof parsed === "object" ? parsed : {}) as Record<string, unknown>;
}

/** Verifica se o CPF pode ser usado no convite (não pertence a outro colaborador da loja). */
export async function checkInviteCpfAvailability(
  token: string,
  cpf: string
): Promise<{ ok: boolean; available: boolean }> {
  const cleanToken = String(token || "").trim();
  const cleanCpf = String(cpf || "").replace(/\D/g, "");
  if (!cleanToken) throw new Error("Convite inválido.");
  if (cleanCpf.length !== 11) throw new Error("Informe um CPF válido.");

  const { N8N_WEBHOOK_URL, N8N_WEBHOOK_COLABORADORES_PATH } = await import("../config");
  const response = await panelFetch(
    `${N8N_WEBHOOK_URL}${N8N_WEBHOOK_COLABORADORES_PATH}/check-invite-cpf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: cleanToken, cpf: cleanCpf }),
    }
  );
  const rawText = await response.text();
  let parsed: unknown;
  try {
    parsed = rawText ? JSON.parse(rawText) : {};
  } catch {
    throw new Error(rawText.slice(0, 280) || "Resposta inválida");
  }
  if (!response.ok) {
    const p = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    throw new Error(
      (typeof p.message === "string" && p.message) ||
        (typeof p.error === "string" && p.error) ||
        rawText.slice(0, 280) ||
        `HTTP ${response.status}`
    );
  }
  return (parsed && typeof parsed === "object" ? parsed : { ok: true }) as {
    ok: boolean;
    available: boolean;
  };
}

export async function resendCollaboratorInvite(
  cnpj: string,
  options: { cpf?: string; id?: string }
): Promise<Record<string, unknown>> {
  const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER, N8N_WEBHOOK_COLABORADORES_PATH } =
    await import("../config");
  const cleanCnpj = String(cnpj || "").replace(/\D/g, "");
  const body: Record<string, string> = { cnpj: cleanCnpj };
  if (options.id) body.id = options.id;
  else if (options.cpf) body.cpf = String(options.cpf).replace(/\D/g, "");
  else throw new Error("Informe cpf ou id do colaborador.");

  const response = await panelFetch(
    `${N8N_WEBHOOK_URL}${N8N_WEBHOOK_COLABORADORES_PATH}/resend-invite`,
    {
      method: "POST",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
  const rawText = await response.text();
  let parsed: unknown;
  try {
    parsed = rawText ? JSON.parse(rawText) : {};
  } catch {
    throw new Error(rawText.slice(0, 280) || "Resposta inválida");
  }
  if (!response.ok) {
    const p = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    throw new Error(
      (typeof p.message === "string" && p.message) ||
        (typeof p.error === "string" && p.error) ||
        rawText.slice(0, 280) ||
        `HTTP ${response.status}`
    );
  }
  return (parsed && typeof parsed === "object" ? parsed : {}) as Record<string, unknown>;
}

export async function createPassword(
  cnpj: string,
  password: string,
  options?: { cpf?: string; token?: string }
): Promise<any> {
  try {
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER, N8N_WEBHOOK_COLABORADORES_PATH } = await import('../config');
    const cleanCnpj = cnpj.replace(/\D/g, "");
    const cleanCpf = options?.cpf ? String(options.cpf).replace(/\D/g, "") : "";
    const hasInviteToken = Boolean(options?.token?.trim());
    const collaboratorFlow = cleanCpf.length > 0 || hasInviteToken;
    const webhookUrl = collaboratorFlow
      ? `${N8N_WEBHOOK_URL}${N8N_WEBHOOK_COLABORADORES_PATH}/auth-password`
      : `${N8N_WEBHOOK_URL}auth/create-password`;
    const requestBody: Record<string, string> = collaboratorFlow
      ? {
          action: "create_password",
          cnpj: cleanCnpj,
          password,
          ...(cleanCpf.length === 11 ? { cpf: cleanCpf } : {}),
          ...(options?.token ? { token: options.token.trim() } : {}),
        }
      : { cnpj: cleanCnpj, password };
    if (!collaboratorFlow && options?.cpf) {
      requestBody.cpf = String(options.cpf).replace(/\D/g, "");
    }
    const response = await panelFetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    const rawText = await response.text();
    let parsed: unknown;
    try {
      parsed = rawText ? JSON.parse(rawText) : {};
    } catch {
      parsed = null;
    }
    if (!response.ok) {
      logError("Erro ao criar senha:", response.status, rawText);
      const p = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
      const nested =
        p?.message && typeof p.message === "object"
          ? (p.message as Record<string, unknown>)
          : null;
      const msg =
        (typeof p?.mensagem === "string" && p.mensagem) ||
        (typeof p?.message === "string" && p.message) ||
        (nested && typeof nested.message === "string" && nested.message) ||
        (typeof p?.error === "string" && p.error) ||
        rawText.slice(0, 400);
      throw new Error(msg || `Erro HTTP ${response.status}`);
    }
    const data = parsed && typeof parsed === "object" ? parsed : {};
    return data;
  } catch (error) {
    logError('Erro ao criar senha:', error);
    throw error;
  }
}

export async function verifyPassword(
  cnpj: string,
  password: string,
  options?: { cpf?: string }
): Promise<any> {
  try {
    const {
      N8N_WEBHOOK_URL,
      N8N_WEBHOOK_COLABORADORES_PATH,
      panelAuthHeaders,
      PANEL_FETCH_CREDENTIALS,
    } = await import('../config');
    const cleanCnpj = cnpj.replace(/\D/g, "");
    const cleanCpf = options?.cpf ? String(options.cpf).replace(/\D/g, "") : "";
    const collaboratorFlow = cleanCpf.length > 0;
    const webhookUrl = collaboratorFlow
      ? `${N8N_WEBHOOK_URL}${N8N_WEBHOOK_COLABORADORES_PATH}/auth-password`
      : `${N8N_WEBHOOK_URL}auth/verify-password`;
    const body: Record<string, string> = collaboratorFlow
      ? {
          action: "verify_password",
          cnpj: cleanCnpj,
          cpf: cleanCpf,
          password,
        }
      : { cnpj: cleanCnpj, password };
    if (!collaboratorFlow && options?.cpf) {
      body.cpf = String(options.cpf).replace(/\D/g, "");
    }
    const response = await panelFetch(webhookUrl, {
      method: 'POST',
      headers: panelAuthHeaders({ 'Content-Type': 'application/json' }),
      credentials: PANEL_FETCH_CREDENTIALS,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      logError("Erro ao verificar senha:", response.status);
      throw new HttpStatusError(response.status, `HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    logError('Erro ao verificar senha:', error);
    throw error;
  }
}

/** Troca de senha com usuário já autenticado (valida senha atual antes de gravar a nova). */
export async function changePasswordWhileLoggedIn(
  cnpj: string,
  currentPassword: string,
  newPassword: string,
  options?: { cpf?: string }
): Promise<void> {
  const verify = await verifyPassword(
    cnpj,
    currentPassword,
    options?.cpf ? { cpf: options.cpf } : undefined
  );
  if (verify?.ok !== true) {
    throw new Error("Senha atual incorreta.");
  }
  const created = await createPassword(
    cnpj,
    newPassword,
    options?.cpf ? { cpf: options.cpf } : undefined
  );
  if (created?.ok === false) {
    const msg =
      (typeof created?.mensagem === "string" && created.mensagem) ||
      (typeof created?.message === "string" && created.message) ||
      "Não foi possível alterar a senha.";
    throw new Error(msg);
  }
}

// Função para buscar dados da licença via webhook
export async function getLicenseData(cnpj: string): Promise<any> {
  try {
    logDebug("📋 Buscando dados da licença para CNPJ:", cnpj);
    
    const { N8N_WEBHOOK_URL } = await import('../config');
    const webhookUrl = `${N8N_WEBHOOK_URL}get-customer`;
    
    logDebug("🌐 Fazendo requisição para:", webhookUrl);
    
    const url = `${webhookUrl}?cnpj=${encodeURIComponent(cnpj)}`;
    
    const { N8N_AUTH_HEADER } = await import('../config');
    
    const response = await panelFetch(url, {
      method: 'GET',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
    });

    logDebug("📥 Status da resposta:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      logError("❌ Erro HTTP:", response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    logDebug("📋 Resposta do webhook get-customer:", data);
    
    // O webhook retorna um array com os dados do cliente
    if (Array.isArray(data) && data.length > 0) {
      const cliente = data[0]; // Pega o primeiro cliente encontrado
      logDebug("👤 Cliente encontrado:", cliente);
      
      // Extrair dados da licença
      logDebug("🔍 Cliente completo recebido:", cliente);
      logDebug("🔍 cliente.dias_restantes:", cliente.dias_restantes);
      logDebug("🔍 typeof cliente.dias_restantes:", typeof cliente.dias_restantes);
      
      const licenseData = {
        dias_restantes: cliente.dias_restantes || 0,
        ultima_renovacao: cliente.ultima_renovacao || cliente.last_renewal_at || null,
        conta_ativa: cliente.ativo !== false && cliente.active !== false,
        mod_atendimento: cliente.mod_atendimento || false,
        mod_motoboy: cliente.mod_motoboy || false,
        mod_gerente: cliente.mod_gerente || false,
        cliente: cliente
      };
      
      logDebug("📋 Dados da licença extraídos:", licenseData);
      logDebug("📋 licenseData.dias_restantes:", licenseData.dias_restantes);
      
      return {
        success: true,
        licenseData: licenseData
      };
    }
    
    // Se não encontrou cliente
    if (Array.isArray(data) && data.length === 0) {
      return {
        success: false,
        error: "Cliente não encontrado",
        code: "NOT_FOUND"
      };
    }
    
    // Se retornou um objeto com success/data
    if (data.success && data.data) {
      const cliente = data.data;
      const licenseData = {
        dias_restantes: cliente.dias_restantes || 0,
        ultima_renovacao: cliente.ultima_renovacao || cliente.last_renewal_at || null,
        conta_ativa: cliente.ativo !== false && cliente.active !== false,
        mod_atendimento: cliente.mod_atendimento || false,
        mod_motoboy: cliente.mod_motoboy || false,
        mod_gerente: cliente.mod_gerente || false,
        cliente: cliente
      };
      
      return {
        success: true,
        licenseData: licenseData
      };
    }
    
    // Fallback para outros formatos
    const cliente = data;
    const licenseData = {
      dias_restantes: cliente.dias_restantes || 0,
      ultima_renovacao: cliente.ultima_renovacao || cliente.last_renewal_at || null,
      conta_ativa: cliente.ativo !== false && cliente.active !== false,
      mod_atendimento: cliente.mod_atendimento || false,
      mod_motoboy: cliente.mod_motoboy || false,
      mod_gerente: cliente.mod_gerente || false,
      cliente: cliente
    };
    
    return {
      success: true,
      licenseData: licenseData
    };
  } catch (error) {
    logError('Erro ao buscar dados da licença:', error);
    throw error;
  }
}

/**
 * Catálogo global de valores exibidos na tela de licença (pay_types.license_catalog no cliente CNPJ interno DelivAI).
 * Qualquer usuário pode buscar apenas para ler preços configurados pela equipe.
 */
export async function fetchPublicLicensePricingCatalog(): Promise<LicenseCatalogState> {
  const digits = DELIVAI_INTERNAL_CNPJ.replace(/\D/g, "");
  try {
    const data = await getCustomerByCnpj(digits, true);
    if (!data?.success || !data?.cliente) return defaultLicenseCatalog;
    const cl = data.cliente as Record<string, unknown>;
    const pt = cl.pay_types;
    let payTypesParsed: unknown = {};
    if (pt !== undefined && pt !== null) {
      try {
        payTypesParsed = typeof pt === "string" ? JSON.parse(pt as string) : pt;
      } catch {
        payTypesParsed = {};
      }
    }
    return mergeLicenseCatalogFromPayTypes(payTypesParsed);
  } catch (e) {
    logWarn("fetchPublicLicensePricingCatalog: usando defaults", e);
    return defaultLicenseCatalog;
  }
}

export const saveDeliveryData = async (cnpj: string, deliveryData: any): Promise<any> => {
  try {
    logDebug('💾 Salvando dados de delivery para CNPJ:', cnpj);
    logDebug('📦 Dados a serem salvos:', deliveryData);
    
    const { N8N_WEBHOOK_URL } = await import('../config');
    const response = await panelFetch(`${N8N_WEBHOOK_URL}delivery-save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        cnpj: cnpj,
        delivery_data: deliveryData
      })
    });

    if (!response.ok) {
      throw new Error(`Erro na requisição: ${response.status}`);
    }

    const result = await response.json();
    logDebug('✅ Dados de delivery salvos com sucesso:', result);
    return result;
    
  } catch (error) {
    logError('❌ Erro ao salvar dados de delivery:', error);
    throw error;
  }
};

// Função para buscar tokens do Google do cliente do banco de dados
export async function getCustomerGoogleToken(cnpj: string): Promise<{ accessToken: string | null; refreshToken: string | null }> {
  try {
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import('../config');
    
    const url = `${N8N_WEBHOOK_URL}get-customer?cnpj=${encodeURIComponent(cnpj)}`;
    
    logDebug("🌐 [GET_CUSTOMER_TOKEN] Enviando requisição para:", url);
    
    const response = await panelFetch(url, {
      method: 'GET',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
    });

    logDebug("📊 [GET_CUSTOMER_TOKEN] Resposta da API:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries())
    });

    if (!response.ok) {
      let errorText = "";
      try {
        errorText = await response.text();
        logError("❌ [GET_CUSTOMER_TOKEN] Erro HTTP:", {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
      } catch (parseError) {
        logError("❌ [GET_CUSTOMER_TOKEN] Erro HTTP (sem detalhes):", response.status, response.statusText);
      }
      return { accessToken: null, refreshToken: null };
    }

    const data = await response.json();
    // O webhook retorna um array com os dados do cliente
    if (Array.isArray(data) && data.length > 0) {
      const cliente = data[0];
      const googleAccessToken = cliente.google_access_token;
      const googleRefreshToken = cliente.google_refresh_token;
      return {
        accessToken: (googleAccessToken && googleAccessToken.trim() !== '') ? googleAccessToken : null,
        refreshToken: (googleRefreshToken && googleRefreshToken.trim() !== '') ? googleRefreshToken : null
      };
    }
    
    return { accessToken: null, refreshToken: null };
  } catch (error) {
    logError('Erro ao buscar tokens do Google do cliente');
    return { accessToken: null, refreshToken: null };
  }
}

// Função para atualizar token do Google do cliente
// Funções de agendamentos removidas - agora tudo é gerenciado pelo Google Calendar

export async function updateCustomerGoogleToken(cnpj: string, googleAccessToken: string, googleRefreshToken?: string): Promise<any> {
  try {
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import('../config');
    const url = `${N8N_WEBHOOK_URL}update-customer`;
    const payload: any = { cnpj, google_access_token: googleAccessToken };
    if (googleRefreshToken) {
      payload.google_refresh_token = googleRefreshToken;
    }
    const response = await panelFetch(url, {
      method: 'POST',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logError("Erro ao atualizar token do Google:", response.status);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const responseText = await response.text();
    
    let data;
    if (responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        logDebug("✅ [UPDATE_CUSTOMER_TOKEN] Resposta parseada:", data);
      } catch (parseError) {
        logError("❌ [UPDATE_CUSTOMER_TOKEN] Erro ao fazer parse da resposta:", {
          error: parseError instanceof Error ? parseError.message : String(parseError),
          responseText
        });
        const errorMessage = parseError instanceof Error ? parseError.message : String(parseError);
        throw new Error(`Erro ao processar resposta do servidor: ${errorMessage}`);
      }
    } else {
      data = { success: true, message: "Token do Google atualizado com sucesso" };
      logDebug("✅ [UPDATE_CUSTOMER_TOKEN] Resposta vazia, usando resposta padrão:", data);
    }
    
    return data;
  } catch (error) {
    logError('Erro ao atualizar token do Google do cliente:', error);
    throw error;
  }
}

// Função para atualizar configuração de impressão automática do cliente
export async function updateCustomerThermalReceiptSize(
  cnpj: string,
  size: "economico" | "grande"
): Promise<any> {
  return updateCustomerRecordViaWebhook(cnpj, {
    tamanho_cupom_impressao: size,
    thermal_receipt_size: size,
  });
}

export async function updateCustomerAutoPrint(cnpj: string, imprimeAuto: boolean): Promise<any> {
  try {
    logDebug("🖨️ [UPDATE_AUTO_PRINT] Atualizando configuração de impressão automática para CNPJ:", cnpj);
    logDebug("🖨️ [UPDATE_AUTO_PRINT] imprime_auto:", imprimeAuto);
    
    // Configuração do n8n webhook
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import('../config');
    
    const url = `${N8N_WEBHOOK_URL}update-customer`;
    
    const payload = { 
      cnpj,
      imprime_auto: imprimeAuto
    };
    
    logDebug("🌐 [UPDATE_AUTO_PRINT] Enviando requisição para:", url);
    logDebug("📤 [UPDATE_AUTO_PRINT] Payload:", payload);
    
    const response = await panelFetch(url, {
      method: 'POST',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    logDebug("📊 [UPDATE_AUTO_PRINT] Resposta da API:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    if (!response.ok) {
      const errorText = await response.text();
      logError("❌ [UPDATE_AUTO_PRINT] Erro HTTP:", {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    // Verificar se a resposta tem conteúdo antes de tentar fazer parse
    const responseText = await response.text();
    logDebug("📋 [UPDATE_AUTO_PRINT] Resposta (texto):", responseText);
    
    let data;
    if (responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        logDebug("✅ [UPDATE_AUTO_PRINT] Resposta parseada:", data);
      } catch (parseError) {
        logError("❌ [UPDATE_AUTO_PRINT] Erro ao fazer parse da resposta:", {
          error: parseError instanceof Error ? parseError.message : String(parseError),
          responseText
        });
        const errorMessage = parseError instanceof Error ? parseError.message : String(parseError);
        throw new Error(`Erro ao processar resposta do servidor: ${errorMessage}`);
      }
    } else {
      data = { success: true, message: "Configuração de impressão automática atualizada com sucesso" };
      logDebug("✅ [UPDATE_AUTO_PRINT] Resposta vazia, usando resposta padrão:", data);
    }
    
    return data;
  } catch (error) {
    logError('Erro ao atualizar configuração de impressão automática do cliente:', error);
    throw error;
  }
}

export async function updateCustomerAutoAcceptDelidesk(
  cnpj: string,
  enabled: boolean
): Promise<any> {
  logDebug("🖨️ [UPDATE_AUTO_ACCEPT_DELIDESK] aceite_auto_delidesk:", enabled);
  return updateCustomerRecordViaWebhook(cnpj, {
    aceite_auto_delidesk: enabled,
  });
}

// Função para atualizar configuração do BOT do cliente
export async function updateCustomerOpenStatus(cnpj: string, open: boolean): Promise<any> {
  try {
    logDebug("🏪 [UPDATE_OPEN_STATUS] Atualizando status de abertura da loja para CNPJ:", cnpj);
    logDebug("🏪 [UPDATE_OPEN_STATUS] open:", open);
    
    // Configuração do n8n webhook
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import('../config');
    
    const url = `${N8N_WEBHOOK_URL}update-customer`;
    
    const payload = { 
      cnpj,
      open: open
    };
    
    logDebug("🌐 [UPDATE_OPEN_STATUS] Enviando requisição para:", url);
    logDebug("📤 [UPDATE_OPEN_STATUS] Payload:", payload);
    
    const response = await panelFetch(url, {
      method: 'POST',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    logDebug("📊 [UPDATE_OPEN_STATUS] Resposta da API:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    if (!response.ok) {
      const errorText = await response.text();
      logError("❌ [UPDATE_OPEN_STATUS] Erro HTTP:", {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    // Verificar se a resposta tem conteúdo antes de tentar fazer parse
    const responseText = await response.text();
    logDebug("📋 [UPDATE_OPEN_STATUS] Resposta (texto):", responseText);
    
    let data;
    if (responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        logDebug("✅ [UPDATE_OPEN_STATUS] Resposta parseada:", data);
      } catch (parseError) {
        logError("❌ [UPDATE_OPEN_STATUS] Erro ao fazer parse da resposta:", {
          error: parseError instanceof Error ? parseError.message : String(parseError),
          responseText
        });
        const errorMessage = parseError instanceof Error ? parseError.message : String(parseError);
        throw new Error(`Erro ao processar resposta do servidor: ${errorMessage}`);
      }
    } else {
      data = { success: true, message: "Status de abertura da loja atualizado com sucesso" };
      logDebug("✅ [UPDATE_OPEN_STATUS] Resposta vazia, usando resposta padrão:", data);
    }
    
    return data;
  } catch (error) {
    logError('Erro ao atualizar status de abertura da loja:', error);
    throw error;
  }
}

export async function updateCustomerBotStatus(cnpj: string, botOn: boolean): Promise<any> {
  try {
    logDebug("🤖 [UPDATE_BOT_STATUS] Atualizando configuração do BOT para CNPJ:", cnpj);
    logDebug("🤖 [UPDATE_BOT_STATUS] BOT_ON:", botOn);
    
    // Configuração do n8n webhook
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import('../config');
    
    const url = `${N8N_WEBHOOK_URL}update-customer`;
    
    const payload = { 
      cnpj,
      BOT_ON: botOn
    };
    
    logDebug("🌐 [UPDATE_BOT_STATUS] Enviando requisição para:", url);
    logDebug("📤 [UPDATE_BOT_STATUS] Payload:", payload);
    
    const response = await panelFetch(url, {
      method: 'POST',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    logDebug("📊 [UPDATE_BOT_STATUS] Resposta da API:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    if (!response.ok) {
      const errorText = await response.text();
      logError("❌ [UPDATE_BOT_STATUS] Erro HTTP:", {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    // Verificar se a resposta tem conteúdo antes de tentar fazer parse
    const responseText = await response.text();
    logDebug("📋 [UPDATE_BOT_STATUS] Resposta (texto):", responseText);
    
    let data;
    if (responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        logDebug("✅ [UPDATE_BOT_STATUS] Resposta parseada:", data);
      } catch (parseError) {
        logError("❌ [UPDATE_BOT_STATUS] Erro ao fazer parse da resposta:", {
          error: parseError instanceof Error ? parseError.message : String(parseError),
          responseText
        });
        const errorMessage = parseError instanceof Error ? parseError.message : String(parseError);
        throw new Error(`Erro ao processar resposta do servidor: ${errorMessage}`);
      }
    } else {
      data = { success: true, message: "Configuração do BOT atualizada com sucesso" };
      logDebug("✅ [UPDATE_BOT_STATUS] Resposta vazia, usando resposta padrão:", data);
    }
    
    return data;
  } catch (error) {
    logError('Erro ao atualizar configuração do BOT do cliente:', error);
    throw error;
  }
}

/** Painel interno: atualiza dias de licença do cliente (webhook update-customer / n8n). */
export async function updateCustomerDiasRestantes(cnpj: string, diasRestantes: number): Promise<any> {
  try {
    logDebug("📅 [UPDATE_DIAS] Atualizando dias_restantes para CNPJ:", cnpj, diasRestantes);
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
    const url = `${N8N_WEBHOOK_URL}update-customer`;
    const payload = { cnpj, dias_restantes: diasRestantes };
    const response = await panelFetch(url, {
      method: "POST",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    const responseText = await response.text();
    if (responseText.trim()) {
      return JSON.parse(responseText);
    }
    return { success: true, message: "Dias de licença atualizados" };
  } catch (error) {
    logError("Erro ao atualizar dias de licença:", error);
    throw error;
  }
}

/** Painel interno: atualiza pay_types (planos/preços exibidos) — JSON como no cliente. */
export async function updateCustomerPayTypes(cnpj: string, payTypes: unknown): Promise<any> {
  try {
    logDebug("💳 [UPDATE_PAY_TYPES] Atualizando pay_types para CNPJ:", cnpj);
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
    const { payTypesForApiUpdate, coercePayTypesObject } = await import("./payTypes");
    const url = `${N8N_WEBHOOK_URL}update-customer`;
    const payload = { cnpj, pay_types: payTypesForApiUpdate(coercePayTypesObject(payTypes)) };
    const response = await panelFetch(url, {
      method: "POST",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    const responseText = await response.text();
    if (responseText.trim()) {
      return JSON.parse(responseText);
    }
    return { success: true, message: "Planos atualizados" };
  } catch (error) {
    logError("Erro ao atualizar pay_types:", error);
    throw error;
  }
}

/** Flags de módulos da licença (sidebar / telas condicionais), alinhadas a `licenseData` no AuthContext. */
export type CustomerLicenseModuleFlags = {
  mod_atendimento: boolean;
  mod_motoboy: boolean;
  mod_gerente: boolean;
  mod_agendamento: boolean;
};

/** Painel interno: liga/desliga módulos do cliente (update-customer / n8n → colunas booleanas). */
export async function updateCustomerLicenseModules(
  cnpj: string,
  modules: CustomerLicenseModuleFlags
): Promise<any> {
  try {
    logDebug("🧩 [UPDATE_LICENSE_MODULES] CNPJ:", cnpj, modules);
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
    const url = `${N8N_WEBHOOK_URL}update-customer`;
    const payload = {
      cnpj,
      mod_atendimento: modules.mod_atendimento,
      mod_motoboy: modules.mod_motoboy,
      mod_gerente: modules.mod_gerente,
      mod_agendamento: modules.mod_agendamento,
    };
    const response = await panelFetch(url, {
      method: "POST",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    const responseText = await response.text();
    if (responseText.trim()) {
      return JSON.parse(responseText);
    }
    return { success: true, message: "Módulos da licença atualizados" };
  } catch (error) {
    logError("Erro ao atualizar módulos da licença:", error);
    throw error;
  }
}

// Função para atualizar cardápio JSON do cliente via webhook update-customer
export async function updateCustomerCardapioJson(cnpj: string, cardapioJson: any): Promise<any> {
  try {
    logDebug("🍽️ [UPDATE_CARDAPIO_JSON] Atualizando cardápio JSON para CNPJ:", cnpj);
    logDebug("🍽️ [UPDATE_CARDAPIO_JSON] Dados do cardápio:", cardapioJson);
    
    // Configuração do n8n webhook
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import('../config');
    
    const url = `${N8N_WEBHOOK_URL}update-customer`;
    
    // Garantir que o JSON está no formato correto
    let formattedCardapioJson;
    if (typeof cardapioJson === 'string') {
      try {
        const parsedJson = JSON.parse(cardapioJson);
        formattedCardapioJson = JSON.stringify(parsedJson, null, 2);
      } catch (e) {
        logDebug("⚠️ Cardápio já está em formato string, mantendo como está");
        formattedCardapioJson = cardapioJson;
      }
    } else {
      // Se é um objeto, formatar com indentação
      formattedCardapioJson = JSON.stringify(cardapioJson, null, 2);
    }
    
    const payload = { 
      cnpj,
      cardapio_json: formattedCardapioJson
    };
    
    logDebug("🌐 [UPDATE_CARDAPIO_JSON] Enviando requisição para:", url);
    logDebug("📤 [UPDATE_CARDAPIO_JSON] Payload:", payload);
    
    const response = await panelFetch(url, {
      method: 'POST',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    logDebug("📊 [UPDATE_CARDAPIO_JSON] Resposta da API:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    if (!response.ok) {
      const errorText = await response.text();
      logError("❌ [UPDATE_CARDAPIO_JSON] Erro HTTP:", {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    // Verificar se a resposta tem conteúdo antes de tentar fazer parse
    const responseText = await response.text();
    logDebug("📋 [UPDATE_CARDAPIO_JSON] Resposta (texto):", responseText);
    
    let data;
    if (responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        logDebug("✅ [UPDATE_CARDAPIO_JSON] Resposta parseada:", data);
      } catch (parseError) {
        logError("❌ [UPDATE_CARDAPIO_JSON] Erro ao fazer parse da resposta:", {
          error: parseError instanceof Error ? parseError.message : String(parseError),
          responseText
        });
        const errorMessage = parseError instanceof Error ? parseError.message : String(parseError);
        throw new Error(`Erro ao processar resposta do servidor: ${errorMessage}`);
      }
    } else {
      data = { success: true, message: "Cardápio JSON atualizado com sucesso" };
      logDebug("✅ [UPDATE_CARDAPIO_JSON] Resposta vazia, usando resposta padrão:", data);
    }
    
    return data;
  } catch (error) {
    logError('Erro ao atualizar cardápio JSON do cliente:', error);
    throw error;
  }
}

function sanitizeClienteFieldsForWebhook(
  fields: Record<string, unknown>,
  options?: { includeCnpj?: boolean }
): Record<string, unknown> {
  const omit = new Set<string>(["id", "created_at"]);
  if (!options?.includeCnpj) omit.add("cnpj");
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(fields)) {
    if (omit.has(k)) continue;
    let val: unknown = v;
    if (k === "cardapio_json" && val !== null && val !== undefined && typeof val === "object") {
      val = JSON.stringify(val, null, 2);
    }
    if ((k === "pay_types" || k === "payment_methods") && val != null) {
      const formatted = normalizePayTypesForCustomerUpdate(val);
      if (!formatted) continue;
      val = formatted;
    }
    out[k] = val;
  }
  return stripEmptyJsonFieldsFromCustomerUpdate(out);
}

/**
 * Atualiza colunas do registro em `clientes` via webhook `update-customer`
 * (POST com `{ cnpj, ...colunas }`, mesmo padrão de `updateCustomerDeliveryData`).
 * `cardapio_json` como objeto é serializado em string JSON.
 */
export async function updateCustomerRecordViaWebhook(
  cnpj: string,
  fields: Record<string, unknown>
): Promise<any> {
  try {
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
    const url = `${N8N_WEBHOOK_URL}update-customer`;
    const payload = { cnpj, ...sanitizeClienteFieldsForWebhook(fields) };
    logDebug("📝 [UPDATE_CUSTOMER_RECORD] URL:", url, "payload keys:", Object.keys(payload));

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45_000);

    const response = await panelFetch(url, {
      method: "POST",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    }).finally(() => clearTimeout(timeoutId));

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const responseText = await response.text();
    if (responseText.trim()) {
      return JSON.parse(responseText);
    }
    return { success: true, message: "Cliente atualizado com sucesso" };
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error(
        "Tempo esgotado ao salvar (45s). Verifique se o backend de teste (VPS) está acessível e o .env.local do front aponta para a URL correta."
      );
    }
    logError("Erro ao atualizar registro do cliente:", error);
    throw error;
  }
}

/**
 * Cria cliente em `clientes` via webhook `update-customer` com `action: "create"`.
 * O fluxo n8n deve ignorar a coluna `action` no Insert (mapear só colunas da tabela).
 */
export async function createCustomerViaWebhook(fields: Record<string, unknown>): Promise<any> {
  try {
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
    const url = `${N8N_WEBHOOK_URL}update-customer`;
    const { id: _id, action: _a, ...rest } = fields;
    const sanitized = sanitizeClienteFieldsForWebhook(rest, { includeCnpj: true });
    const payload = { action: "create", ...sanitized };
    logDebug("➕ [CREATE_CUSTOMER] URL:", url, "payload keys:", Object.keys(payload));

    const response = await panelFetch(url, {
      method: "POST",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const responseText = await response.text();
    if (responseText.trim()) {
      return JSON.parse(responseText);
    }
    return { success: true, message: "Cliente criado com sucesso" };
  } catch (error) {
    logError("Erro ao criar cliente:", error);
    throw error;
  }
}

async function postPromptTemplatesWebhook(
  body: Record<string, unknown>
): Promise<unknown> {
  const url = `${N8N_WEBHOOK_URL}${N8N_WEBHOOK_PROMPT_TEMPLATES_PATH}`;
  logDebug("🤖 [PROMPT_TEMPLATES] POST", url, Object.keys(body));

  const response = await panelFetch(url, {
    method: "POST",
    headers: {
      Authorization: N8N_AUTH_HEADER,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  const responseText = await response.text();
  let data: unknown = null;
  if (responseText.trim()) {
    try {
      data = JSON.parse(responseText);
    } catch {
      throw new Error(
        `Resposta inválida (${response.status}): ${responseText.slice(0, 200)}`
      );
    }
  }

  if (!response.ok) {
    let msg = responseText || response.statusText;
    if (typeof data === "object" && data !== null) {
      const o = data as Record<string, unknown>;
      if (typeof o.error === "string") msg = o.error;
      else if (typeof o.message === "string") msg = o.message;
      const parts: string[] = [];
      if (o.details != null) {
        parts.push(
          typeof o.details === "string" ? o.details : JSON.stringify(o.details)
        );
      }
      if (typeof o.hint === "string") parts.push(o.hint);
      if (parts.length) {
        msg = `${msg || "Erro"} — ${parts.join(" | ")}`;
      }
    }
    throw new Error(msg || `HTTP ${response.status}`);
  }

  return data;
}

/** Lista todos os templates (`action: get`, `type: all`). */
export async function getPromptTemplates(): Promise<PromptTemplateRow[]> {
  const data = (await postPromptTemplatesWebhook({
    action: "get",
    type: "all",
  })) as { success?: boolean; templates?: unknown[] };
  if (data?.success === false) {
    throw new Error("Falha ao listar templates");
  }
  return normalizePromptTemplateList(data?.templates);
}

/** Um template por `id` ou `nicho` (`action: get`, `type: one`). */
export async function getPromptTemplateOne(params: {
  id?: string;
  nicho?: string;
}): Promise<PromptTemplateRow | null> {
  const data = (await postPromptTemplatesWebhook({
    action: "get",
    type: "one",
    ...params,
  })) as { success?: boolean; template?: unknown | null };
  if (data?.success === false) {
    throw new Error("Falha ao buscar template");
  }
  if (data?.template == null) return null;
  return normalizePromptTemplateRow(data.template);
}

export async function updatePromptTemplate(
  id: string,
  fields: PromptTemplateUpdateInput
): Promise<PromptTemplateRow> {
  const data = (await postPromptTemplatesWebhook({
    action: "update",
    id,
    ...fields,
  })) as { success?: boolean; template?: unknown; error?: string };
  if (!data?.success || !data.template) {
    throw new Error(data?.error || "Falha ao atualizar template");
  }
  const normalized = normalizePromptTemplateRow(data.template);
  if (!normalized) {
    throw new Error("Resposta inválida ao atualizar template");
  }
  return normalized;
}

export async function createPromptTemplate(
  row: PromptTemplateCreateInput
): Promise<PromptTemplateRow> {
  const data = (await postPromptTemplatesWebhook({
    action: "create",
    row,
  })) as { success?: boolean; template?: unknown; error?: string };
  if (!data?.success || !data.template) {
    throw new Error(data?.error || "Falha ao criar template");
  }
  const normalized = normalizePromptTemplateRow(data.template);
  if (!normalized) {
    throw new Error("Resposta inválida ao criar template");
  }
  return normalized;
}

export async function deletePromptTemplate(id: string): Promise<void> {
  const data = (await postPromptTemplatesWebhook({
    action: "delete",
    id,
  })) as { success?: boolean; deleted?: boolean; error?: string };
  if (!data?.success) {
    throw new Error(data?.error || "Falha ao excluir template");
  }
}

// Função para atualizar colaboradores do cliente via webhook update-customer
export async function updateCustomerColaboradores(cnpj: string, colaboradores: any): Promise<any> {
  try {
    logDebug("👥 [UPDATE_COLABORADORES] Atualizando colaboradores para CNPJ:", cnpj);
    logDebug("👥 [UPDATE_COLABORADORES] Dados dos colaboradores:", colaboradores);
    
    // Configuração do n8n webhook
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import('../config');
    
    const url = `${N8N_WEBHOOK_URL}update-customer`;
    
    // Garantir que o JSON está no formato correto (string JSON)
    let formattedColaboradores: string;
    if (typeof colaboradores === 'string') {
      try {
        // Se já é string, validar que é JSON válido
        const parsedJson = JSON.parse(colaboradores);
        formattedColaboradores = JSON.stringify(parsedJson, null, 2);
        logDebug("✅ [UPDATE_COLABORADORES] String JSON validada e formatada");
      } catch (e) {
        logDebug("⚠️ [UPDATE_COLABORADORES] String inválida, mantendo como está");
        formattedColaboradores = colaboradores;
      }
    } else {
      // Se é um objeto/array, converter para string JSON
      formattedColaboradores = JSON.stringify(colaboradores, null, 2);
      logDebug("✅ [UPDATE_COLABORADORES] Objeto convertido para string JSON");
    }
    
    logDebug("📋 [UPDATE_COLABORADORES] JSON formatado (primeiros 500 chars):", formattedColaboradores.substring(0, 500));
    
    const payload = { 
      cnpj,
      colaboradores: formattedColaboradores
    };
    
    logDebug("📦 [UPDATE_COLABORADORES] Payload completo:", JSON.stringify(payload, null, 2));
    
    logDebug("🌐 [UPDATE_COLABORADORES] Enviando requisição para:", url);
    logDebug("📤 [UPDATE_COLABORADORES] Payload:", payload);
    
    const response = await panelFetch(url, {
      method: 'POST',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    logDebug("📊 [UPDATE_COLABORADORES] Resposta da API:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    if (!response.ok) {
      const errorText = await response.text();
      logError("❌ [UPDATE_COLABORADORES] Erro HTTP:", {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    // Verificar se a resposta tem conteúdo antes de tentar fazer parse
    const responseText = await response.text();
    logDebug("📋 [UPDATE_COLABORADORES] Resposta (texto):", responseText);
    
    let data;
    if (responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        logDebug("✅ [UPDATE_COLABORADORES] Resposta parseada:", data);
      } catch (parseError) {
        logError("❌ [UPDATE_COLABORADORES] Erro ao fazer parse da resposta:", {
          error: parseError instanceof Error ? parseError.message : String(parseError),
          responseText
        });
        const errorMessage = parseError instanceof Error ? parseError.message : String(parseError);
        throw new Error(`Erro ao processar resposta do servidor: ${errorMessage}`);
      }
    } else {
      data = { success: true, message: "Colaboradores atualizados com sucesso" };
      logDebug("✅ [UPDATE_COLABORADORES] Resposta vazia, usando resposta padrão:", data);
    }
    
    return data;
  } catch (error) {
    logError('Erro ao atualizar colaboradores do cliente:', error);
    throw error;
  }
}

// Função para atualizar dados de delivery do cliente via webhook update-customer
export async function updateCustomerDeliveryData(cnpj: string, deliveryData: {
  bairros_taxas?: any;
  imagem_cardapio?: any[];
  nicho_empresa?: string;
  tempo_espera_padrao?: number | null;
  prazo_atual?: any;
  pay_types?: any;
  pedidos_pendentes?: any[];
  order_types_public?: { aceitas?: ('entrega' | 'retirada' | 'consumo_no_local')[] };
  /** Pode ir no mesmo POST que pay_types (evita 2× update-customer). */
  open?: boolean;
  gift_claim_settings?: Record<string, unknown>;
  appointment_message_templates?: Record<string, unknown>;
  agenda_settings?: Record<string, unknown>;
}): Promise<any> {
  try {
    logDebug("🚚 [UPDATE_DELIVERY_DATA] Atualizando dados de delivery para CNPJ:", cnpj);
    const normalizedDeliveryData = { ...deliveryData } as Record<string, unknown>;
    if (normalizedDeliveryData.pay_types != null) {
      const formatted = normalizePayTypesForCustomerUpdate(normalizedDeliveryData.pay_types);
      if (!formatted) {
        throw new Error('pay_types vazio: alteração ignorada para não apagar dados da loja');
      }
      normalizedDeliveryData.pay_types = formatted;
    }
    if (typeof normalizedDeliveryData.chave_pix === 'string' && !String(normalizedDeliveryData.chave_pix).trim()) {
      delete normalizedDeliveryData.chave_pix;
    }
    if (normalizedDeliveryData.bairros_taxas !== undefined) {
      const { deliveryFeesPayloadFromBairrosTaxas } = await import("./bairrosTaxas");
      const { delivery_fees } = deliveryFeesPayloadFromBairrosTaxas(
        normalizedDeliveryData.bairros_taxas
      );
      (normalizedDeliveryData as Record<string, unknown>).delivery_fees = delivery_fees;
      delete normalizedDeliveryData.bairros_taxas;
    }
    logDebug("🚚 [UPDATE_DELIVERY_DATA] Dados de delivery:", normalizedDeliveryData);
    
    // Configuração do n8n webhook
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import('../config');
    
    const url = `${N8N_WEBHOOK_URL}update-customer`;
    
    const payload = stripEmptyJsonFieldsFromCustomerUpdate({
      cnpj,
      ...normalizedDeliveryData,
    });
    
    logDebug("🌐 [UPDATE_DELIVERY_DATA] Enviando requisição para:", url);
    logDebug("📤 [UPDATE_DELIVERY_DATA] Payload:", payload);

    let lastError: unknown;
    // 3 tentativas: o container do backend pode estar reiniciando (502/HTML do EasyPanel) e
    // perder o fechamento manual da loja seria pior que esperar alguns segundos.
    const maxAttempts = 3;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 45_000);
      try {
        const response = await panelFetch(url, {
          method: 'POST',
          headers: {
            'Authorization': N8N_AUTH_HEADER,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        logDebug("📊 [UPDATE_DELIVERY_DATA] Resposta da API:", {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          attempt,
        });

        if (!response.ok) {
          const errorText = await response.text();
          logError("❌ [UPDATE_DELIVERY_DATA] Erro HTTP:", {
            status: response.status,
            statusText: response.statusText,
            errorText,
          });
          if (attempt < maxAttempts - 1 && isTransientGatewayFailure(response.status, errorText)) {
            await sleepMs(attempt === 0 ? 600 : 2_000);
            continue;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseText = await response.text();
        if (responseText.trim()) {
          try {
            return JSON.parse(responseText);
          } catch (parseError) {
            logError("❌ [UPDATE_DELIVERY_DATA] Resposta 2xx sem JSON; a gravação segue valendo:", {
              error: parseError instanceof Error ? parseError.message : String(parseError),
            });
            return { success: true };
          }
        }
        return { success: true };
      } catch (error) {
        lastError = error;
        if (attempt < maxAttempts - 1 && error instanceof TypeError) {
          await sleepMs(attempt === 0 ? 600 : 2_000);
          continue;
        }
        throw error;
      } finally {
        window.clearTimeout(timeoutId);
      }
    }
    throw lastError instanceof Error ? lastError : new Error("update_delivery_failed");
  } catch (error) {
    logError('Erro ao atualizar dados de delivery do cliente:', error);
    throw error;
  }
}

/** Emite cupons de presente para clientes selecionados (FID-1). */
export async function issueGiftCoupons(
  cnpj: string,
  consumerIds: string[],
  programId?: string
): Promise<{ ok?: boolean; count?: number; issued?: unknown[] }> {
  const { N8N_WEBHOOK_URL: base, N8N_AUTH_HEADER } = await import("../config");
  const url = `${base}gift-coupons/issue`;
  const response = await panelFetch(url, {
    method: "POST",
    headers: {
      Authorization: N8N_AUTH_HEADER,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      control: { cnpj },
      cnpj,
      consumer_ids: consumerIds,
      ...(programId ? { program_id: programId } : {}),
    }),
  });
  const text = await response.text();
  let data: any = {};
  if (text.trim()) {
    try {
      data = JSON.parse(text);
    } catch {
      data = {};
    }
  }
  if (!response.ok) {
    const parsed = parseApiErrorPayload(data, response.status);
    throw new Error(formatApiErrorForUser(parsed) || `HTTP ${response.status}`);
  }
  return data;
}

/** Renova access token Google via proxy do backend (sem client secret no front). */
export async function refreshGoogleAccessToken(
  _refreshToken?: string,
  cnpjOverride?: string
): Promise<{ access_token: string; expires_in: number } | null> {
  try {
    const { getCurrentUser } = await import("../config");
    const { googleProxy } = await import("../services/api/secureProxies");
    const cnpj = (cnpjOverride || getCurrentUser()?.cnpj || "").replace(/\D/g, "");
    if (cnpj.length !== 14) {
      logError("CNPJ inválido para refresh Google");
      return null;
    }
    const employeeId = localStorage.getItem("google_agenda_employee_id") || undefined;
    const result = await googleProxy.refresh(cnpj, employeeId);
    const accessToken = String(result?.access_token || "");
    if (!accessToken) {
      return null;
    }
    return {
      access_token: accessToken,
      expires_in: Number(result?.expires_in || 3600),
    };
  } catch (error: unknown) {
    const code =
      error && typeof error === "object" && "code" in error
        ? String((error as { code?: string }).code || "")
        : "";
    if (code === "google_invalid_grant" || code === "no_refresh_token") {
      localStorage.removeItem("google_access_token");
      localStorage.removeItem("google_refresh_token");
      // Não zera tokens no backend via update-customer — só limpa cache local.
      // invalid_grant já é tratado no proxy (alerta ops); reconectar grava de novo.
    }
    logError("Erro ao renovar token Google via proxy:", error);
    return null;
  }
}

// ============================================
// FUNÇÕES PARA BUSCAR CLIENTE POR NOME
// ============================================

// Função para normalizar nome da empresa para URL (slug)
export function normalizeCompanyName(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^a-z0-9]+/g, '-') // Substitui caracteres especiais por hífen
    .replace(/^-+|-+$/g, ''); // Remove hífens do início e fim
}

// Função para buscar cliente por nome da empresa
// NOTA: Esta função requer um webhook específico get-customer-by-name no n8n
// Por enquanto, vamos criar uma estrutura que funciona, mas idealmente deve haver
// um webhook que busca cliente por nome_empresa normalizado
export async function getCustomerByCompanyName(companyName: string): Promise<{ success: boolean; cliente?: any; cnpj?: string; error?: string }> {
  try {
    logDebug("🔍 [GET_CUSTOMER_BY_NAME] Buscando cliente por nome:", companyName);
    
    // Normalizar o nome para busca (remover hífens e normalizar)
    const normalizedName = companyName
      .replace(/-/g, ' ')
      .toLowerCase()
      .trim();
    
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import('../config');
    
    // Tentar usar um webhook específico para buscar por nome (se existir)
    // Se não existir, retornar erro informando que precisa ser implementado
    const webhookUrl = `${N8N_WEBHOOK_URL}get-customer-by-name`;
    
    try {
      const response = await panelFetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Authorization': N8N_AUTH_HEADER,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ company_name: normalizedName }),
      });

      if (response.ok) {
        const data = await response.json();
        logDebug("✅ [GET_CUSTOMER_BY_NAME] Resposta do webhook:", data);
        
        if (data.success && data.cliente) {
          return {
            success: true,
            cliente: data.cliente,
            cnpj: data.cliente.cnpj || data.cnpj
          };
        } else if (data.success && data.cnpj) {
          // Se retornou apenas o CNPJ, buscar os dados completos do cliente
          const customerResponse = await getCustomerByCnpj(data.cnpj);
          if (customerResponse.success && customerResponse.cliente) {
            return {
              success: true,
              cliente: customerResponse.cliente,
              cnpj: data.cnpj
            };
          }
        }
      } else {
        const errorText = await response.text();
        logDebug("⚠️ [GET_CUSTOMER_BY_NAME] Webhook retornou erro:", response.status, errorText);
      }
    } catch (webhookError) {
      logDebug("⚠️ Webhook get-customer-by-name não disponível, tentando usar nome como CNPJ...");
      
      // Fallback: tentar usar o nome normalizado como se fosse um CNPJ
      // Isso permite que funcione temporariamente até o webhook ser implementado
      // Remove todos os caracteres não numéricos e verifica se tem 14 dígitos
      const cleanName = normalizedName.replace(/\D/g, '');
      
      if (cleanName.length === 14) {
        // Parece ser um CNPJ, tentar buscar diretamente
        logDebug("🔍 [GET_CUSTOMER_BY_NAME] Tentando usar como CNPJ:", cleanName);
        try {
          const customerResponse = await getCustomerByCnpj(cleanName);
          if (customerResponse.success && customerResponse.cliente) {
            return {
              success: true,
              cliente: customerResponse.cliente,
              cnpj: cleanName
            };
          }
        } catch (cnpjError) {
          logDebug("⚠️ [GET_CUSTOMER_BY_NAME] Não funcionou como CNPJ também");
        }
      }
    }
    
    // Se o webhook específico não existir e não funcionou como CNPJ, retornar erro informativo
    return {
      success: false,
      error: "Webhook get-customer-by-name não está disponível. Por favor, implemente este webhook no n8n ou use o CNPJ diretamente na URL (ex: /catalog/12345678000190)."
    };
  } catch (error) {
    logError("❌ [GET_CUSTOMER_BY_NAME] Erro ao buscar cliente:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro desconhecido ao buscar cliente'
    };
  }
}

// ============================================
// CLIENTES DELIVERY (consumidores finais por empresa)
// ============================================

export interface DeliveryClient {
  id: string | number;
  /** UUID da empresa (cadastro principal); alguns webhooks n8n usam para filtrar `clientes_delivery`. */
  cliente_id?: string;
  nome: string;
  telefone: string;
  endereco?: string;
  bairro?: string;
  complemento?: string;
  referencia?: string;
  cep?: string;
  numero?: string;
  /** Quando true, o bot não deve responder mensagens deste cliente (atendimento automático pausado). */
  pausa_atendimento?: boolean;
  /** JID do WhatsApp (ex.: 5511914822755@s.whatsapp.net); usado para extrair telefone quando cadastro está sem número */
  remotejid?: string | null;
  /** Pedidos deste cliente nesta loja (coluna `consumers.store_order_count`). */
  total_pedidos_loja?: number;
  store_order_count?: number;
  /** Veio pelo QR presente (FID-1) — `consumers.acquisition_source = qr_claim`. */
  from_qr_claim?: boolean;
  veio_do_qr?: boolean;
  acquisition_source?: string | null;
  /** Lojas do grupo em que este telefone aparece. */
  stores?: Array<{ cnpj?: string; label?: string; letter?: string }>;
}

export type DeliveryClientsQrStats = {
  qr_issued?: number;
  qr_claimed?: number;
  clients_from_qr?: number;
};

/**
 * Extrai número de telefone do remotejid (WhatsApp).
 * Formato comum: 55 + DDD (11, 12, ...) + número (8 ou 9 dígitos) + @s.whatsapp.net.
 * Ignora remotejid que termina em @lid (não é número confiável).
 * Retorna apenas dígitos (DDD + número, 10 ou 11 dígitos) ou null se não for possível extrair.
 */
export function parsePhoneFromRemoteJid(remotejid: string | null | undefined): string | null {
  if (!remotejid || typeof remotejid !== "string") return null;
  if (remotejid.endsWith("@lid")) return null;
  const digits = remotejid.replace(/\D/g, "");
  if (digits.length < 10) return null;
  // Brasil: 55 + 2 (DDD) + 8 ou 9 dígitos = 12 ou 13
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
    return digits.slice(2);
  }
  // Já está só DDD + número (10 ou 11 dígitos)
  if (digits.length === 10 || digits.length === 11) return digits;
  return null;
}

/** Extrai array de registros do JSON do n8n / API (formatos variados). */
function extractDeliveryClientsList(raw: unknown): any[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "object") return [];
  const o = raw as Record<string, unknown>;
  if (Array.isArray(o.clientes)) return o.clientes as any[];
  if (Array.isArray(o.data)) return o.data as any[];
  if (Array.isArray(o.records)) return o.records as any[];
  if (Array.isArray(o.items)) return o.items as any[];
  if (Array.isArray(o.results)) return o.results as any[];
  if (Array.isArray(o.rows)) return o.rows as any[];
  const nested = o.data;
  if (nested && typeof nested === "object") {
    const n = nested as Record<string, unknown>;
    if (Array.isArray(n.clientes)) return n.clientes as any[];
    if (Array.isArray(n.records)) return n.records as any[];
    if (Array.isArray(n.data)) return n.data as any[];
  }
  const body = o.body;
  if (body && typeof body === "object") {
    return extractDeliveryClientsList(body);
  }
  return [];
}

export type GetDeliveryClientsAction = "one" | "all";

/** Dígitos locais BR (remove DDI 55 quando presente). */
export function normalizePhoneDigitsBr(raw: string): string {
  const d = (raw || "").replace(/\D/g, "");
  if (d.length > 11 && d.startsWith("55")) return d.slice(2);
  return d;
}

/** Mesmo número BR (DDD + linha). Não casa só os 10 finais — DDDs diferentes ficam de fora. */
export function phonesMatchBr(a: string, b: string): boolean {
  const da = normalizePhoneDigitsBr(a);
  const db = normalizePhoneDigitsBr(b);
  if (!da || !db) return false;
  if (da.length < 10 || db.length < 10) return false;
  return da === db;
}

/** Página pública: só registros do telefone digitado (nunca listar outros clientes). */
export function filterDeliveryClientsByExactPhone(
  clients: DeliveryClient[],
  phoneDigits: string
): DeliveryClient[] {
  const needle = normalizePhoneDigitsBr(phoneDigits);
  if (needle.length < 10) return [];
  return clients.filter((c) => phonesMatchBr(c.telefone || "", needle));
}

/**
 * Lista clientes delivery. Webhook n8n: `get-delivery-clients`
 * - `action=all` + opcional `search`: tela interna / filtro por nome ou telefone (parcial).
 * - `action=one` + `telefone` (só dígitos): página pública / autocomplete quando telefone completo (≥10 dígitos).
 */
export async function getDeliveryClients(
  cnpj: string,
  search?: string,
  action: GetDeliveryClientsAction = "all",
  options?: { skipAuth?: boolean; remotejid?: string; scope?: string }
): Promise<{
  success: boolean;
  clientes?: DeliveryClient[];
  qr_stats?: DeliveryClientsQrStats;
  error?: string;
}> {
  try {
    const cleanCnpj = (cnpj || "").replace(/\D/g, "");
    if (!cleanCnpj || cleanCnpj.length !== 14) {
      return { success: false, error: "CNPJ inválido", clientes: [] };
    }
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
    const params = new URLSearchParams();
    params.set("cnpj", cleanCnpj);
    params.set("action", action);
    if (options?.scope) params.set("scope", options.scope);
    if (action === "one") {
      const tel = (search || "").replace(/\D/g, "");
      if (tel) params.set("telefone", tel);
      const remotejid = (options?.remotejid || "").trim();
      if (remotejid) params.set("remotejid", remotejid);
    } else if (search && search.trim()) {
      params.set("search", search.trim());
    }
    const url = `${N8N_WEBHOOK_URL}get-delivery-clients?${params.toString()}`;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (!options?.skipAuth && N8N_AUTH_HEADER) {
      headers.Authorization = N8N_AUTH_HEADER;
    }
    const response = await panelFetch(url, {
      method: "GET",
      headers,
    });
    const rawText = await response.text();
    if (!response.ok) {
      logWarn("[getDeliveryClients] Webhook não disponível ou erro:", response.status, rawText);
      return {
        success: false,
        error: `Não foi possível carregar clientes (HTTP ${response.status}). Verifique o n8n e o webhook get-delivery-clients.`,
        clientes: [],
      };
    }
    let data: unknown = null;
    try {
      data = rawText ? JSON.parse(rawText) : null;
    } catch {
      logWarn("[getDeliveryClients] Resposta não é JSON válido:", rawText.slice(0, 200));
      return {
        success: false,
        error: "Resposta inválida do servidor ao listar clientes.",
        clientes: [],
      };
    }
    if (data == null) return { success: true, clientes: [] };

    if (typeof data === "object" && data !== null && (data as any).success === false) {
      const msg = String((data as any).error || (data as any).message || "Erro ao listar clientes.");
      return { success: false, error: msg, clientes: [] };
    }

    const list = extractDeliveryClientsList(data);
    if (!Array.isArray(list)) {
      logWarn("[getDeliveryClients] Payload sem array de clientes:", data);
      return { success: true, clientes: [] };
    }

    const clientes: DeliveryClient[] = list.map((c: any, idx: number) => {
      let telefone = String(c.telefone ?? c.phone ?? c.celular ?? c.whatsapp ?? "").replace(/\D/g, "");
      if (!telefone && c.remotejid) {
        const p = parsePhoneFromRemoteJid(c.remotejid);
        if (p) telefone = p;
      }
      const idRaw = c.id ?? c.id_cliente;
      const idStr = idRaw !== undefined && idRaw !== null ? String(idRaw).trim() : "";
      const looksUuid =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
          idStr
        );
      const id = looksUuid
        ? idStr
        : telefone
          ? `tel-${telefone}`
          : `idx-${idx}`;
      const clienteIdStr =
        c.cliente_id != null && String(c.cliente_id).trim() !== ""
          ? String(c.cliente_id).trim()
          : undefined;
      const pausa =
        c.pausa_atendimento === true ||
        c.pausa_atendimento === "true" ||
        c.pausa_atendimento === 1
          ? true
          : c.pausa_atendimento === false ||
              c.pausa_atendimento === "false" ||
              c.pausa_atendimento === 0
            ? false
            : undefined;
      const fromQr =
        c.from_qr_claim === true ||
        c.veio_do_qr === true ||
        c.acquisition_source === "qr_claim";
      return {
        id,
        cliente_id: clienteIdStr,
        nome: (c.nome ?? c.nome_cliente ?? c.name ?? "").toString().trim(),
        telefone,
        endereco: c.endereco ?? c.endereco_entrega,
        bairro: c.bairro,
        complemento: c.complemento,
        referencia: c.referencia,
        cep: c.cep,
        numero: c.numero,
        pausa_atendimento: pausa,
        remotejid: c.remotejid ?? null,
        total_pedidos_loja: Number(c.total_pedidos_loja ?? c.store_order_count ?? c.qtd_pedidos_loja) || 0,
        store_order_count: Number(c.total_pedidos_loja ?? c.store_order_count ?? c.qtd_pedidos_loja) || 0,
        from_qr_claim: fromQr,
        veio_do_qr: fromQr,
        acquisition_source: c.acquisition_source ?? (fromQr ? "qr_claim" : null),
      };
    });

    const qrStatsRaw =
      data && typeof data === "object" ? (data as { qr_stats?: DeliveryClientsQrStats }).qr_stats : undefined;

    if (action === "one") {
      const tel = (search || "").replace(/\D/g, "");
      const filtered = filterDeliveryClientsByExactPhone(clientes, tel);
      if (clientes.length > filtered.length) {
        logWarn(
          `[getDeliveryClients] action=one: API retornou ${clientes.length} registros; ` +
            `filtrados para ${filtered.length} pelo telefone (privacidade).`
        );
      }
      return { success: true, clientes: filtered };
    }

    return { success: true, clientes, qr_stats: qrStatsRaw };
  } catch (e) {
    logWarn("[getDeliveryClients] Erro:", e);
    return {
      success: false,
      error: e instanceof Error ? e.message : "Erro ao carregar clientes.",
      clientes: [],
    };
  }
}

/**
 * Busca clientes delivery cadastrados no MESMO endereço (logradouro + número).
 * Usado no Fast iFood, onde o telefone é 0800 e não identifica o cliente.
 * Webhook: `get-delivery-clients?action=by-address`.
 */
export async function findDeliveryClientsByAddress(
  cnpj: string,
  params: { endereco: string; numero?: string; bairro?: string }
): Promise<{ success: boolean; clientes: DeliveryClient[]; error?: string }> {
  try {
    const cleanCnpj = (cnpj || "").replace(/\D/g, "");
    if (!cleanCnpj || cleanCnpj.length !== 14) {
      return { success: false, error: "CNPJ inválido", clientes: [] };
    }
    if (!params.endereco?.trim()) return { success: true, clientes: [] };

    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
    const qs = new URLSearchParams();
    qs.set("cnpj", cleanCnpj);
    qs.set("action", "by-address");
    qs.set("endereco", params.endereco.trim());
    if (params.numero?.trim()) qs.set("numero", params.numero.trim());
    if (params.bairro?.trim()) qs.set("bairro", params.bairro.trim());

    const url = `${N8N_WEBHOOK_URL}get-delivery-clients?${qs.toString()}`;
    const response = await panelFetch(url, {
      method: "GET",
      headers: { Authorization: N8N_AUTH_HEADER, "Content-Type": "application/json" },
    });
    const rawText = await response.text();
    if (!response.ok) {
      logWarn("[findDeliveryClientsByAddress] HTTP erro:", response.status, rawText);
      return { success: false, error: `HTTP ${response.status}`, clientes: [] };
    }
    let data: unknown = null;
    try {
      data = rawText ? JSON.parse(rawText) : null;
    } catch {
      return { success: false, error: "Resposta inválida", clientes: [] };
    }
    const list = extractDeliveryClientsList(data);
    if (!Array.isArray(list)) return { success: true, clientes: [] };

    const clientes: DeliveryClient[] = list.map((c: any, idx: number) => {
      let telefone = String(c.telefone ?? c.phone ?? "").replace(/\D/g, "");
      if (!telefone && c.remotejid) {
        const p = parsePhoneFromRemoteJid(c.remotejid);
        if (p) telefone = p;
      }
      const idRaw = c.id ?? c.cliente_id;
      const id =
        idRaw != null && String(idRaw).trim() !== "" ? idRaw : telefone ? `tel-${telefone}` : `idx-${idx}`;
      return {
        id,
        cliente_id:
          c.cliente_id != null && String(c.cliente_id).trim() !== "" ? String(c.cliente_id).trim() : undefined,
        nome: (c.nome ?? c.nome_cliente ?? c.name ?? "").toString().trim(),
        telefone,
        endereco: c.endereco ?? c.endereco_entrega,
        bairro: c.bairro,
        complemento: c.complemento,
        referencia: c.referencia,
        cep: c.cep,
        numero: c.numero,
        remotejid: c.remotejid ?? null,
        total_pedidos_loja: Number(c.total_pedidos_loja ?? c.store_order_count ?? c.qtd_pedidos_loja) || 0,
        store_order_count: Number(c.total_pedidos_loja ?? c.store_order_count ?? c.qtd_pedidos_loja) || 0,
      };
    });
    return { success: true, clientes };
  } catch (e) {
    logWarn("[findDeliveryClientsByAddress] Erro:", e);
    return { success: false, error: e instanceof Error ? e.message : "Erro", clientes: [] };
  }
}

export type DeliveryClientMutationAction = "add" | "update" | "remove";

/**
 * Cria ou atualiza cliente delivery. Webhook: save-delivery-client (POST) com `action`: add | update`.
 * - **add:** `{ action: "add", cnpj, cliente_id?, data: { nome, telefone, endereco… } }`.
 * - **update:** `{ action: "update", cnpj, telefone, id, cliente_id?, data: { … } }` — `telefone`/`id` no topo para filtros no n8n; conteúdo do registro em **`data`** (`Prepare Update Data`).
 *
 * No n8n, o nó **Prepare Insert Data** deve mapear o nome para a coluna **`nome`** (não `name`); caso contrário o Supabase autoMap falha ou ignora o campo.
 */
export async function createOrUpdateDeliveryClient(
  cnpj: string,
  data: {
    id?: string | number;
    cliente_id?: string | number;
    nome: string;
    telefone: string;
    endereco?: string;
    bairro?: string;
    complemento?: string;
    referencia?: string;
    cep?: string;
    numero?: string;
  }
): Promise<{ success: boolean; id?: string | number; cliente?: DeliveryClient; error?: string }> {
  try {
    const cleanCnpj = (cnpj || "").replace(/\D/g, "");
    if (!cleanCnpj || cleanCnpj.length !== 14) {
      return { success: false, error: "CNPJ inválido" };
    }
    // Só UUID real do Postgres; placeholders `tel-`/`idx-` ou id inválido → cria/upsert por telefone.
    const idStr =
      data.id !== undefined && data.id !== null ? String(data.id).trim() : "";
    const hasId =
      !!idStr &&
      !idStr.startsWith("tel-") &&
      !idStr.startsWith("idx-") &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        idStr
      );
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
    const url = `${N8N_WEBHOOK_URL}save-delivery-client`;
    const cleanTel = (data.telefone || "").replace(/\D/g, "");
    const nome = (data.nome || "").trim();

    const dataPayload: Record<string, unknown> = {
      nome,
      telefone: cleanTel,
    };
    if (data.endereco !== undefined) {
      dataPayload.endereco = data.endereco?.trim() || "";
    }
    if (data.bairro !== undefined) {
      dataPayload.bairro = data.bairro?.trim() || "";
    }
    if (data.complemento !== undefined) {
      dataPayload.complemento = data.complemento?.trim() || "";
    }
    if (data.referencia !== undefined) {
      dataPayload.referencia = data.referencia?.trim() || "";
    }
    if (data.cep !== undefined) {
      dataPayload.cep = data.cep?.replace(/\D/g, "") || "";
    }
    if (data.numero !== undefined) {
      dataPayload.numero = data.numero?.trim() || "";
    }

    let body: Record<string, unknown>;

    if (hasId) {
      body = {
        action: "update",
        cnpj: cleanCnpj,
        telefone: cleanTel,
        id: data.id,
        data: dataPayload,
      };
      if (
        data.cliente_id !== undefined &&
        data.cliente_id !== null &&
        String(data.cliente_id).trim() !== ""
      ) {
        body.cliente_id = data.cliente_id;
      }
    } else {
      body = {
        action: "add",
        cnpj: cleanCnpj,
        data: dataPayload,
      };
      if (
        data.cliente_id !== undefined &&
        data.cliente_id !== null &&
        String(data.cliente_id).trim() !== ""
      ) {
        body.cliente_id = data.cliente_id;
      }
    }
    const response = await panelFetch(url, {
      method: "POST",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    const rawText = await response.text();
    let result: Record<string, unknown> = {};
    try {
      result = rawText ? (JSON.parse(rawText) as Record<string, unknown>) : {};
    } catch {
      result = {};
    }
    if (!response.ok) {
      logWarn("[createOrUpdateDeliveryClient] Webhook erro:", response.status, rawText);
      return {
        success: false,
        error:
          (typeof result.error === "string" && result.error) ||
          `Serviço indisponível (HTTP ${response.status})`,
      };
    }
    if (result.success === false) {
      return {
        success: false,
        error: (typeof result.error === "string" && result.error) || "Erro ao salvar cliente.",
      };
    }
    const id = (result.id ?? (result.cliente as any)?.id) as string | number | undefined;
    const cliente = (result.cliente as DeliveryClient) ?? (id ? ({ id, ...body } as any) : undefined);
    return { success: true, id, cliente };
  } catch (e) {
    logWarn("[createOrUpdateDeliveryClient] Erro:", e);
    return { success: false, error: e instanceof Error ? e.message : "Erro ao salvar cliente" };
  }
}

/**
 * Atualiza campos de um cliente delivery sem o payload plano de `createOrUpdateDeliveryClient`.
 * Webhook: `save-delivery-client` (POST).
 *
 * Corpo enviado: `{ cnpj, action: "update", telefone, id?, cliente_id?, data: { ...campos } }`.
 * Muitos fluxos n8n exigem `id` (PK) ou `cliente_id` no corpo para rotear o UPDATE — sem isso o webhook pode responder 400.
 */
export async function updateDeliveryClientFields(
  cnpj: string,
  telefoneDigits: string,
  data: Record<string, unknown>,
  record?: { id?: string | number | null; cliente_id?: string | number | null }
): Promise<{ success: boolean; error?: string; id?: string | number }> {
  try {
    const cleanCnpj = (cnpj || "").replace(/\D/g, "");
    if (!cleanCnpj || cleanCnpj.length !== 14) {
      return { success: false, error: "CNPJ inválido" };
    }
    const tel = (telefoneDigits || "").replace(/\D/g, "");
    if (!tel || tel.length < 10) {
      return { success: false, error: "Telefone inválido" };
    }
    if (!data || typeof data !== "object" || Object.keys(data).length === 0) {
      return { success: false, error: "Nenhum campo para atualizar" };
    }
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
    const url = `${N8N_WEBHOOK_URL}save-delivery-client`;
    const rid = record?.id;
    const hasRealId =
      rid !== undefined &&
      rid !== null &&
      String(rid).trim() !== "" &&
      !String(rid).startsWith("tel-") &&
      !String(rid).startsWith("idx-") &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        String(rid).trim()
      );
    const body: Record<string, unknown> = {
      cnpj: cleanCnpj,
      action: hasRealId ? ("update" as const) : ("add" as const),
      telefone: tel,
      data,
    };
    if (hasRealId) body.id = rid;
    const cid = record?.cliente_id;
    if (cid !== undefined && cid !== null && String(cid).trim() !== "") {
      body.cliente_id = cid;
    }
    const response = await panelFetch(url, {
      method: "POST",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    const rawText = await response.text();
    let result: Record<string, unknown> = {};
    try {
      result = rawText ? (JSON.parse(rawText) as Record<string, unknown>) : {};
    } catch {
      result = {};
    }
    if (!response.ok || result.success === false || result.ok === false) {
      logWarn("[updateDeliveryClientFields] Webhook erro:", response.status, rawText);
      return {
        success: false,
        error:
          (typeof result.message === "string" && result.message) ||
          (typeof result.error === "string" && result.error) ||
          `Serviço indisponível (HTTP ${response.status})`,
      };
    }
    return { success: true, id: (result.id as string | number | undefined) };
  } catch (e) {
    logWarn("[updateDeliveryClientFields] Erro:", e);
    return { success: false, error: e instanceof Error ? e.message : "Erro ao atualizar cliente" };
  }
}

// ============================================
// FUNÇÕES PARA PEDIDOS PÚBLICOS
// ============================================

// Interface para Pedido
export interface Order {
  id: number;
  cliente: number;
  status: string;
  total?: number;
  endereco_entrega?: string;
  telefone_cliente?: string;
  nome_cliente?: string;
  observacoes?: string;
  created_at?: string;
  updated_at?: string;
  tipo_pedido?: 'local' | 'delivery'; // Tipo do pedido: local ou delivery
  entregador?: string; // Nome do motoboy/entregador
  desconto_ifood?: number; // Desconto iFood
  desc_ifood?: number; // Desconto iFood (alternativa)
  origem_pedido?: string | null; // Origem do pedido (interno/ifood)
  numero_localizador?: string; // Número localizador do iFood
  loc_ifood?: string; // Localizador iFood (alternativa)
  bairro?: string; // Bairro de entrega
  complemento?: string; // Complemento do endereço
  referencia?: string; // Referência do endereço
  taxa_entrega?: number; // Taxa de entrega
  total_produtos?: number; // Total dos produtos
  total_pedido?: number; // Total do pedido
  pagamento?: string; // Forma de pagamento
  pago?: boolean; // Status de pagamento (true/false)
  tempo_espera?: number; // Tempo de espera em minutos
  [key: string]: any; // Para campos adicionais
}

// Interface para Caixa (alinhada a public.caixas no Supabase)
export interface Caixa {
  id: string;
  created_at: string;
  data_abertura: string;
  data_fechamento: string;
  valor_abertura: number;
  valor_fechamento: number;
  status: 'aberto' | 'fechado' | 'open' | 'closed';
  cliente_id: string;
  controle_dia: number;
}

export type ManageCaixaControl = {
  cnpj: string;
  action: 'create' | 'update' | 'get';
  id?: string;
};

async function manageCaixaRequest(
  control: ManageCaixaControl,
  caixaData?: Record<string, unknown>
): Promise<Response> {
  const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER, N8N_WEBHOOK_MANAGE_CAIXA_PATH } = await import('../config');
  const url = `${N8N_WEBHOOK_URL}${N8N_WEBHOOK_MANAGE_CAIXA_PATH}`;
  return fetch(url, {
    method: 'POST',
    headers: {
      Authorization: N8N_AUTH_HEADER,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ control, caixaData: caixaData ?? {} }),
  });
}

/** Lista caixas do cliente (hook filtra por CNPJ → cliente_id). */
export async function manageCaixaList(
  cnpj: string
): Promise<{ ok: true; caixas: Caixa[] } | { ok: false; error: string }> {
  try {
    const clean = cnpj.replace(/\D/g, '');
    if (clean.length !== 14) {
      return { ok: false, error: 'CNPJ inválido' };
    }
    const res = await manageCaixaRequest({ cnpj: clean, action: 'get' }, {});
    const text = await res.text();
    let data: Record<string, unknown> = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      return { ok: false, error: text || 'Resposta inválida do servidor' };
    }
    if (!res.ok) {
      return {
        ok: false,
        error: String(data.error || `Erro ${res.status}`),
      };
    }
    const raw = data.caixas;
    const caixas = Array.isArray(raw) ? (raw as Caixa[]) : [];
    return { ok: true, caixas };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Erro ao listar caixas',
    };
  }
}

export async function manageCaixaCreate(
  cnpj: string,
  caixaData: Record<string, unknown>
): Promise<{ ok: true; id: string; caixa: Caixa } | { ok: false; error: string }> {
  try {
    const clean = cnpj.replace(/\D/g, '');
    if (clean.length !== 14) {
      return { ok: false, error: 'CNPJ inválido' };
    }
    const res = await manageCaixaRequest({ cnpj: clean, action: 'create' }, caixaData);
    const text = await res.text();
    let data: Record<string, unknown> = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      return { ok: false, error: text || 'Resposta inválida' };
    }
    if (!res.ok || data.success === false) {
      return {
        ok: false,
        error: String(data.error || `Erro ${res.status}`),
      };
    }
    const id = data.id != null ? String(data.id) : '';
    const caixa = (data.caixa as Caixa) || (data as unknown as Caixa);
    if (!id && caixa?.id) {
      return { ok: true, id: String(caixa.id), caixa };
    }
    if (!id) {
      return { ok: false, error: 'Resposta sem id do caixa' };
    }
    return { ok: true, id, caixa };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Erro ao criar caixa',
    };
  }
}

export async function manageCaixaUpdate(
  cnpj: string,
  caixaId: string,
  caixaData: Record<string, unknown>
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const clean = cnpj.replace(/\D/g, '');
    if (clean.length !== 14) {
      return { ok: false, error: 'CNPJ inválido' };
    }
    const res = await manageCaixaRequest(
      { cnpj: clean, action: 'update', id: caixaId },
      caixaData
    );
    const text = await res.text();
    let data: Record<string, unknown> = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      return { ok: false, error: text || 'Resposta inválida' };
    }
    if (!res.ok || data.success === false) {
      return {
        ok: false,
        error: String(data.error || `Erro ${res.status}`),
      };
    }
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Erro ao atualizar caixa',
    };
  }
}

/**
 * Fecha caixas abertos vencidos, abre novo se necessário; retorna id do caixa ativo e próximo controle_dia do pedido.
 */
export async function ensureCaixaForNewOrder(
  cnpj: string,
  horarioRaw: unknown,
  options?: { /** Instante do pedido (ex.: created_at de pendência); default = agora SP. */
    resolveInstant?: Date }
): Promise<
  | { ok: true; caixaId: string; nextControleDia: number }
  | { ok: false; error: string }
> {
  const clean = cnpj.replace(/\D/g, '');
  if (clean.length !== 14) {
    return { ok: false, error: 'CNPJ inválido' };
  }
  const horarios = parseHorarioFuncionamentoRaw(horarioRaw);
  const agoraNow = agoraEmSaoPaulo();
  const agoraPedido =
    options?.resolveInstant != null
      ? new Date(
          options.resolveInstant.toLocaleString("en-US", {
            timeZone: "America/Sao_Paulo",
          })
        )
      : agoraNow;

  const closeStale = async () => {
    const list = await manageCaixaList(clean);
    if (!list.ok) {
      return { ok: false as const, error: list.error };
    }
    const abertos = list.caixas.filter((c) => {
      const s = String(c.status || "").toLowerCase();
      return s === "aberto" || s === "open";
    });
    for (const c of abertos) {
      if (caixaAbertoEstaVencido(c, agoraNow)) {
        const upd = await manageCaixaUpdate(clean, c.id, payloadFecharCaixa(c, agoraNow));
        if (!upd.ok) {
          return { ok: false as const, error: upd.error };
        }
      }
    }
    return { ok: true as const };
  };

  let closed = await closeStale();
  if (!closed.ok) {
    return { ok: false, error: closed.error };
  }

  let list = await manageCaixaList(clean);
  if (!list.ok) {
    return { ok: false, error: list.error };
  }

  const janelaPedido = getJanelaParaNovoCaixa(horarios, agoraPedido);
  const porJanela = list.caixas.filter((c) => caixaPertenceAJanela(c, janelaPedido));
  if (porJanela.length > 0) {
    porJanela.sort(
      (a, b) =>
        new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );
    const c = porJanela[0];
    const next = Number(c.controle_dia ?? 0) + 1;
    return { ok: true, caixaId: c.id, nextControleDia: next };
  }

  const payload = payloadNovoCaixaAberto(janelaPedido, '');
  delete (payload as { cliente_id?: string }).cliente_id;
  const created = await manageCaixaCreate(clean, payload);
  if (!created.ok) {
    return { ok: false, error: created.error };
  }
  return { ok: true, caixaId: created.id, nextControleDia: 1 };
}

/** Respostas HTTP de “sem caixa aberto” viram success + lista vazia (sem erro ao usuário). */
function isBenignNoCaixaHttpError(status: number, body: Record<string, unknown> | null): boolean {
  if (status !== 404) return false;
  const code = body && typeof body.code === 'string' ? body.code : '';
  const errRaw = body && body.error != null ? String(body.error) : '';
  const err = errRaw.toLowerCase();
  if (code === 'NO_OPEN_CAIXA' || code === 'NO_CAIXA' || code === 'CAIXA_CLOSED') return true;
  if (code === 'NOT_FOUND' && err.includes('caixa')) return true;
  if (!code && err.includes('caixa')) return true;
  return false;
}

/** Evita exibir HTML de proxy/gateway (502 Easypanel) na UI. */
function userFacingHttpError(status: number, rawText: string): string {
  const trimmed = rawText.trim();
  const looksLikeHtml =
    trimmed.startsWith('<!DOCTYPE') ||
    trimmed.startsWith('<html') ||
    trimmed.includes('easypanel.io') ||
    trimmed.includes('Service is not reachable');
  if (looksLikeHtml || status === 502 || status === 503 || status === 504) {
    return `Servidor temporariamente indisponível (HTTP ${status}). Tente novamente.`;
  }
  if (trimmed.length > 280) {
    return `Erro ao buscar pedidos (HTTP ${status}). Tente novamente.`;
  }
  return trimmed || `Erro ao buscar pedidos: ${status}`;
}

// Função para buscar pedidos de um cliente
// Webhook esperado: https://delival-n8n.rwysej.easypanel.host/webhook/get-orders
// Parâmetros: cnpj (como query parameter no GET)
// Retorna: { success: boolean, pedidos?: Order[], caixas?: Caixa[]; noOpenCaixa?: true quando não há caixa aberto (lista vazia, sem falha) }
// Caixas também podem ser obtidos via manageCaixaList (webhook manage-caixa, action get) quando precisar da lista atualizada fora do get-orders.
export async function getPublicOrders(
  cnpj: string,
  dataFiltro?: string,
  /** Com telefone: backend retorna só pedidos deste titular (LGPD). */
  viewerPhone?: string
): Promise<{ success: boolean; pedidos?: Order[]; caixas?: Caixa[]; error?: string; noOpenCaixa?: boolean }> {
  try {
    // Limpar CNPJ (remover formatação, deixar apenas números)
    const cleanCnpj = cnpj ? cnpj.replace(/\D/g, '') : '';
    
    logDebug("📦 [GET_PUBLIC_ORDERS] Buscando pedidos para CNPJ:", cleanCnpj);
    logDebug("📦 [GET_PUBLIC_ORDERS] CNPJ original:", cnpj);
    logDebug("📦 [GET_PUBLIC_ORDERS] dataFiltro recebido:", dataFiltro);
    logDebug("📦 [GET_PUBLIC_ORDERS] dataFiltro type:", typeof dataFiltro);
    logDebug("📦 [GET_PUBLIC_ORDERS] dataFiltro truthy?", !!dataFiltro);
    
    if (!cleanCnpj || cleanCnpj.trim() === '' || cleanCnpj.length !== 14) {
      return {
        success: false,
        error: "CNPJ é obrigatório e deve ter 14 dígitos"
      };
    }
    
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import('../config');
    
    // Construir URL com parâmetros
    let url = `${N8N_WEBHOOK_URL}get-orders?cnpj=${encodeURIComponent(cleanCnpj)}`;
    
    // Se uma data foi fornecida, adicionar como parâmetro
    if (dataFiltro) {
      url += `&data=${encodeURIComponent(dataFiltro)}`;
      logDebug("📅 [GET_PUBLIC_ORDERS] Filtrando por data:", dataFiltro);
    }
    const phoneDigits = (viewerPhone || "").replace(/\D/g, "");
    if (phoneDigits.length >= 10) {
      url += `&telefone=${encodeURIComponent(phoneDigits)}`;
      logDebug("🔒 [GET_PUBLIC_ORDERS] Filtrando por telefone do titular (LGPD)");
    }
    
    logDebug("🌐 [GET_PUBLIC_ORDERS] Fazendo requisição GET para:", url);
    logDebug("📤 [GET_PUBLIC_ORDERS] Headers:", {
      'Authorization': N8N_AUTH_HEADER ? 'Presente' : 'Ausente',
      'Content-Type': 'application/json'
    });

    const GET_ORDERS_TIMEOUT_MS = 25_000;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), GET_ORDERS_TIMEOUT_MS);

    let response: Response;
    try {
      response = await panelFetch(url, {
        method: 'GET',
        headers: {
          Authorization: N8N_AUTH_HEADER,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });
    } catch (fetchError) {
      if (fetchError instanceof DOMException && fetchError.name === 'AbortError') {
        return {
          success: false,
          error: `Tempo esgotado ao buscar pedidos (${GET_ORDERS_TIMEOUT_MS / 1000}s). Tente novamente.`,
        };
      }
      throw fetchError;
    } finally {
      window.clearTimeout(timeoutId);
    }

    logDebug("📥 [GET_PUBLIC_ORDERS] Status da resposta:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      let errorData: Record<string, unknown>;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }

      if (isBenignNoCaixaHttpError(response.status, errorData)) {
        logDebug(
          '✅ [GET_PUBLIC_ORDERS] Sem caixa aberto para o período — tratando como sucesso com lista vazia (sem erro ao usuário)'
        );
        return {
          success: true,
          pedidos: [],
          caixas: undefined,
          noOpenCaixa: true,
        };
      }

      logError('❌ [GET_PUBLIC_ORDERS] Erro HTTP:', response.status, errorText.slice(0, 200));
      const parsedError =
        typeof errorData.error === 'string' && errorData.error.trim()
          ? errorData.error
          : '';
      return {
        success: false,
        error: userFacingHttpError(response.status, parsedError || errorText),
      };
    }

    let responseData = await response.json();
    logDebug("✅ [GET_PUBLIC_ORDERS] ===== RESPOSTA BRUTA DO WEBHOOK =====");
    logDebug("✅ [GET_PUBLIC_ORDERS] Resposta completa:", JSON.stringify(responseData, null, 2));
    logDebug("✅ [GET_PUBLIC_ORDERS] Tipo de responseData:", typeof responseData);
    logDebug("✅ [GET_PUBLIC_ORDERS] É array?", Array.isArray(responseData));
    
    // O webhook get-orders pode retornar:
    // 1. Array direto: [{ "Caixas": [...] }, { "Pedidos": [...] }]
    // 2. Objeto com array: { success: true, pedidos: [...] } ou { data: [...] }
    
    let data: any = null;
    
    // Se a resposta é um array direto, usar diretamente
    if (Array.isArray(responseData)) {
      logDebug("✅ [GET_PUBLIC_ORDERS] Resposta é array direto:", responseData.length, "itens");
      data = responseData;
    } 
    // Se é um objeto, verificar propriedades
    else if (responseData && typeof responseData === 'object') {
      logDebug("✅ [GET_PUBLIC_ORDERS] Resposta é objeto. Chaves:", Object.keys(responseData));
      
      // NOVO FORMATO: { success: true, pedidos: { Pedidos: [...] } }
      // Verificar primeiro se tem a estrutura { pedidos: { Pedidos: [...] } }
      if ('pedidos' in responseData && responseData.pedidos && typeof responseData.pedidos === 'object') {
        if ('Pedidos' in responseData.pedidos && Array.isArray(responseData.pedidos.Pedidos)) {
          logDebug("✅ [GET_PUBLIC_ORDERS] Formato detectado: { pedidos: { Pedidos: [...] } }");
          // Usar o objeto pedidos completo para processar depois
          data = responseData.pedidos;
        } else if ('data' in responseData.pedidos && Array.isArray(responseData.pedidos.data)) {
          logDebug("✅ [GET_PUBLIC_ORDERS] Formato detectado: { pedidos: { data: [...] } }");
          data = responseData.pedidos;
        }
      }
      
      // Se ainda não encontrou, verificar todas as propriedades que podem conter arrays
      if (!data) {
        for (const key in responseData) {
          const value = responseData[key];
          if (Array.isArray(value)) {
            logDebug(`✅ [GET_PUBLIC_ORDERS] Encontrado array na chave '${key}' com ${value.length} itens`);
            // Priorizar chaves conhecidas
            if (key === 'pedidos' || key === 'Pedidos' || key === 'data' || key === 'Data') {
              data = value;
              logDebug(`✅ [GET_PUBLIC_ORDERS] Usando array da chave '${key}'`);
              break;
            }
          }
        }
      }
      
      // Se não encontrou array nas propriedades conhecidas, usar o primeiro array encontrado
      if (!data) {
        for (const key in responseData) {
          const value = responseData[key];
          if (Array.isArray(value)) {
            logDebug(`✅ [GET_PUBLIC_ORDERS] Usando primeiro array encontrado na chave '${key}'`);
            data = value;
            break;
          }
        }
      }
      
      // Se ainda não encontrou array, o objeto pode ser o array em si (caso especial)
      if (!data) {
        logDebug("✅ [GET_PUBLIC_ORDERS] Nenhum array encontrado. Verificando se o objeto em si é o array...");
        // Tentar usar o objeto completo se não encontrou array
        data = responseData;
      }
    } else {
      logError("❌ [GET_PUBLIC_ORDERS] Formato de resposta não reconhecido:", typeof responseData);
      data = responseData;
    }
    
    // Processar diferentes formatos de resposta
    let pedidos: Order[] = [];
    let caixas: Caixa[] = [];
    
    // Usar a variável 'data' que foi extraída acima
    const dataToProcess = data;
    
    logDebug("✅ [GET_PUBLIC_ORDERS] Data final para processamento:", typeof dataToProcess, Array.isArray(dataToProcess) ? `Array com ${dataToProcess.length} itens` : 'Objeto');
    if (Array.isArray(dataToProcess) && dataToProcess.length > 0) {
      logDebug("✅ [GET_PUBLIC_ORDERS] Primeiro item do array:", JSON.stringify(dataToProcess[0], null, 2));
      logDebug("✅ [GET_PUBLIC_ORDERS] Chaves do primeiro item:", Object.keys(dataToProcess[0]));
    } else if (dataToProcess && typeof dataToProcess === 'object' && !Array.isArray(dataToProcess)) {
      logDebug("✅ [GET_PUBLIC_ORDERS] Data é objeto. Estrutura completa:", JSON.stringify(dataToProcess, null, 2));
    }
    
    // Novo formato: array com um único objeto que tem as chaves "Caixas" e "Pedidos" (com maiúsculas)
    // Formato SEM filtro de data: [{ "Caixas": [...], "Pedidos": [...] }]
    // Formato COM filtro de data: [{ "Pedidos": [...] }] (sem "Caixas", apenas pedidos do dia)
    if (Array.isArray(dataToProcess) && dataToProcess.length > 0) {
      logDebug("✅ [GET_PUBLIC_ORDERS] Processando array com", dataToProcess.length, "itens");
      
      // Processar cada item do array
      dataToProcess.forEach((item: any, index: number) => {
        if (item && typeof item === 'object') {
          // Verificar se tem chave "Caixas" (com C maiúsculo) - pode ser array ou objeto único
          if ('Caixas' in item) {
            if (Array.isArray(item.Caixas)) {
              item.Caixas.forEach((caixa: any) => {
                caixas.push(caixa);
                logDebug("✅ [GET_PUBLIC_ORDERS] Caixa encontrado:", caixa.id, "- Status:", caixa.status);
              });
            } else if (item.Caixas && typeof item.Caixas === 'object') {
              // Se for um objeto único, adicionar diretamente
              caixas.push(item.Caixas);
              logDebug("✅ [GET_PUBLIC_ORDERS] Caixa encontrado (objeto único):", item.Caixas.id);
            }
          }
          
          // Verificar se tem chave "Pedidos" (com P maiúsculo) - pode ser array ou objeto único
          if ('Pedidos' in item) {
            if (Array.isArray(item.Pedidos)) {
              item.Pedidos.forEach((pedido: any) => {
                pedidos.push(pedido);
                logDebug("✅ [GET_PUBLIC_ORDERS] Pedido encontrado:", pedido.id, "- Status:", pedido.status);
              });
            } else if (item.Pedidos && typeof item.Pedidos === 'object') {
              // Se for um objeto único, adicionar diretamente
              pedidos.push(item.Pedidos);
              logDebug("✅ [GET_PUBLIC_ORDERS] Pedido encontrado (objeto único):", item.Pedidos.id);
            }
          }
          
          // Fallback: verificar chaves minúsculas também (compatibilidade)
          if ('caixas' in item) {
            if (Array.isArray(item.caixas)) {
              item.caixas.forEach((caixa: any) => {
                caixas.push(caixa);
                logDebug("✅ [GET_PUBLIC_ORDERS] Caixa encontrado (minúscula):", caixa.id);
              });
            } else if (item.caixas && typeof item.caixas === 'object') {
              caixas.push(item.caixas);
              logDebug("✅ [GET_PUBLIC_ORDERS] Caixa encontrado (minúscula, objeto único):", item.caixas.id);
            }
          }
          
          if ('pedidos' in item) {
            if (Array.isArray(item.pedidos)) {
              item.pedidos.forEach((pedido: any) => {
                pedidos.push(pedido);
                logDebug("✅ [GET_PUBLIC_ORDERS] Pedido encontrado (minúscula):", pedido.id);
              });
            } else if (item.pedidos && typeof item.pedidos === 'object') {
              pedidos.push(item.pedidos);
              logDebug("✅ [GET_PUBLIC_ORDERS] Pedido encontrado (minúscula, objeto único):", item.pedidos.id);
            }
          }
        } else {
          logWarn(`⚠️ [GET_PUBLIC_ORDERS] Item ${index} não é um objeto válido:`, typeof item);
        }
      });
      
      logDebug("✅ [GET_PUBLIC_ORDERS] Total de caixas processados:", caixas.length);
      logDebug("✅ [GET_PUBLIC_ORDERS] Total de pedidos processados:", pedidos.length);
    } else if (dataToProcess && typeof dataToProcess === 'object' && !Array.isArray(dataToProcess)) {
      // Se a resposta veio como objeto único (não array), processar diretamente
      logDebug("✅ [GET_PUBLIC_ORDERS] Resposta é objeto único, verificando estrutura...");
      
      // Verificar chaves maiúsculas primeiro
      if ('Caixas' in dataToProcess) {
        if (Array.isArray(dataToProcess.Caixas)) {
          caixas = dataToProcess.Caixas;
          logDebug("✅ [GET_PUBLIC_ORDERS] Encontrou chave 'Caixas' com array:", caixas.length);
        } else if (dataToProcess.Caixas && typeof dataToProcess.Caixas === 'object') {
          caixas = [dataToProcess.Caixas];
          logDebug("✅ [GET_PUBLIC_ORDERS] Encontrou chave 'Caixas' com objeto único");
        }
      }
      
      if ('Pedidos' in dataToProcess) {
        if (Array.isArray(dataToProcess.Pedidos)) {
          pedidos = dataToProcess.Pedidos;
          logDebug("✅ [GET_PUBLIC_ORDERS] Encontrou chave 'Pedidos' com array:", pedidos.length);
        } else if (dataToProcess.Pedidos && typeof dataToProcess.Pedidos === 'object') {
          pedidos = [dataToProcess.Pedidos];
          logDebug("✅ [GET_PUBLIC_ORDERS] Encontrou chave 'Pedidos' com objeto único");
        }
      }
      
      // Fallback: chaves minúsculas
      if ('caixas' in dataToProcess) {
        if (Array.isArray(dataToProcess.caixas)) {
          caixas = dataToProcess.caixas;
          logDebug("✅ [GET_PUBLIC_ORDERS] Encontrou chave 'caixas' com array:", caixas.length);
        } else if (dataToProcess.caixas && typeof dataToProcess.caixas === 'object') {
          caixas = [dataToProcess.caixas];
          logDebug("✅ [GET_PUBLIC_ORDERS] Encontrou chave 'caixas' com objeto único");
        }
      }
      
      if ('pedidos' in dataToProcess) {
        if (Array.isArray(dataToProcess.pedidos)) {
          pedidos = dataToProcess.pedidos;
          logDebug("✅ [GET_PUBLIC_ORDERS] Encontrou chave 'pedidos' com array:", pedidos.length);
        } else if (dataToProcess.pedidos && typeof dataToProcess.pedidos === 'object') {
          pedidos = [dataToProcess.pedidos];
          logDebug("✅ [GET_PUBLIC_ORDERS] Encontrou chave 'pedidos' com objeto único");
        }
      }
    }
    
    // Fallback: processar formato antigo (array de arrays com { data: [...] })
    if (pedidos.length === 0 && caixas.length === 0 && Array.isArray(data) && data.length > 0) {
      data.forEach((item: any) => {
        if (item && typeof item === 'object' && 'data' in item && Array.isArray(item.data)) {
          if (item.data.length > 0) {
            const firstItem = item.data[0];
            // Verificar se é array de caixas (tem campos específicos de caixa)
            if (firstItem && (firstItem.data_abertura || firstItem.data_fechamento || firstItem.status === 'aberto' || firstItem.status === 'fechado')) {
              caixas = item.data;
              logDebug("✅ [GET_PUBLIC_ORDERS] Caixas encontrados (formato antigo):", caixas.length);
            } 
            // Verificar se é array de pedidos (tem campos específicos de pedido)
            else if (firstItem && (firstItem.cliente || firstItem.total_pedido || firstItem.produtos || firstItem.caixa_id)) {
              pedidos = item.data;
              logDebug("✅ [GET_PUBLIC_ORDERS] Pedidos encontrados (formato antigo):", pedidos.length);
            }
          }
        }
      });
    }
    
    // Se ainda não encontrou, processar outros formatos
    // IMPORTANTE: Se pedidos.length === 0, pode ser que o array dentro de 'pedidos' contenha objetos com "Caixas" e "Pedidos"
    if (pedidos.length === 0 && caixas.length === 0) {
    // Formato 1: { pedidos: [...] } - mas o array pode conter objetos com "Caixas" e "Pedidos"
    if (data && typeof data === 'object' && 'pedidos' in data) {
      logDebug("✅ [GET_PUBLIC_ORDERS] Encontrou chave 'pedidos'");
      logDebug("✅ [GET_PUBLIC_ORDERS] Tipo de data.pedidos:", typeof data.pedidos);
      logDebug("✅ [GET_PUBLIC_ORDERS] data.pedidos é array?", Array.isArray(data.pedidos));
      logDebug("✅ [GET_PUBLIC_ORDERS] Valor de data.pedidos:", data.pedidos);
      
      if (Array.isArray(data.pedidos)) {
        // Verificar se o array contém objetos com "Caixas" e "Pedidos"
        if (data.pedidos.length > 0) {
          const firstItem = data.pedidos[0];
          if (firstItem && typeof firstItem === 'object' && ('Caixas' in firstItem || 'Pedidos' in firstItem || 'caixas' in firstItem || 'pedidos' in firstItem)) {
            // O array contém objetos com "Caixas" e "Pedidos", processar cada item
            logDebug("✅ [GET_PUBLIC_ORDERS] Array dentro de 'pedidos' contém objetos com 'Caixas' e 'Pedidos', processando...");
            data.pedidos.forEach((item: any) => {
              if (item && typeof item === 'object') {
                if ('Caixas' in item && Array.isArray(item.Caixas)) {
                  item.Caixas.forEach((caixa: any) => caixas.push(caixa));
                }
                if ('Pedidos' in item && Array.isArray(item.Pedidos)) {
                  item.Pedidos.forEach((pedido: any) => pedidos.push(pedido));
                }
                if ('caixas' in item && Array.isArray(item.caixas)) {
                  item.caixas.forEach((caixa: any) => caixas.push(caixa));
                }
                if ('pedidos' in item && Array.isArray(item.pedidos)) {
                  item.pedidos.forEach((pedido: any) => pedidos.push(pedido));
                }
              }
            });
            logDebug("✅ [GET_PUBLIC_ORDERS] Após processar array dentro de 'pedidos':", pedidos.length, "pedidos,", caixas.length, "caixas");
          } else {
            // É um array direto de pedidos
            pedidos = data.pedidos;
            logDebug("✅ [GET_PUBLIC_ORDERS] Formato detectado: { pedidos: [...] } (array direto de pedidos)");
          }
        } else {
          pedidos = data.pedidos;
          logDebug("✅ [GET_PUBLIC_ORDERS] Formato detectado: { pedidos: [] } (array vazio)");
        }
      } else if (data.pedidos && typeof data.pedidos === 'object') {
        // Se pedidos não é array, pode ser um objeto com estrutura aninhada
        logDebug("✅ [GET_PUBLIC_ORDERS] data.pedidos é objeto, verificando estrutura...");
        logDebug("✅ [GET_PUBLIC_ORDERS] Chaves de data.pedidos:", Object.keys(data.pedidos));
        
        // Tentar encontrar array dentro do objeto - NOVO FORMATO: { pedidos: { Pedidos: [...] } }
        if (Array.isArray(data.pedidos.Pedidos)) {
          pedidos = data.pedidos.Pedidos;
          logDebug("✅ [GET_PUBLIC_ORDERS] Formato detectado: { pedidos: { Pedidos: [...] } }");
        } else if (Array.isArray(data.pedidos.data)) {
          pedidos = data.pedidos.data;
          logDebug("✅ [GET_PUBLIC_ORDERS] Formato detectado: { pedidos: { data: [...] } }");
        } else if (Array.isArray(data.pedidos.pedidos)) {
          pedidos = data.pedidos.pedidos;
          logDebug("✅ [GET_PUBLIC_ORDERS] Formato detectado: { pedidos: { pedidos: [...] } }");
        }
      }
    }
    }
    // Formato 2: [{ data: [...] }] - formato antigo do webhook (já processado acima como fallback)
    // Formato 3: Array direto de pedidos (só processar se ainda não encontrou nada)
    if (pedidos.length === 0 && caixas.length === 0 && Array.isArray(data) && data.length > 0) {
      // Verificar se é array direto de pedidos (sem estrutura { data: [...] } ou { caixas/pedidos: ... })
      const firstItem = data[0];
      // Só processar se não tem as chaves "caixas" ou "pedidos" (já processado acima)
      if (firstItem && !('caixas' in firstItem) && !('pedidos' in firstItem) && (firstItem.cliente || firstItem.total_pedido || firstItem.produtos || firstItem.caixa_id)) {
        pedidos = data;
        logDebug("✅ [GET_PUBLIC_ORDERS] Formato detectado: Array direto de pedidos");
      }
    }
    // Formato 3: Array direto de pedidos (já verificado acima)
    // Formato 4: { data: [...] }
    else if (data && typeof data === 'object' && 'data' in data) {
      logDebug("✅ [GET_PUBLIC_ORDERS] Encontrou chave 'data'");
      if (Array.isArray(data.data)) {
        pedidos = data.data;
        logDebug("✅ [GET_PUBLIC_ORDERS] Formato detectado: { data: [...] }");
      } else if (Array.isArray(data.data) === false && data.data && typeof data.data === 'object') {
        // Se data não é array, pode ser um objeto com estrutura aninhada
        logDebug("✅ [GET_PUBLIC_ORDERS] data.data é objeto, verificando estrutura...");
        if (Array.isArray(data.data.data)) {
          pedidos = data.data.data;
          logDebug("✅ [GET_PUBLIC_ORDERS] Formato detectado: { data: { data: [...] } }");
        } else if (Array.isArray(data.data.pedidos)) {
          pedidos = data.data.pedidos;
          logDebug("✅ [GET_PUBLIC_ORDERS] Formato detectado: { data: { pedidos: [...] } }");
        }
      }
    }
    
    if (pedidos.length === 0) {
      logWarn("⚠️ [GET_PUBLIC_ORDERS] Formato de resposta não reconhecido, retornando array vazio");
      logWarn("⚠️ [GET_PUBLIC_ORDERS] Estrutura completa recebida:", JSON.stringify(data, null, 2));
    }
    
    logDebug(`✅ [GET_PUBLIC_ORDERS] ${pedidos.length} pedido(s) processado(s)`);
    logDebug(`✅ [GET_PUBLIC_ORDERS] ${caixas.length} caixa(s) processado(s)`);

    if (dataFiltro && dataFiltro.trim() && pedidos.length > 0) {
      const day = dataFiltro.trim();
      const before = pedidos.length;
      pedidos = pedidos.filter((p) =>
        orderMatchesOperationalDay((p as Order).created_at, day)
      );
      logDebug(
        `📅 [GET_PUBLIC_ORDERS] Filtro operacional ${day}: ${before} → ${pedidos.length} pedido(s)`
      );
    }

    let noOpenCaixaFromBody = false;
    if (responseData && typeof responseData === 'object') {
      const rd = responseData as Record<string, unknown>;
      const c = typeof rd.code === 'string' ? rd.code : '';
      if (c === 'NO_OPEN_CAIXA' || c === 'NO_CAIXA' || c === 'CAIXA_CLOSED') {
        noOpenCaixaFromBody = true;
      }
      if (rd.noOpenCaixa === true) noOpenCaixaFromBody = true;
    }

    return {
      success: true,
      pedidos: pedidos,
      caixas: caixas.length > 0 ? caixas : undefined,
      ...(noOpenCaixaFromBody ? { noOpenCaixa: true as const } : {}),
    };
  } catch (error) {
    logError("❌ [GET_PUBLIC_ORDERS] Erro ao buscar pedidos:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro desconhecido ao buscar pedidos'
    };
  }
}

/** Fila JSON aguardando aprovação — endpoint público minimizado (LGPD). */
export async function getPublicPendingOrders(
  cnpj: string,
  telefone: string
): Promise<{ success: boolean; pedidos_pendentes?: unknown[]; error?: string }> {
  try {
    const cleanCnpj = cnpj.replace(/\D/g, "");
    const phoneDigits = telefone.replace(/\D/g, "");
    if (cleanCnpj.length !== 14 || phoneDigits.length < 10) {
      return { success: false, error: "CNPJ ou telefone inválido" };
    }
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
    const url = `${N8N_WEBHOOK_URL}public-pending-orders?cnpj=${encodeURIComponent(cleanCnpj)}&telefone=${encodeURIComponent(phoneDigits)}`;
    const response = await panelFetch(url, {
      method: "GET",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      if (response.status === 404) {
        return { success: true, pedidos_pendentes: [] };
      }
      const text = await response.text();
      return { success: false, error: text || `HTTP ${response.status}` };
    }
    const data = (await response.json()) as {
      ok?: boolean;
      pedidos_pendentes?: unknown[];
    };
    return {
      success: true,
      pedidos_pendentes: Array.isArray(data.pedidos_pendentes)
        ? data.pedidos_pendentes
        : [],
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Erro ao buscar pendentes",
    };
  }
}

// Função para atualizar status de um pedido (POST)
// Webhook esperado: https://delival-n8n.rwysej.easypanel.host/webhook/update-order
// Body: { control: { cnpj, action, id, pedido_id, controle_dia, caixa_id, cliente, cliente_delivery_id, telefone?, … }, orderData: { patch } }
// Use options.searchContext (ex.: orderSearchContextForWebhook(pedido)) quando orderData for mínimo, para o n8n filtrar com as mesmas chaves do Supabase.
// O n8n deve priorizar `control.id`; as demais chaves em `control` são para condições extras (cliente UUID, caixa, controle_dia, etc.).
// Por padrão `orderData` é um PATCH: só entram no JSON as chaves presentes no objeto (+ `status` via 4º arg quando não há `status` em orderData).
// Use options.syncFullOrder: true para espelhar todo o objeto (ex.: impressão com auto-map completo).
// Chaves de ligação (PK/FK) ficam em `control` para o n8n filtrar o UPDATE sem auto-mapear `id` no payload do Supabase (evita pedidos_pkey duplicado).
// Retorna: { success: boolean, message?: string }
function isUuidString(v: unknown): v is string {
  if (typeof v !== "string") return false;
  const s = v.trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

function ownKey(obj: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

/**
 * Colunas de `public.pedidos` confirmadas no schema DelivAI (capturas neste projeto).
 * O n8n com Supabase “Auto-Map” falha (PGRST204) se `orderData` tiver qualquer chave fora deste conjunto.
 */
const PEDIDOS_SUPABASE_ORDERDATA_KEYS = new Set([
  "status",
  "nome_cliente",
  "telefone",
  "cliente_delivery_id",
  "tipo_pedido",
  "endereco",
  "bairro",
  "complemento",
  "referencia",
  "produtos",
  "total_produtos",
  "taxa_entrega",
  "desc_ifood",
  "total_pedido",
  "pagamento",
  "troco_para",
  "troco",
  "observacoes",
  "loc_ifood",
  "pago",
  "caixa_id",
  "controle_dia",
  "entregador",
  "lat_entrega",
  "lng_entrega",
  "delivery_lat",
  "delivery_lng",
  "created_at",
  "privacy_consent_at",
  "privacy_policy_version",
]);

function pickPedidosOrderDataColumns(fields: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(fields)) {
    if (PEDIDOS_SUPABASE_ORDERDATA_KEYS.has(k)) {
      out[k] = v;
    }
  }
  return out;
}

/** PK/FK e ligações: só em `control`, nunca no objeto `orderData` do webhook. */
const ORDER_UPDATE_LINKAGE_KEYS = new Set([
  "id",
  "pedido_id",
  "controle_dia",
  "caixa_id",
  "cliente_delivery_id",
  "cliente",
  "cliente_id",
]);

function applyOrderLinkageToControl(control: Record<string, unknown>, orderData: object) {
  const od = orderData as Record<string, unknown>;
  if (od.controle_dia !== undefined && od.controle_dia !== null && od.controle_dia !== "") {
    control.controle_dia = od.controle_dia;
  }
  if (od.caixa_id !== undefined && od.caixa_id !== null && od.caixa_id !== "") {
    control.caixa_id = od.caixa_id;
  }
  if (
    od.cliente_delivery_id !== undefined &&
    od.cliente_delivery_id !== null &&
    od.cliente_delivery_id !== ""
  ) {
    control.cliente_delivery_id = od.cliente_delivery_id;
  }
  const rawCliente = od.cliente;
  if (rawCliente !== undefined && rawCliente !== null && rawCliente !== "") {
    if (isUuidString(rawCliente)) {
      control.cliente = String(rawCliente).trim();
    } else if (typeof rawCliente === "number" && Number.isFinite(rawCliente)) {
      control.cliente_id = rawCliente;
    }
  }
}

/** Monta chaves de pesquisa do pedido para `control` (n8n/Supabase), sem duplicar em orderData. */
export function orderSearchContextForWebhook(order: unknown): Record<string, unknown> {
  if (!order || typeof order !== "object") return {};
  const o = order as Record<string, unknown>;
  const ctx: Record<string, unknown> = {};
  if (o.controle_dia !== undefined && o.controle_dia !== null && o.controle_dia !== "") {
    ctx.controle_dia = o.controle_dia;
  }
  if (o.caixa_id !== undefined && o.caixa_id !== null && o.caixa_id !== "") {
    ctx.caixa_id = o.caixa_id;
  }
  if (
    o.cliente_delivery_id !== undefined &&
    o.cliente_delivery_id !== null &&
    o.cliente_delivery_id !== ""
  ) {
    ctx.cliente_delivery_id = o.cliente_delivery_id;
  }
  if (o.cliente !== undefined && o.cliente !== null && o.cliente !== "") {
    ctx.cliente = o.cliente;
  }
  const tel = o.telefone ?? o.telefone_cliente;
  if (tel !== undefined && tel !== null && String(tel).trim() !== "") {
    ctx.telefone = String(tel).trim();
  }
  return ctx;
}

/** Aplica em `control` o contexto de pesquisa (ligações + telefone opcional para filtro no workflow). */
function applyWebhookSearchContext(control: Record<string, unknown>, ctx: object) {
  applyOrderLinkageToControl(control, ctx);
  const od = ctx as Record<string, unknown>;
  const tel = od.telefone ?? od.telefone_cliente;
  if (tel !== undefined && tel !== null && String(tel).trim() !== "") {
    control.telefone = String(tel).trim();
  }
}

function stripLinkageKeysFromOrderFields(fields: Record<string, unknown>) {
  for (const k of ORDER_UPDATE_LINKAGE_KEYS) {
    delete fields[k];
  }
}

export async function updatePublicOrder(
  cnpj: string,
  pedido_id: number,
  action: string,
  status: string,
  orderData?: any,
  options?: {
    timeoutMs?: number;
    syncFullOrder?: boolean;
    /** Chaves de pesquisa do pedido (FKs, controle_dia, telefone) quando o body não traz o objeto completo. */
    searchContext?: Record<string, unknown>;
    /** Notificações WhatsApp enviadas server-side após gravar o pedido. */
    whatsappNotify?: import("../utils/whatsappNotifications").WhatsappNotifyItem[];
    /** Split de pagamento (`order_payments`). Fora do orderData — não é coluna de pedidos. */
    pagamentos?: Array<{
      pagamento?: string;
      payment_method?: string;
      valor?: number;
      amount?: number;
    }>;
    /** Soma no caixa sem apagar cobranças já lançadas nesta comanda. */
    appendPayments?: boolean;
  }
): Promise<{ success: boolean; message?: string; error?: string; printid?: number | null }> {
  try {
    const syncFullOrder = options?.syncFullOrder === true;
    logDebug("📦 [UPDATE_PUBLIC_ORDER] Atualizando pedido:", { cnpj, pedido_id, action, status, syncFullOrder });

    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
    const webhookUrl = `${N8N_WEBHOOK_URL}update-order`;

    logDebug("🌐 [UPDATE_PUBLIC_ORDER] Fazendo requisição para:", webhookUrl);

    const pidArg = Number(pedido_id);
    const oidFromBody = orderData?.id != null ? Number(orderData.id) : NaN;
    const rowId = Number.isFinite(oidFromBody) ? oidFromBody : pidArg;

    const control: Record<string, unknown> = {
      cnpj,
      action: action || "update",
    };
    if (Number.isFinite(rowId)) {
      control.id = rowId;
      control.pedido_id = rowId;
    }
    if (options?.appendPayments) control.append_payments = true;

    const sc = options?.searchContext;
    if (sc && typeof sc === "object" && Object.keys(sc).length > 0) {
      applyWebhookSearchContext(control, sc);
    }

    if (orderData) {
      applyOrderLinkageToControl(control, orderData);
      if (orderData.print_format != null && String(orderData.print_format).trim() !== "") {
        control.print_format = String(orderData.print_format).trim();
      }
      // print_kind / pdv_headline NÃO são coluna de pedidos — só em control (QR, comanda, ticket PDV).
      if (orderData.print_kind != null && String(orderData.print_kind).trim() !== "") {
        control.print_kind = String(orderData.print_kind).trim().toLowerCase();
      }
      if (orderData.print_origin != null && String(orderData.print_origin).trim() !== "") {
        control.print_origin = String(orderData.print_origin).trim().toLowerCase();
      }
      if (orderData.pdv_headline != null && String(orderData.pdv_headline).trim() !== "") {
        control.pdv_headline = String(orderData.pdv_headline).trim();
      }
      if (orderData.pessoas != null && String(orderData.pessoas).trim() !== "") {
        const people = Number(orderData.pessoas);
        if (Number.isFinite(people) && people > 0) {
          control.pessoas = Math.floor(people);
        }
      }
    }

    const orderFields: Record<string, unknown> = {};

    if (orderData && syncFullOrder) {
      const enderecoVal = orderData.endereco ?? orderData.endereco_entrega ?? "";
      const taxaVal = orderData.taxa_entrega ?? orderData.taxa ?? 0;
      const totalPedidoVal = orderData.total_pedido ?? orderData.total ?? 0;
      const totalProdutosVal = orderData.total_produtos ?? orderData.total ?? 0;
      if (status) orderFields.status = status;
      else if (orderData.status !== undefined) orderFields.status = orderData.status ?? "";
      if (orderData.telefone !== undefined || orderData.telefone_cliente !== undefined) {
        orderFields.telefone = orderData.telefone ?? orderData.telefone_cliente ?? "";
      }
      if (enderecoVal !== undefined && enderecoVal !== null) {
        orderFields.endereco = String(enderecoVal).trim();
      }
      if (orderData.bairro !== undefined && orderData.bairro !== null) {
        orderFields.bairro = String(orderData.bairro).trim();
      }
      if (orderData.complemento !== undefined && orderData.complemento !== null) {
        orderFields.complemento = String(orderData.complemento).trim();
      }
      if (orderData.referencia !== undefined && orderData.referencia !== null) {
        orderFields.referencia = String(orderData.referencia).trim();
      }
      if (totalProdutosVal !== undefined && totalProdutosVal !== null) {
        orderFields.total_produtos = Number(totalProdutosVal) || 0;
      }
      if (taxaVal !== undefined && taxaVal !== null) {
        orderFields.taxa_entrega = Number(taxaVal) || 0;
      }
      if (totalPedidoVal !== undefined && totalPedidoVal !== null) {
        orderFields.total_pedido = Number(totalPedidoVal) || 0;
      }
      if (orderData.pagamento !== undefined) orderFields.pagamento = orderData.pagamento || "";
      if (orderData.observacoes !== undefined || orderData.obs !== undefined) {
        orderFields.observacoes = (orderData.observacoes ?? orderData.obs ?? "") as string;
      }
      if (orderData.tipo_pedido !== undefined) orderFields.tipo_pedido = orderData.tipo_pedido || "";
      if (orderData.entregador !== undefined) orderFields.entregador = orderData.entregador || "";
      if (
        orderData.lat_entrega !== undefined ||
        orderData.delivery_lat !== undefined
      ) {
        const lat = orderData.lat_entrega ?? orderData.delivery_lat;
        if (lat != null) orderFields.lat_entrega = Number(lat);
      }
      if (
        orderData.lng_entrega !== undefined ||
        orderData.delivery_lng !== undefined
      ) {
        const lng = orderData.lng_entrega ?? orderData.delivery_lng;
        if (lng != null) orderFields.lng_entrega = Number(lng);
      }
      if (orderData.produtos !== undefined && Array.isArray(orderData.produtos) && orderData.produtos.length > 0) {
        orderFields.produtos = orderData.produtos;
      }
      if (orderData.nome_cliente !== undefined) orderFields.nome_cliente = orderData.nome_cliente || "";
      else if (
        orderData.cliente !== undefined &&
        typeof orderData.cliente === "string" &&
        String(orderData.cliente).trim() !== "" &&
        !isUuidString(orderData.cliente)
      ) {
        orderFields.nome_cliente = String(orderData.cliente).trim();
      }
      if (orderData.created_at !== undefined) orderFields.created_at = orderData.created_at || null;
      if (orderData.desc_ifood !== undefined || orderData.desconto_ifood !== undefined) {
        const dv = orderData.desc_ifood ?? orderData.desconto_ifood ?? 0;
        orderFields.desc_ifood = Number(dv) || 0;
      }
      if (orderData.loc_ifood !== undefined || orderData.numero_localizador !== undefined) {
        orderFields.loc_ifood = String(
          orderData.loc_ifood ?? orderData.numero_localizador ?? ""
        ).trim();
      }
      if (orderData.pago !== undefined) orderFields.pago = orderData.pago || false;
    } else if (orderData && !syncFullOrder) {
      if (ownKey(orderData, "status")) orderFields.status = orderData.status ?? "";
      else if (status) orderFields.status = status;

      if (ownKey(orderData, "telefone") || ownKey(orderData, "telefone_cliente")) {
        orderFields.telefone = orderData.telefone ?? orderData.telefone_cliente ?? "";
      }
      if (ownKey(orderData, "endereco") || ownKey(orderData, "endereco_entrega")) {
        const ev = orderData.endereco ?? orderData.endereco_entrega ?? "";
        orderFields.endereco = String(ev).trim();
      }
      if (ownKey(orderData, "bairro") && orderData.bairro != null) {
        orderFields.bairro = String(orderData.bairro).trim();
      }
      if (ownKey(orderData, "complemento") && orderData.complemento != null) {
        orderFields.complemento = String(orderData.complemento).trim();
      }
      if (ownKey(orderData, "referencia") && orderData.referencia != null) {
        orderFields.referencia = String(orderData.referencia).trim();
      }
      if (ownKey(orderData, "total_produtos")) {
        orderFields.total_produtos = Number(orderData.total_produtos) || 0;
      }
      if (ownKey(orderData, "taxa_entrega") || ownKey(orderData, "taxa")) {
        const tv = orderData.taxa_entrega ?? orderData.taxa ?? 0;
        orderFields.taxa_entrega = Number(tv) || 0;
      }
      if (ownKey(orderData, "total_pedido") || ownKey(orderData, "total")) {
        const tp = orderData.total_pedido ?? orderData.total ?? 0;
        orderFields.total_pedido = Number(tp) || 0;
      }
      if (ownKey(orderData, "pagamento")) orderFields.pagamento = orderData.pagamento || "";
      if (ownKey(orderData, "observacoes") || ownKey(orderData, "obs")) {
        orderFields.observacoes = (orderData.observacoes ?? orderData.obs ?? "") as string;
      }
      if (ownKey(orderData, "tipo_pedido")) orderFields.tipo_pedido = orderData.tipo_pedido || "";
      if (ownKey(orderData, "entregador")) orderFields.entregador = orderData.entregador || "";
      if (ownKey(orderData, "produtos") && Array.isArray(orderData.produtos) && orderData.produtos.length > 0) {
        orderFields.produtos = orderData.produtos;
      }
      if (ownKey(orderData, "nome_cliente")) orderFields.nome_cliente = orderData.nome_cliente || "";
      else if (
        ownKey(orderData, "cliente") &&
        typeof orderData.cliente === "string" &&
        String(orderData.cliente).trim() !== "" &&
        !isUuidString(orderData.cliente)
      ) {
        orderFields.nome_cliente = String(orderData.cliente).trim();
      }
      if (ownKey(orderData, "created_at")) orderFields.created_at = orderData.created_at ?? null;
      if (ownKey(orderData, "desconto_ifood") || ownKey(orderData, "desc_ifood")) {
        const dv = orderData.desc_ifood ?? orderData.desconto_ifood ?? 0;
        orderFields.desc_ifood = Number(dv) || 0;
      }
      if (ownKey(orderData, "loc_ifood") || ownKey(orderData, "numero_localizador")) {
        orderFields.loc_ifood = String(
          orderData.loc_ifood ?? orderData.numero_localizador ?? ""
        ).trim();
      }
      if (ownKey(orderData, "pago")) orderFields.pago = orderData.pago ?? false;
    } else if (status) {
      orderFields.status = status;
    }

    stripLinkageKeysFromOrderFields(orderFields);

    const notifyItems = (options?.whatsappNotify || []).filter(
      (i) => (i.text || "").trim() && (i.phone || "").trim()
    );

    const pagamentos = (options?.pagamentos || []).filter((row) => {
      const method = String(row.pagamento || row.payment_method || "").trim();
      const amount = Number(row.valor ?? row.amount ?? 0);
      return Boolean(method) && Number.isFinite(amount) && amount > 0;
    });

    const requestBody = {
      control,
      orderData: pickPedidosOrderDataColumns(orderFields),
      ...(notifyItems.length ? { whatsapp_notify: notifyItems } : {}),
      ...(pagamentos.length ? { pagamentos } : {}),
    };

    logDebug("📤 [UPDATE_PUBLIC_ORDER] JSON enviado:", JSON.stringify(requestBody, null, 2));
    logDebug("📤 [UPDATE_PUBLIC_ORDER] control:", control);
    logDebug("📤 [UPDATE_PUBLIC_ORDER] orderData:", orderFields);

    const timeoutMs = options?.timeoutMs && options.timeoutMs > 0 ? options.timeoutMs : 0;
    const postOnce = async () => {
      const controller = timeoutMs > 0 ? new AbortController() : null;
      const timeoutId =
        timeoutMs > 0 && controller
          ? window.setTimeout(() => controller.abort(), timeoutMs)
          : null;
      try {
        return await panelFetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Authorization': N8N_AUTH_HEADER,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
          signal: controller?.signal,
        });
      } finally {
        if (timeoutId !== null) window.clearTimeout(timeoutId);
      }
    };
    let response: Response;
    try {
      response = await postOnce();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return {
          success: false,
          error: `Tempo esgotado ao atualizar pedido (${timeoutMs}ms)`,
        };
      }
      throw err;
    }

    logDebug("📥 [UPDATE_PUBLIC_ORDER] Status da resposta:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      logError("❌ [UPDATE_PUBLIC_ORDER] Erro HTTP:", response.status, errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      
      return {
        success: false,
        error: errorData.error || `Erro ao atualizar pedido: ${response.status}`
      };
    }

    const data = await response.json();
    logDebug("✅ [UPDATE_PUBLIC_ORDER] Resposta:", data);

    const dataRec =
      data && typeof data === "object" ? (data as Record<string, unknown>) : {};
    void runWhatsappNotifyClientFallback(
      notifyItems,
      cnpj,
      dataRec,
      Number.isFinite(rowId) ? rowId : undefined
    );
    
    return {
      success: true,
      message: data.message || 'Pedido atualizado com sucesso',
      printid: data.printid || null // Incluir printid se disponível na resposta
    };
  } catch (error) {
    logError("❌ [UPDATE_PUBLIC_ORDER] Erro ao atualizar pedido:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro desconhecido ao atualizar pedido'
    };
  }
}

// Interface para item do pedido
export interface OrderItem {
  produto_id?: string;
  produto_nome: string;
  quantidade: number;
  preco: number;
  observacoes?: string;
  tipo_preco?: string; // Para produtos com múltiplos tamanhos/preços
}

// Interface para criação de pedido (estrutura do GPT)
export interface CreateOrderData {
  // Campos para o webhook
  cnpj: string;
  action: string;
  pedido_id: number;
  status: string;
  // Estrutura do GPT
  cliente: string;
  telefone: string;
  tipo_pedido: string; // entrega, retirada ou consumo_no_local
  endereco: string;
  bairro: string;
  complemento: string;
  referencia: string;
  produtos: Array<{
    nome: string;
    quantidade: number;
    preco: number;
  }>;
  total_produtos: number;
  taxa_entrega: number;
  desconto_ifood?: number;
  desc_ifood?: number; // Desconto iFood (alternativa)
  total_pedido: number;
  pagamento: string;
  observacoes: string;
  /** Valor que o cliente vai pagar em dinheiro (campo "Para quanto?"). */
  troco_para?: number;
  /** Troco a levar até o cliente. */
  troco?: number;
  origem_pedido?: string | null;
  numero_localizador?: string;
  loc_ifood?: string; // Localizador iFood (alternativa)
  pago?: boolean; // Status de pagamento (true/false)
  /** ID do cliente em clientes_delivery quando selecionado no autocomplete */
  cliente_delivery_id?: string | number | null;
  /** Preenchidos pelo fluxo de caixa (manage-caixa + ensure) quando aplicável */
  caixa_id?: string;
  controle_dia?: number;
  entregador?: string;
  /** Só repassado se existir coluna no Supabase (ex.: pendência com timestamp) */
  created_at?: string | null;
  /** Consentimento LGPD no checkout público (quando exigência de aceite está ligada). */
  privacy_consent_at?: string;
  privacy_policy_version?: string;
}

/** Interpreta respostas do n8n após insert em `pedidos` (formatos variados). */
function firstPositivePedidoIdFromCreateResponse(data: Record<string, unknown>): number | undefined {
  const tryNum = (v: unknown): number | undefined => {
    if (v === undefined || v === null || v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };
  let x = tryNum(data.pedido_id ?? data.id ?? data.pedidoId);
  if (x != null) return x;
  const d = data.data;
  if (d && typeof d === "object") {
    const o = d as Record<string, unknown>;
    x = tryNum(o.id ?? o.pedido_id);
    if (x != null) return x;
  }
  const rec = data.record;
  if (rec && typeof rec === "object") {
    x = tryNum((rec as Record<string, unknown>).id);
    if (x != null) return x;
  }
  for (const key of ["results", "rows"] as const) {
    const arr = data[key];
    if (Array.isArray(arr) && arr[0] && typeof arr[0] === "object") {
      x = tryNum((arr[0] as Record<string, unknown>).id);
      if (x != null) return x;
    }
  }
  return undefined;
}

async function runWhatsappNotifyClientFallback(
  items: import("./whatsappNotifications").WhatsappNotifyItem[] | undefined,
  cnpj: string,
  serverData: Record<string, unknown>,
  pedidoId?: number
): Promise<void> {
  const list = (items || []).filter((i) => (i.text || "").trim() && (i.phone || "").trim());
  if (!list.length) return;
  const {
    dispatchWhatsappNotifyItems,
    enrichWhatsappNotifyPedidoId,
    markOrderNotifyDispatchedLocally,
    shouldClientDispatchWhatsappNotify,
  } = await import("./whatsappNotifications");
  const enriched = enrichWhatsappNotifyPedidoId(list, pedidoId);
  // Notify já foi no body → servidor envia; só marca dedupe local e sai.
  if (
    !shouldClientDispatchWhatsappNotify(serverData, enriched.length, {
      notifyIncludedInRequest: true,
    })
  ) {
    markOrderNotifyDispatchedLocally(cnpj, enriched, pedidoId);
    return;
  }
  void dispatchWhatsappNotifyItems(enriched, cnpj);
}

/**
 * No checkout público o erro é exibido direto ao consumidor final. Mensagens técnicas
 * cruas do Postgres (ex.: `duplicate key value violates unique constraint "orders_pkey"`)
 * não devem aparecer para o cliente. Aqui trocamos esses casos por um texto amigável,
 * preservando o `(ref: ...)` do correlation_id para o suporte localizar o log.
 */
function sanitizePublicOrderErrorMessage(message: string): string {
  if (!message) return message;
  const technical =
    /duplicate key|orders_pkey|violates unique constraint|psqlexception|sqlstate|constraint\s+"/i;
  if (!technical.test(message)) return message;
  const refMatch = message.match(/\(ref:[^)]*\)/i);
  const friendly = "Não foi possível registrar seu pedido agora. Tente novamente em instantes.";
  return refMatch ? `${friendly} ${refMatch[0]}` : friendly;
}

// Função para criar um novo pedido usando o webhook update-order
// Webhook esperado: https://delival-n8n.rwysej.easypanel.host/webhook/update-order
// Body: { cnpj, action: "create", ...CreateOrderData }
// Retorna: { success: boolean, pedido_id?: number, message?: string, error?: string }
export async function createPublicOrder(
  orderData: CreateOrderData,
  options?: {
    /** `horario_funcionamento` do cliente — com isso resolve/fecha/abre caixa antes do create. */
    horarioFuncionamento?: unknown;
    /** Instante do pedido (ex.: pendência) para amarrar ao caixa do turno correto após meia-noite. */
    caixaResolveInstant?: Date;
    /** Não chama manage-caixa (compatível com workflow n8n antigo). */
    skipCaixaResolution?: boolean;
    /** Grava em `pedidos` sem `controle_dia` — número só na aprovação (loja fechada / sem auto-aceite). */
    awaitingApproval?: boolean;
    /** Token de sessão verificada do cliente (cardápio público). */
    sessionToken?: string | null;
    /** Origem do pedido no backend — interno pula verificação OTP do consumidor. */
    source?: "public" | "internal";
    /** Idioma do cliente no cardápio (WhatsApp e metadados do pedido via control). */
    customerLanguage?: "pt" | "en" | "es";
    /** Notificações WhatsApp enviadas server-side após persistir o pedido. */
    whatsappNotify?: import("../utils/whatsappNotifications").WhatsappNotifyItem[];
  }
): Promise<{
  success: boolean;
  pedido_id?: number;
  /** Número de controle do dia (ex.: comanda), quando o backend enviar. */
  controle_dia?: number;
  /** Pedido ficou na fila sem controle (loja fechada / defer). */
  awaiting_approval?: boolean;
  message?: string;
  error?: string;
}> {
  try {
    logDebug("📦 [CREATE_PUBLIC_ORDER] Criando pedido:", orderData);

    const awaitingApproval = options?.awaitingApproval === true;
    const notifyItems = (options?.whatsappNotify || []).filter(
      (i) => (i.text || "").trim() && (i.phone || "").trim()
    );

    let resolvedCaixaId: string | undefined;
    let resolvedNextControle: number | undefined;

    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER, N8N_SKIP_CAIXA_RESOLUTION } = await import('../config');
    if (!awaitingApproval && !options?.skipCaixaResolution && !N8N_SKIP_CAIXA_RESOLUTION) {
      const cres = await ensureCaixaForNewOrder(
        orderData.cnpj,
        options?.horarioFuncionamento ?? [],
        {
          resolveInstant: options?.caixaResolveInstant,
        }
      );
      if (!cres.ok) {
        // Prod: manage-caixa exige sessão; checkout público segue e o backend abre o caixa.
        const err = String(cres.error || "").toLowerCase();
        const authBlocked =
          err.includes("unauthorized") ||
          err.includes("não autoriz") ||
          err.includes("nao autoriz") ||
          err.includes("sessão") ||
          err.includes("sessao") ||
          err.includes("authorization");
        if (authBlocked) {
          logDebug(
            "📦 [CREATE_PUBLIC_ORDER] Caixa no front pulado (sem sessão) — backend resolve ensureSessionOpen"
          );
        } else {
          return { success: false, error: cres.error };
        }
      } else {
        resolvedCaixaId = cres.caixaId;
        resolvedNextControle = cres.nextControleDia;
      }
    }
    const webhookUrl = `${N8N_WEBHOOK_URL}update-order`;
    
    // Usar update-order com action "create" para criar um novo pedido
    // Estrutura conforme instrução do GPT (sem controle_dia)
    // Garantir que produtos não tenham produto_id e que pagamento/observacoes sejam sempre enviados
    const produtosLimpos = orderData.produtos.map(produto => ({
      nome: produto.nome,
      quantidade: produto.quantidade,
      preco: produto.preco
      // Explicitamente NÃO incluir produto_id
    }));
    
    const requestBody = {
      // Campos de controle do backend (não são colunas da tabela)
      control: {
        cnpj: orderData.cnpj,
        action: orderData.action || "create",
        source: options?.source ?? "public",
        ...(options?.sessionToken
          ? { session_token: options.sessionToken }
          : {}),
        ...(options?.customerLanguage
          ? { idioma_cliente: options.customerLanguage }
          : {}),
        ...(awaitingApproval ? { awaiting_approval: true } : {}),
        ...(resolvedCaixaId != null && resolvedCaixaId !== '' && { caixa_id: resolvedCaixaId }),
        ...(resolvedNextControle != null &&
          Number.isFinite(resolvedNextControle) && { controle_dia: resolvedNextControle }),
      },
      // Colunas enviadas = apenas as que existem em `pedidos` no Supabase (n8n auto-map).
      // Não incluir chaves “genéricas” sem coluna correspondente — cada uma gera PGRST204.
      orderData: pickPedidosOrderDataColumns(
        (() => {
          const od: Record<string, unknown> = {
            status: awaitingApproval ? "awaiting_approval" : orderData.status,
            nome_cliente: orderData.cliente,
            telefone: orderData.telefone,
            tipo_pedido: orderData.tipo_pedido,
            endereco: orderData.endereco,
            bairro: orderData.bairro || "",
            complemento: orderData.complemento || "",
            referencia: orderData.referencia || "",
            produtos: produtosLimpos,
            total_produtos: orderData.total_produtos,
            taxa_entrega: orderData.taxa_entrega || 0,
            desc_ifood: Number(orderData.desc_ifood ?? orderData.desconto_ifood ?? 0) || 0,
            total_pedido: orderData.total_pedido,
            pagamento: orderData.pagamento || "",
            observacoes: orderData.observacoes || "",
            ...(orderData.troco_para != null && orderData.troco_para > 0
              ? { troco_para: orderData.troco_para }
              : {}),
            ...(orderData.troco != null && orderData.troco >= 0 ? { troco: orderData.troco } : {}),
            ...(orderData.privacy_consent_at
              ? {
                  privacy_consent_at: orderData.privacy_consent_at,
                  privacy_policy_version: orderData.privacy_policy_version,
                }
              : {}),
            loc_ifood: String(
              orderData.loc_ifood ?? orderData.numero_localizador ?? ""
            ).trim(),
            pago: orderData.pago || false,
            entregador: orderData.entregador ?? "",
          };
          if (orderData.cliente_delivery_id != null && String(orderData.cliente_delivery_id).trim() !== "") {
            od.cliente_delivery_id = orderData.cliente_delivery_id;
          }
          if (resolvedCaixaId != null && resolvedCaixaId !== "") {
            od.caixa_id = resolvedCaixaId;
          }
          if (resolvedNextControle != null && Number.isFinite(resolvedNextControle)) {
            od.controle_dia = resolvedNextControle;
          }
          if (orderData.created_at != null && String(orderData.created_at).trim() !== "") {
            od.created_at = orderData.created_at;
          }
          return od;
        })()
      ),
      ...(notifyItems.length ? { whatsapp_notify: notifyItems } : {}),
    };
    
    logDebug("🌐 [CREATE_PUBLIC_ORDER] Fazendo requisição para:", webhookUrl);
    logDebug("📤 [CREATE_PUBLIC_ORDER] JSON enviado:", JSON.stringify(requestBody, null, 2));
    logDebug("💳 [CREATE_PUBLIC_ORDER] Pagamento no body:", requestBody.orderData.pagamento);
    logDebug("📝 [CREATE_PUBLIC_ORDER] Observações no body:", orderData.observacoes || "");
    logDebug("📦 [CREATE_PUBLIC_ORDER] Produtos (sem produto_id):", JSON.stringify(requestBody.orderData.produtos, null, 2));
    
    const response = await panelFetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Authorization': N8N_AUTH_HEADER,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    logDebug("📥 [CREATE_PUBLIC_ORDER] Status da resposta:", response.status);

      if (!response.ok) {
      const errorText = await response.text();
      logError("❌ [CREATE_PUBLIC_ORDER] Erro HTTP:", response.status, errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }

      if (response.status === 403 && (errorData as { error?: string })?.error === "requires_verification") {
        return {
          success: false,
          error: "requires_verification",
        };
      }
      
      const parsed = parseApiErrorPayload(errorData, response.status);
      return {
        success: false,
        error: sanitizePublicOrderErrorMessage(formatApiErrorForUser(parsed)),
      };
    }

    // Verificar se a resposta tem conteúdo antes de tentar fazer parse
    const responseText = await response.text();
    logDebug("📥 [CREATE_PUBLIC_ORDER] Resposta texto:", responseText);
    
    let data;
    if (responseText && responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        logDebug("✅ [CREATE_PUBLIC_ORDER] Resposta parseada:", data);
      } catch (parseError) {
        logError("❌ [CREATE_PUBLIC_ORDER] Erro ao fazer parse do JSON:", parseError);
        return {
          success: false,
          error: "Resposta do servidor inválida (pedido não confirmado)",
        };
      }
    } else {
      logWarn("⚠️ [CREATE_PUBLIC_ORDER] Resposta vazia com HTTP 200 — pedido não confirmado");
      data = { success: false };
    }

    const dataRec = data && typeof data === "object" ? (data as Record<string, unknown>) : {};
    const pedidoIdNumOpt = firstPositivePedidoIdFromCreateResponse(dataRec);
    const pedidoIdNum = pedidoIdNumOpt ?? NaN;

    const servidorNegou = data.success === false;
    const hasPedidoId = pedidoIdNumOpt != null;
    /** Alguns workflows só devolvem `{ success: true, message }` sem o id da linha — o insert já ocorreu. */
    const confirmacaoExplicitaOk = data.success === true;

    if (servidorNegou) {
      const parsed = parseApiErrorPayload(dataRec, response.status);
      return {
        success: false,
        error: sanitizePublicOrderErrorMessage(formatApiErrorForUser(parsed)),
      };
    }

    if (!hasPedidoId && !confirmacaoExplicitaOk) {
      return {
        success: false,
        error: sanitizePublicOrderErrorMessage(
          String(
            data.error ||
              data.message ||
              "Pedido não foi gravado (resposta sem pedido_id ou success: false)"
          )
        ),
      };
    }

    if (!hasPedidoId && confirmacaoExplicitaOk) {
      logWarn(
        "⚠️ [CREATE_PUBLIC_ORDER] HTTP 200 com success:true mas sem pedido_id na resposta. " +
          "Inclua no n8n (Respond to Webhook) o id retornado pelo nó Supabase, ex.: " +
          '{{ JSON.stringify({ success: true, pedido_id: $json.id, controle_dia: $json.controle_dia, message: "..." }) }}'
      );
    }

    if (resolvedCaixaId != null && resolvedNextControle != null) {
      const bump = await manageCaixaUpdate(orderData.cnpj, resolvedCaixaId, {
        controle_dia: resolvedNextControle,
      });
      if (!bump.ok) {
        logWarn(
          "⚠️ [CREATE_PUBLIC_ORDER] Pedido criado mas falha ao sincronizar controle_dia do caixa:",
          bump.error
        );
      }
    }

    void runWhatsappNotifyClientFallback(
      notifyItems,
      orderData.cnpj,
      dataRec,
      hasPedidoId ? pedidoIdNum : undefined
    );

    const controleDia = (() => {
      const c = Number(data.controle_dia ?? resolvedNextControle);
      return Number.isFinite(c) && c > 0 ? c : undefined;
    })();
    const awaitingFromServer =
      data.awaiting_approval === true ||
      data.awaitingApproval === true ||
      (awaitingApproval && controleDia == null);

    return {
      success: true,
      ...(hasPedidoId ? { pedido_id: pedidoIdNum } : {}),
      controle_dia: controleDia,
      ...(awaitingFromServer ? { awaiting_approval: true } : {}),
      message: (typeof data.message === "string" && data.message) || "Pedido criado com sucesso",
    };
  } catch (error) {
    logError("❌ [CREATE_PUBLIC_ORDER] Erro ao criar pedido:", error);
    return {
      success: false,
      error: sanitizePublicOrderErrorMessage(
        error instanceof Error ? error.message : 'Erro desconhecido ao criar pedido'
      )
    };
  }
}

/** Pedido na tabela sem `controle_dia` — precisa de `accept` antes de promover status. */
export function orderNeedsAccept(order: unknown): boolean {
  if (!order || typeof order !== "object") return false;
  const o = order as Record<string, unknown>;
  if (o._isPendingVirtual === true) return true;
  const ctl = Number(o.controle_dia ?? o.day_sequence);
  if (Number.isFinite(ctl) && ctl > 0) return false;
  const st = String(o.status ?? "").toLowerCase();
  return (
    st === "awaiting_approval" ||
    st === "anotado" ||
    st === "analysis" ||
    st === "em_analise" ||
    st === "em análise" ||
    st === "pending"
  );
}

/** Aceita pedido aguardando aprovação: atribui `controle_dia` sequencial e status `aprovado`. */
export async function acceptPublicOrder(
  cnpj: string,
  pedidoId: number,
  options?: {
    backendStatus?: string;
    whatsappNotify?: import("../utils/whatsappNotifications").WhatsappNotifyItem[];
  }
): Promise<{
  success: boolean;
  pedido_id?: number;
  controle_dia?: number;
  auto_printed?: boolean;
  printid?: number | null;
  print_error?: string;
  error?: string;
}> {
  try {
    const cleanCnpj = cnpj.replace(/\D/g, "");
    const pid = Number(pedidoId);
    if (cleanCnpj.length !== 14 || !Number.isFinite(pid) || pid <= 0) {
      return { success: false, error: "CNPJ ou pedido_id inválido" };
    }
    const notifyItems = (options?.whatsappNotify || []).filter(
      (i) => (i.text || "").trim() && (i.phone || "").trim()
    );
    const { N8N_WEBHOOK_URL, N8N_AUTH_HEADER } = await import("../config");
    const webhookUrl = `${N8N_WEBHOOK_URL}update-order`;
    const requestBody = {
      control: {
        cnpj: cleanCnpj,
        action: "accept",
        id: pid,
        pedido_id: pid,
      },
      orderData: {
        status: options?.backendStatus || "aprovado",
      },
      ...(notifyItems.length ? { whatsapp_notify: notifyItems } : {}),
    };
    const response = await panelFetch(webhookUrl, {
      method: "POST",
      headers: {
        Authorization: N8N_AUTH_HEADER,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      const errorText = await response.text();
      return { success: false, error: errorText || `HTTP ${response.status}` };
    }
    const responseText = await response.text();
    let data: Record<string, unknown> = {};
    if (responseText.trim()) {
      try {
        data = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        return { success: false, error: "Resposta inválida ao aceitar pedido" };
      }
    }
    if (data.ok === false || data.success === false) {
      return {
        success: false,
        error: String(data.error || data.message || "Falha ao aceitar pedido"),
      };
    }
    const idNum = Number(data.pedido_id ?? data.id ?? pid);
    const ctl = Number(data.controle_dia ?? data.day_sequence);
    const autoPrinted = data.auto_printed === true || data.auto_printed === "true";
    const printIdRaw = data.printid ?? data.print_job_id;
    const printIdNum = printIdRaw != null ? Number(printIdRaw) : NaN;

    void runWhatsappNotifyClientFallback(
      notifyItems,
      cleanCnpj,
      data,
      Number.isFinite(idNum) ? idNum : pid
    );

    return {
      success: true,
      pedido_id: Number.isFinite(idNum) ? idNum : pid,
      controle_dia: Number.isFinite(ctl) && ctl > 0 ? ctl : undefined,
      auto_printed: autoPrinted,
      printid: Number.isFinite(printIdNum) ? printIdNum : null,
      print_error:
        typeof data.print_error === "string" ? data.print_error : undefined,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Erro ao aceitar pedido",
    };
  }
}

// Webhook esperado: https://delival-n8n.rwysej.easypanel.host/webhook/update-order